import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchPageLayoutTemplates,
  fetchPageLayoutTemplate,
  createPageLayoutTemplate,
  updatePageLayoutTemplate,
  clonePageLayoutTemplate,
  deletePageLayoutTemplate,
  fetchTemplateVersionHistory,
  rollbackTemplateVersion,
  fetchDynamicTabs,
  fetchCustomFields,
  fetchLayoutSections,
  createLayoutSection,
  updateLayoutSection,
  deleteLayoutSection,
  addFieldToSection,
  removeFieldFromSection,
  reorderSectionFields,
  fetchVisibilityRules,
  createVisibilityRule,
  updateVisibilityRule,
  deleteVisibilityRule,
  toggleVisibilityRule,
  evaluateVisibilityRules,
  reorderVisibilityRules,
  fetchRelatedLists,
  createRelatedList,
  updateRelatedList,
  deleteRelatedList,
  fetchQuickActions,
  createQuickAction,
  updateQuickAction,
  deleteQuickAction,
  fetchLayoutAssignments,
  createLayoutAssignment,
  deleteLayoutAssignment,
  previewLayout,
  validateLayout,
  cloneLayout,
  getLayoutHealthCheck,
  type PageLayoutTemplate,
  type PageLayoutSection,
  type PageLayoutField,
  type PageLayoutVersion,
  type DynamicTab,
  type CustomField,
  type Application,
  type LayoutSection,
  type SectionField,
  type VisibilityRule,
  type RuleCondition,
  type RuleAction,
  type RuleTarget,
  type RuleLogic,
  type RuleConditionOperator,
  type RuleEvaluationResult,
  type RelatedList,
  type QuickAction,
  type ActionType,
  type ActionIcon,
  type LayoutAssignment,
  type LayoutPreviewData,
  type LayoutValidationResult,
  type LayoutHealthCheck,
  type LayoutCloneRequest
} from '../../services/api/uiConfigurationApi';
import { migrateAllLayouts } from '../../scripts/migrateLayoutsToSystemAdmin';
import { X, ChevronDown, ChevronUp, Plus } from 'lucide-react';
import { DevModeBanner } from '../common/DevModeBanner';
import { useToast } from '../../contexts/ToastContext';
import { StandardLoadingState, StandardErrorState } from '../standard';
import Joyride, { CallBackProps, STATUS, Step } from 'react-joyride';

// Drag-and-drop styles
const dragDropStyles = `
  .dragging {
    opacity: 0.5;
    cursor: grabbing !important;
  }

  .drag-over {
    border-color: var(--primary-color) !important;
    background: var(--primary-color)10 !important;
    transform: translateY(-2px) !important;
    box-shadow: 0 4px 12px rgba(0,0,0,0.15) !important;
  }

  [draggable="true"] {
    user-select: none;
    -webkit-user-select: none;
  }

  [draggable="true"]:active {
    cursor: grabbing;
  }
`;

// Debounce utility function
function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): T & { cancel: () => void } {
  let timeout: NodeJS.Timeout | null = null;

  const debounced = function (this: any, ...args: Parameters<T>) {
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(this, args), wait);
  } as T & { cancel: () => void };

  debounced.cancel = () => {
    if (timeout) {
      clearTimeout(timeout);
      timeout = null;
    }
  };

  return debounced;
}

/**
 * Page Layout Manager
 *
 * Comprehensive page layout configuration system for managing field layouts across applications.
 * Provides drag-and-drop section and field organization, multi-column layouts, visibility rules,
 * related lists, quick actions, version history with rollback, layout assignments, health checks,
 * and real-time preview functionality. Supports application and object-specific filtering.
 *
 * Data Source: uiConfigurationApi.ts (React Query with mock data)
 *
 * @component
 */
const PageLayoutManager: React.FC = () => {
  const queryClient = useQueryClient();
  const { success, error: showError, warning } = useToast();

  // Check if device supports touch
  const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

  // State
  const [selectedApp, setSelectedApp] = useState<Application>('cybersweep');
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [isMigrating, setIsMigrating] = useState(false);
  const [showTemplateSelector, setShowTemplateSelector] = useState(false);
  const [runTour, setRunTour] = useState(false);

  // Fetch templates
  const { data: templatesResponse, isLoading, error } = useQuery({
    queryKey: ['page-layout-templates', selectedApp],
    queryFn: () => fetchPageLayoutTemplates({ application: selectedApp }),
    staleTime: 60 * 1000
  });

  const templates = templatesResponse?.data || [];

  // Fetch selected template details
  const { data: templateResponse, isLoading: templateLoading } = useQuery({
    queryKey: ['page-layout-template', selectedTemplateId],
    queryFn: () => fetchPageLayoutTemplate(selectedTemplateId!),
    enabled: !!selectedTemplateId,
    staleTime: 60 * 1000
  });

  const selectedTemplate = templateResponse?.data;

  // Mutations
  const createMutation = useMutation({
    mutationFn: createPageLayoutTemplate,
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['page-layout-templates'] });
      setSelectedTemplateId(response.data.id);
      setShowCreateModal(false);
    }
  });

  const updateMutation = useMutation({
    mutationFn: ({ templateId, updates }: { templateId: string; updates: any }) =>
      updatePageLayoutTemplate(templateId, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['page-layout-templates'] });
      queryClient.invalidateQueries({ queryKey: ['page-layout-template', selectedTemplateId] });
    }
  });

  const cloneMutation = useMutation({
    mutationFn: ({ templateId, newName }: { templateId: string; newName: string }) =>
      clonePageLayoutTemplate(templateId, newName),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['page-layout-templates'] });
      setSelectedTemplateId(response.data.id);
    }
  });

  const deleteMutation = useMutation({
    mutationFn: deletePageLayoutTemplate,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['page-layout-templates'] });
      setSelectedTemplateId(null);
    }
  });

  // Rollback mutation
  const rollbackMutation = useMutation({
    mutationFn: ({ templateId, versionId }: { templateId: string; versionId: string }) =>
      rollbackTemplateVersion(templateId, versionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['page-layout-templates'] });
      queryClient.invalidateQueries({ queryKey: ['page-layout-template', selectedTemplateId] });
      queryClient.invalidateQueries({ queryKey: ['template-versions', selectedTemplateId] });
      setShowVersionHistoryModal(false);
    }
  });

  // Section management mutations
  const createSectionMutation = useMutation({
    mutationFn: ({ layoutId, sectionData }: { layoutId: string; sectionData: any }) =>
      createLayoutSection(layoutId, sectionData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['layout-sections', selectedTemplateId] });
    }
  });

  const updateSectionMutation = useMutation({
    mutationFn: ({ layoutId, sectionId, updates }: { layoutId: string; sectionId: string; updates: any }) =>
      updateLayoutSection(layoutId, sectionId, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['layout-sections', selectedTemplateId] });
    }
  });

  const deleteSectionMutation = useMutation({
    mutationFn: ({ layoutId, sectionId }: { layoutId: string; sectionId: string }) =>
      deleteLayoutSection(layoutId, sectionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['layout-sections', selectedTemplateId] });
    }
  });

  // Field management mutations
  const addFieldMutation = useMutation({
    mutationFn: ({ layoutId, sectionId, fieldData }: { layoutId: string; sectionId: string; fieldData: any }) =>
      addFieldToSection(layoutId, sectionId, fieldData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['layout-sections', selectedTemplateId] });
    }
  });

  const removeFieldMutation = useMutation({
    mutationFn: ({ layoutId, sectionId, fieldId }: { layoutId: string; sectionId: string; fieldId: string }) =>
      removeFieldFromSection(layoutId, sectionId, fieldId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['layout-sections', selectedTemplateId] });
    }
  });

  const reorderFieldsMutation = useMutation({
    mutationFn: ({ layoutId, sectionId, fieldOrders }: { layoutId: string; sectionId: string; fieldOrders: any[] }) =>
      reorderSectionFields(layoutId, sectionId, fieldOrders),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['layout-sections', selectedTemplateId] });
    }
  });

  // Visibility rule mutations
  const createRuleMutation = useMutation({
    mutationFn: ({ layoutId, ruleData }: { layoutId: string; ruleData: any }) =>
      createVisibilityRule(layoutId, ruleData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['visibility-rules', selectedTemplateId] });
      setShowRuleModal(false);
      setEditingRule(null);
    }
  });

  const updateRuleMutation = useMutation({
    mutationFn: ({ layoutId, ruleId, updates }: { layoutId: string; ruleId: string; updates: any }) =>
      updateVisibilityRule(layoutId, ruleId, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['visibility-rules', selectedTemplateId] });
      setShowRuleModal(false);
      setEditingRule(null);
    }
  });

  const deleteRuleMutation = useMutation({
    mutationFn: ({ layoutId, ruleId }: { layoutId: string; ruleId: string }) =>
      deleteVisibilityRule(layoutId, ruleId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['visibility-rules', selectedTemplateId] });
    }
  });

  const toggleRuleMutation = useMutation({
    mutationFn: ({ layoutId, ruleId, enabled }: { layoutId: string; ruleId: string; enabled: boolean }) =>
      toggleVisibilityRule(layoutId, ruleId, enabled),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['visibility-rules', selectedTemplateId] });
    }
  });

  const reorderRulesMutation = useMutation({
    mutationFn: ({ layoutId, ruleOrders }: { layoutId: string; ruleOrders: Array<{ ruleId: string; priority: number }> }) =>
      reorderVisibilityRules(layoutId, ruleOrders),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['visibility-rules', selectedTemplateId] });
    }
  });

  // Related list mutations
  const createRelatedListMutation = useMutation({
    mutationFn: ({ layoutId, listData }: { layoutId: string; listData: any }) =>
      createRelatedList(layoutId, listData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['related-lists'] });
      setShowRelatedListModal(false);
      setEditingRelatedList(null);
    }
  });

  const updateRelatedListMutation = useMutation({
    mutationFn: ({ layoutId, listId, updates }: { layoutId: string; listId: string; updates: any }) =>
      updateRelatedList(layoutId, listId, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['related-lists'] });
      setShowRelatedListModal(false);
      setEditingRelatedList(null);
    }
  });

  const deleteRelatedListMutation = useMutation({
    mutationFn: ({ layoutId, listId }: { layoutId: string; listId: string }) =>
      deleteRelatedList(layoutId, listId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['related-lists'] });
    }
  });

  // Quick action mutations
  const createQuickActionMutation = useMutation({
    mutationFn: ({ layoutId, actionData }: { layoutId: string; actionData: any }) =>
      createQuickAction(layoutId, actionData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quick-actions'] });
      setShowQuickActionModal(false);
      setEditingQuickAction(null);
    }
  });

  const updateQuickActionMutation = useMutation({
    mutationFn: ({ layoutId, actionId, updates }: { layoutId: string; actionId: string; updates: any }) =>
      updateQuickAction(layoutId, actionId, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quick-actions'] });
      setShowQuickActionModal(false);
      setEditingQuickAction(null);
    }
  });

  const deleteQuickActionMutation = useMutation({
    mutationFn: ({ layoutId, actionId }: { layoutId: string; actionId: string }) =>
      deleteQuickAction(layoutId, actionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quick-actions'] });
    }
  });

  // Phase 8: Assignment mutations
  const createAssignmentMutation = useMutation({
    mutationFn: ({ layoutId, assignmentData }: { layoutId: string; assignmentData: any }) =>
      createLayoutAssignment(layoutId, assignmentData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['layout-assignments'] });
      setShowAssignmentModal(false);
    }
  });

  const deleteAssignmentMutation = useMutation({
    mutationFn: ({ layoutId, assignmentId }: { layoutId: string; assignmentId: string }) =>
      deleteLayoutAssignment(layoutId, assignmentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['layout-assignments'] });
    }
  });

  const cloneLayoutMutation = useMutation({
    mutationFn: (cloneRequest: LayoutCloneRequest) =>
      cloneLayout(cloneRequest),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['page-layout-templates'] });
      setShowCloneModal(false);
      success('Layout cloned successfully');
    }
  });

  // Debounced auto-save function
  const debouncedAutoSave = useMemo(
    () =>
      debounce((templateId: string, updates: Partial<PageLayoutTemplate>) => {
        console.log('🔄 Auto-saving template...', updates);
        setIsSaving(true);
        setSaveError(null);

        updateMutation.mutate(
          { templateId, updates },
          {
            onSuccess: () => {
              setHasUnsavedChanges(false);
              setLastSavedAt(new Date());
              setPendingChanges(null);
              setIsSaving(false);
              console.log('✅ Auto-save successful');
            },
            onError: (error: any) => {
              setSaveError(error.message || 'Failed to save changes');
              setIsSaving(false);
              console.error('❌ Auto-save failed:', error);
            }
          }
        );
      }, 2000), // 2 second debounce
    [updateMutation]
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      debouncedAutoSave.cancel();
    };
  }, [debouncedAutoSave]);

  // Section management mode state
  const [sectionManagementMode, setSectionManagementMode] = useState<'template' | 'layout' | 'rules' | 'actions' | 'assignments'>('layout');

  // Section editing state
  const [editingSection, setEditingSection] = useState<PageLayoutSection | null>(null);
  const [showSectionModal, setShowSectionModal] = useState(false);
  const [editingLayoutSection, setEditingLayoutSection] = useState<LayoutSection | null>(null);
  const [showLayoutSectionModal, setShowLayoutSectionModal] = useState(false);

  // Visibility rules state
  const [showRuleModal, setShowRuleModal] = useState(false);
  const [editingRule, setEditingRule] = useState<VisibilityRule | null>(null);
  const [showRulePreviewModal, setShowRulePreviewModal] = useState(false);
  const [rulePreviewData, setRulePreviewData] = useState<Record<string, any>>({});
  const [draggedRuleId, setDraggedRuleId] = useState<string | null>(null);

  // Related lists and actions state
  const [showRelatedListModal, setShowRelatedListModal] = useState(false);
  const [showQuickActionModal, setShowQuickActionModal] = useState(false);
  const [editingRelatedList, setEditingRelatedList] = useState<RelatedList | null>(null);
  const [editingQuickAction, setEditingQuickAction] = useState<QuickAction | null>(null);

  // Field assignment state
  const [selectedSectionId, setSelectedSectionId] = useState<string | null>(null);
  const [showFieldSelectorModal, setShowFieldSelectorModal] = useState(false);
  const [editingField, setEditingField] = useState<PageLayoutField | null>(null);
  const [showFieldConfigModal, setShowFieldConfigModal] = useState(false);

  // PII configuration state
  const [showPIIConfigModal, setShowPIIConfigModal] = useState(false);
  const [configuringPIIField, setConfiguringPIIField] = useState<{
    sectionId: string;
    field: PageLayoutField;
  } | null>(null);

  // Version history state
  const [showVersionHistoryModal, setShowVersionHistoryModal] = useState(false);
  const [showCompareModal, setShowCompareModal] = useState(false);
  const [compareVersionId, setCompareVersionId] = useState<string | null>(null);

  // Preview state
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewMode, setPreviewMode] = useState<'desktop' | 'tablet' | 'mobile'>('desktop');
  const [showSampleData, setShowSampleData] = useState(true);

  // Auto-save state
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [pendingChanges, setPendingChanges] = useState<Partial<PageLayoutTemplate> | null>(null);

  // Phase 8: Assignment & Testing state
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [showHealthModal, setShowHealthModal] = useState(false);
  const [showCloneModal, setShowCloneModal] = useState(false);
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  const [showLayoutPreviewModal, setShowLayoutPreviewModal] = useState(false);
  const [layoutPreviewData, setLayoutPreviewData] = useState<LayoutPreviewData | null>(null);
  const [validationResult, setValidationResult] = useState<LayoutValidationResult | null>(null);
  const [healthCheck, setHealthCheck] = useState<LayoutHealthCheck | null>(null);

  // Drag and drop state
  const [draggedSectionId, setDraggedSectionId] = useState<string | null>(null);
  const [draggedFieldId, setDraggedFieldId] = useState<string | null>(null);
  const [dragOverSectionId, setDragOverSectionId] = useState<string | null>(null);
  const [dragOverFieldId, setDragOverFieldId] = useState<string | null>(null);

  // Fetch dynamic tabs for the selected application
  const { data: tabsResponse } = useQuery({
    queryKey: ['dynamic-tabs', selectedApp],
    queryFn: () => fetchDynamicTabs({ application: selectedApp }),
    staleTime: 60 * 1000
  });

  const availableTabs = tabsResponse?.data || [];

  // Fetch custom fields for assignment
  const { data: customFieldsResponse } = useQuery({
    queryKey: ['custom-fields', selectedApp],
    queryFn: () => fetchCustomFields({ application: selectedApp }),
    staleTime: 60 * 1000
  });

  const availableFields = customFieldsResponse?.data || [];
  
  // Check if tour has been completed
  React.useEffect(() => {
    const tourCompleted = localStorage.getItem('pageLayoutTourCompleted');
    if (!tourCompleted && selectedTemplate) {
      // Auto-start tour on first visit
      setTimeout(() => setRunTour(true), 1000);
    }
  }, [selectedTemplate]);
  
  // Guided tour steps
  const tourSteps: Step[] = [
    {
      target: '[data-tour="template-selector"]',
      content: 'Select a template from the list to start editing. You can also create a new template.',
      placement: 'right',
      disableBeacon: true
    },
    {
      target: '[data-tour="apply-template"]',
      content: 'Use the "Apply Template" button to quickly apply preset layouts (Compact, Detailed, or Mobile-Friendly).',
      placement: 'bottom'
    },
    {
      target: '[data-tour="sections"]',
      content: 'Manage sections here. Add new sections, reorder them, or edit existing ones. Sections organize fields into logical groups.',
      placement: 'right'
    },
    {
      target: '[data-tour="fields"]',
      content: 'Drag and drop fields into sections. Click "Add Fields" to open the field palette with search functionality.',
      placement: 'top'
    },
    {
      target: '[data-tour="visibility-rules"]',
      content: 'Configure visibility rules to show or hide fields based on conditions. This enables dynamic layouts.',
      placement: 'top'
    },
    {
      target: '[data-tour="save"]',
      content: 'Changes auto-save every 2 seconds. You can also manually save using Ctrl+S or the "Save Now" button.',
      placement: 'top'
    }
  ];
  
  // Handle tour callback
  const handleTourCallback = (data: CallBackProps) => {
    const { status } = data;
    if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
      localStorage.setItem('pageLayoutTourCompleted', 'true');
      setRunTour(false);
    }
  };
  
  // Layout template presets
  const layoutTemplates = {
    compact: {
      name: 'Compact',
      description: '2 columns, smaller spacing, minimal fields',
      sections: [
        {
          name: 'Main Information',
          label: 'Main Information',
          columns: 2,
          collapsible: false,
          visible: true,
          page: 1,
          tab: 'Details',
          order: 1
        }
      ]
    },
    detailed: {
      name: 'Detailed',
      description: '1 column, larger spacing, all fields visible',
      sections: [
        {
          name: 'All Fields',
          label: 'All Fields',
          columns: 1,
          collapsible: false,
          visible: true,
          page: 1,
          tab: 'Details',
          order: 1
        }
      ]
    },
    mobileFriendly: {
      name: 'Mobile-Friendly',
      description: 'Single column, collapsible sections',
      sections: [
        {
          name: 'Primary Information',
          label: 'Primary Information',
          columns: 1,
          collapsible: true,
          visible: true,
          page: 1,
          tab: 'Details',
          order: 1
        },
        {
          name: 'Additional Details',
          label: 'Additional Details',
          columns: 1,
          collapsible: true,
          visible: true,
          page: 1,
          tab: 'Details',
          order: 2
        }
      ]
    }
  };
  
  // Check required fields visibility
  const checkRequiredFieldsVisibility = useMemo(() => {
    if (!selectedTemplate || !availableFields.length) return { hidden: [], allVisible: true };
    
    const requiredFields = availableFields.filter(f => f.isRequired);
    const visibleFieldIds = new Set<string>();
    
    selectedTemplate.sections.forEach(section => {
      section.fields?.forEach(field => {
        if (field.fieldId) {
          visibleFieldIds.add(field.fieldId);
        }
      });
    });
    
    const hiddenRequired = requiredFields.filter(f => !visibleFieldIds.has(f.id));
    
    return {
      hidden: hiddenRequired,
      allVisible: hiddenRequired.length === 0
    };
  }, [selectedTemplate, availableFields]);
  
  // Apply layout template
  const applyTemplate = (templateName: 'compact' | 'detailed' | 'mobileFriendly') => {
    if (!selectedTemplate) return;
    
    const template = layoutTemplates[templateName];
    const templateSections: PageLayoutSection[] = template.sections.map((section, idx) => ({
      id: `section-${Date.now()}-${idx}`,
      name: section.name,
      description: '',
      columns: section.columns as 1 | 2 | 3 | 4,
      collapsible: section.collapsible,
      defaultCollapsed: false,
      visible: section.visible,
      page: String(section.page),
      tab: section.tab,
      order: section.order,
      fields: [] as PageLayoutField[],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      createdBy: 'current-user',
      updatedBy: 'current-user'
    }));
    
    // Add all available fields to first section if detailed template
    if (templateName === 'detailed' && templateSections.length > 0 && availableFields.length > 0) {
      templateSections[0].fields = availableFields.slice(0, 10).map((field, idx): PageLayoutField => ({
        fieldId: field.id,
        label: field.label,
        fieldKey: field.fieldKey,
        type: field.type,
        required: field.isRequired || false,
        order: idx + 1,
        width: 'full' as const
      }));
    }
    
    // Trigger auto-save
    setHasUnsavedChanges(true);
    setPendingChanges({ sections: templateSections });
    debouncedAutoSave(selectedTemplate.id, { sections: templateSections });
    setShowTemplateSelector(false);
    success('Template Applied', `${template.name} template has been applied to this layout`);
  };

  // Fetch layout sections for the selected template
  const { data: layoutSectionsResponse, isLoading: sectionsLoading } = useQuery({
    queryKey: ['layout-sections', selectedTemplateId],
    queryFn: () => fetchLayoutSections(selectedTemplateId!),
    enabled: !!selectedTemplateId,
    staleTime: 60 * 1000
  });

  const layoutSections = layoutSectionsResponse?.data || [];

  // Fetch visibility rules for the selected template
  const { data: visibilityRulesResponse, isLoading: rulesLoading } = useQuery({
    queryKey: ['visibility-rules', selectedTemplateId],
    queryFn: () => fetchVisibilityRules(selectedTemplateId!),
    enabled: !!selectedTemplateId,
    staleTime: 60 * 1000
  });

  const visibilityRules = visibilityRulesResponse?.data || [];

  // Fetch related lists for the selected template
  const { data: relatedListsResponse, isLoading: relatedListsLoading } = useQuery({
    queryKey: ['related-lists', selectedTemplateId],
    queryFn: () => fetchRelatedLists(selectedTemplateId!),
    enabled: !!selectedTemplateId,
    staleTime: 60 * 1000
  });

  const relatedLists = relatedListsResponse?.data || [];

  // Fetch quick actions for the selected template
  const { data: quickActionsResponse, isLoading: quickActionsLoading } = useQuery({
    queryKey: ['quick-actions', selectedTemplateId],
    queryFn: () => fetchQuickActions(selectedTemplateId!),
    enabled: !!selectedTemplateId,
    staleTime: 60 * 1000
  });

  const quickActions = quickActionsResponse?.data || [];

  // Fetch layout assignments (Phase 8)
  const { data: assignmentsResponse, isLoading: assignmentsLoading } = useQuery({
    queryKey: ['layout-assignments', selectedTemplateId],
    queryFn: () => fetchLayoutAssignments(selectedTemplateId!),
    enabled: !!selectedTemplateId,
    staleTime: 60 * 1000
  });

  const layoutAssignments = assignmentsResponse?.data || [];

  // Fetch version history for selected template
  const { data: versionsResponse, isLoading: versionsLoading } = useQuery({
    queryKey: ['template-versions', selectedTemplateId],
    queryFn: () => fetchTemplateVersionHistory(selectedTemplateId!),
    enabled: !!selectedTemplateId,
    staleTime: 60 * 1000
  });

  const versions = versionsResponse?.data || [];

  // Fetch specific version for comparison
  const { data: compareVersionResponse } = useQuery({
    queryKey: ['template-version', compareVersionId],
    queryFn: async () => {
      const version = versions.find(v => v.id === compareVersionId);
      return version ? { data: version } : null;
    },
    enabled: !!compareVersionId && showCompareModal
  });

  const compareVersion = compareVersionResponse?.data;

  // Handlers
  const handleCreateTemplate = (data: any) => {
    createMutation.mutate({
      name: data.name,
      description: data.description,
      application: selectedApp,
      isPublic: data.isPublic || false,
      isDefault: false,
      sections: []
    });
  };

  const handleCloneTemplate = (templateId: string) => {
    const template = templates.find(t => t.id === templateId);
    if (!template) return;

    const newName = prompt(`Clone "${template.name}" as:`, `${template.name} (Copy)`);
    if (newName) {
      cloneMutation.mutate({ templateId, newName });
    }
  };

  const handleDeleteTemplate = (templateId: string) => {
    if (window.confirm('Are you sure you want to delete this template?')) {
      deleteMutation.mutate(templateId);
    }
  };

  // Version history handlers
  const handleViewVersionHistory = () => {
    setShowVersionHistoryModal(true);
  };

  const handleRollbackVersion = (versionId: string) => {
    if (!selectedTemplate) return;

    const version = versions.find(v => v.id === versionId);
    if (!version) return;

    const confirmMessage = `Are you sure you want to rollback to version ${version.version}?\n\n` +
      `Current version (${selectedTemplate.version}) will be saved as a new version.\n` +
      `Changes: ${version.changes}`;

    if (window.confirm(confirmMessage)) {
      rollbackMutation.mutate({
        templateId: selectedTemplate.id,
        versionId
      });
    }
  };

  const handleCompareVersion = (versionId: string) => {
    setCompareVersionId(versionId);
    setShowCompareModal(true);
  };

  // Preview handlers
  const handleOpenPreview = () => {
    setShowPreviewModal(true);
  };

  const handleClosePreview = () => {
    setShowPreviewModal(false);
  };

  const handleChangePreviewMode = (mode: 'desktop' | 'tablet' | 'mobile') => {
    setPreviewMode(mode);
  };

  const handleToggleSampleData = () => {
    setShowSampleData(!showSampleData);
  };

  // Manual save handler
  const handleManualSave = useCallback(() => {
    if (!selectedTemplate || !hasUnsavedChanges || !pendingChanges) return;

    // Cancel debounced save and save immediately
    debouncedAutoSave.cancel();

    console.log('💾 Manual save triggered');
    setIsSaving(true);
    setSaveError(null);

    updateMutation.mutate(
      { templateId: selectedTemplate.id, updates: pendingChanges },
      {
        onSuccess: () => {
          setHasUnsavedChanges(false);
          setLastSavedAt(new Date());
          setPendingChanges(null);
          setIsSaving(false);
          console.log('✅ Manual save successful');
        },
        onError: (error: any) => {
          setSaveError(error.message || 'Failed to save changes');
          setIsSaving(false);
          console.error('❌ Manual save failed:', error);
        }
      }
    );
  }, [selectedTemplate, hasUnsavedChanges, pendingChanges, debouncedAutoSave, updateMutation]);

  // Migration handler
  const handleMigrateLayouts = async () => {
    // eslint-disable-next-line no-restricted-globals
    if (!confirm('This will import all hardcoded layouts into System Admin. Continue?')) {
      return;
    }

    setIsMigrating(true);
    try {
      console.log('🚀 Starting layout migration...');
      await migrateAllLayouts();

      // Refresh the layout list
      queryClient.invalidateQueries({ queryKey: ['page-layout-templates'] });

      success('Migration complete', 'All hardcoded layouts have been imported into System Admin');
      console.log('✅ Migration successful');
    } catch (error: any) {
      console.error('❌ Migration failed:', error);
      showError('Migration failed', error.message || 'Unknown error');
    } finally {
      setIsMigrating(false);
    }
  };

  // Keyboard shortcuts (Ctrl+P for preview, Ctrl+S for save, Esc to close modals)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
      const cmdOrCtrl = isMac ? e.metaKey : e.ctrlKey;

      // Ctrl/Cmd + S = Save
      if (cmdOrCtrl && e.key === 's') {
        e.preventDefault();
        handleManualSave();
      }

      // Ctrl/Cmd + P = Preview
      if (cmdOrCtrl && e.key === 'p' && selectedTemplate) {
        e.preventDefault();
        handleOpenPreview();
      }

      // Escape = Close modals
      if (e.key === 'Escape') {
        if (showSectionModal) setShowSectionModal(false);
        if (showFieldSelectorModal) setShowFieldSelectorModal(false);
        if (showFieldConfigModal) setShowFieldConfigModal(false);
        if (showPIIConfigModal) setShowPIIConfigModal(false);
        if (showVersionHistoryModal) setShowVersionHistoryModal(false);
        if (showCompareModal) setShowCompareModal(false);
        if (showPreviewModal) setShowPreviewModal(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [
    selectedTemplate,
    hasUnsavedChanges,
    pendingChanges,
    showSectionModal,
    showFieldSelectorModal,
    showFieldConfigModal,
    showPIIConfigModal,
    showVersionHistoryModal,
    showCompareModal,
    showPreviewModal,
    handleManualSave
  ]);

  // Navigation protection (prevent data loss)
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
        return e.returnValue;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  // Section handlers
  const handleAddSection = () => {
    setEditingSection(null);
    setShowSectionModal(true);
  };

  const handleEditSection = (section: PageLayoutSection) => {
    setEditingSection(section);
    setShowSectionModal(true);
  };

  const handleSaveSection = (sectionData: any) => {
    if (!selectedTemplate) return;

    const updatedSections = editingSection
      ? selectedTemplate.sections.map(s =>
          s.id === editingSection.id ? { ...s, ...sectionData, updatedAt: new Date().toISOString() } : s
        )
      : [
          ...selectedTemplate.sections,
          {
            id: `section-${Date.now()}`,
            ...sectionData,
            fields: [],
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            createdBy: 'current-user',
            updatedBy: 'current-user'
          }
        ];

    // Trigger auto-save
    setHasUnsavedChanges(true);
    setPendingChanges({ sections: updatedSections });
    debouncedAutoSave(selectedTemplate.id, { sections: updatedSections });

    setShowSectionModal(false);
    setEditingSection(null);
  };

  const handleDeleteSection = (sectionId: string) => {
    if (!selectedTemplate) return;
    if (!window.confirm('Are you sure you want to delete this section?')) return;

    const updatedSections = selectedTemplate.sections.filter(s => s.id !== sectionId);

    // Trigger auto-save
    setHasUnsavedChanges(true);
    setPendingChanges({ sections: updatedSections });
    debouncedAutoSave(selectedTemplate.id, { sections: updatedSections });
  };

  const handleReorderSection = (sectionId: string, direction: 'up' | 'down') => {
    if (!selectedTemplate) return;

    const sections = [...selectedTemplate.sections];
    const index = sections.findIndex(s => s.id === sectionId);
    if (index === -1) return;

    if (direction === 'up' && index === 0) return;
    if (direction === 'down' && index === sections.length - 1) return;

    const newIndex = direction === 'up' ? index - 1 : index + 1;
    [sections[index], sections[newIndex]] = [sections[newIndex], sections[index]];

    // Update order properties
    const updatedSections = sections.map((section, idx) => ({
      ...section,
      order: idx,
      updatedAt: new Date().toISOString()
    }));

    // Trigger auto-save
    setHasUnsavedChanges(true);
    setPendingChanges({ sections: updatedSections });
    debouncedAutoSave(selectedTemplate.id, { sections: updatedSections });
  };

  // Layout Section handlers (new API)
  const handleAddLayoutSection = () => {
    setEditingLayoutSection(null);
    setShowLayoutSectionModal(true);
  };

  const handleEditLayoutSection = (section: LayoutSection) => {
    setEditingLayoutSection(section);
    setShowLayoutSectionModal(true);
  };

  const handleSaveLayoutSection = (sectionData: {
    name: string;
    label: string;
    order: number;
    columns: 1 | 2;
    collapsible: boolean;
    collapsed: boolean;
  }) => {
    if (!selectedTemplateId) return;

    if (editingLayoutSection) {
      // Update existing section
      updateSectionMutation.mutate({
        layoutId: selectedTemplateId,
        sectionId: editingLayoutSection.id,
        updates: sectionData
      });
    } else {
      // Create new section
      createSectionMutation.mutate({
        layoutId: selectedTemplateId,
        sectionData
      });
    }

    setShowLayoutSectionModal(false);
    setEditingLayoutSection(null);
  };

  const handleDeleteLayoutSection = (sectionId: string) => {
    if (!selectedTemplateId) return;
    if (!window.confirm('Are you sure you want to delete this layout section?')) return;

    deleteSectionMutation.mutate({
      layoutId: selectedTemplateId,
      sectionId
    });
  };

  const handleAddFieldToLayoutSection = (sectionId: string, fieldIds: string[]) => {
    if (!selectedTemplateId) return;

    fieldIds.forEach((fieldId, index) => {
      const customField = availableFields.find(f => f.id === fieldId);
      if (!customField) return;

      const section = layoutSections.find(s => s.id === sectionId);
      if (!section) return;

      const fieldData = {
        fieldId: customField.id,
        fieldName: customField.fieldKey,
        fieldLabel: customField.label,
        fieldType: customField.type,
        order: (section.fields?.length || 0) + index + 1,
        column: 1 as 1 | 2,
        required: customField.isRequired,
        readonly: false,
        width: 'half' as 'full' | 'half'
      };

      addFieldMutation.mutate({
        layoutId: selectedTemplateId,
        sectionId,
        fieldData
      });
    });
  };

  const handleRemoveFieldFromLayoutSection = (sectionId: string, fieldId: string) => {
    if (!selectedTemplateId) return;

    removeFieldMutation.mutate({
      layoutId: selectedTemplateId,
      sectionId,
      fieldId
    });
  };

  const handleReorderLayoutSectionFields = (sectionId: string, fieldOrders: { fieldId: string; order: number; column: number }[]) => {
    if (!selectedTemplateId) return;

    reorderFieldsMutation.mutate({
      layoutId: selectedTemplateId,
      sectionId,
      fieldOrders
    });
  };

  // Field management handlers
  const handleAddFieldsToSection = (sectionId: string) => {
    setSelectedSectionId(sectionId);
    setShowFieldSelectorModal(true);
  };

  const handleAssignFields = (selectedFieldIds: string[]) => {
    if (!selectedTemplate || !selectedSectionId) return;

    const sections = [...selectedTemplate.sections];
    const sectionIndex = sections.findIndex(s => s.id === selectedSectionId);

    if (sectionIndex === -1) return;

    const section = sections[sectionIndex];
    const existingFieldIds = section.fields?.map(f => f.fieldId) || [];

    // Add only new fields (avoid duplicates)
    const newFields: PageLayoutField[] = selectedFieldIds
      .filter(fieldId => !existingFieldIds.includes(fieldId))
      .map((fieldId, index) => {
        const customField = availableFields.find(f => f.id === fieldId);
        if (!customField) return null;

        return {
          fieldId: customField.id,
          label: customField.label,
          fieldKey: customField.fieldKey,
          type: customField.type,
          required: customField.isRequired,
          placeholder: customField.placeholder,
          order: (section.fields?.length || 0) + index + 1,
          width: 'half' as const
        };
      })
      .filter(Boolean) as PageLayoutField[];

    sections[sectionIndex] = {
      ...section,
      fields: [...(section.fields || []), ...newFields]
    };

    // Trigger auto-save
    setHasUnsavedChanges(true);
    setPendingChanges({ sections });
    debouncedAutoSave(selectedTemplate.id, { sections });

    setShowFieldSelectorModal(false);
    setSelectedSectionId(null);
  };

  const handleEditField = (sectionId: string, field: PageLayoutField) => {
    setSelectedSectionId(sectionId);
    setEditingField(field);
    setShowFieldConfigModal(true);
  };

  const handleSaveFieldConfig = (fieldConfig: Partial<PageLayoutField>) => {
    if (!selectedTemplate || !selectedSectionId || !editingField) return;

    const sections = [...selectedTemplate.sections];
    const sectionIndex = sections.findIndex(s => s.id === selectedSectionId);

    if (sectionIndex === -1) return;

    const section = sections[sectionIndex];
    const fieldIndex = section.fields.findIndex(f => f.fieldId === editingField.fieldId);

    if (fieldIndex === -1) return;

    section.fields[fieldIndex] = {
      ...section.fields[fieldIndex],
      ...fieldConfig
    };

    sections[sectionIndex] = section;

    // Trigger auto-save
    setHasUnsavedChanges(true);
    setPendingChanges({ sections });
    debouncedAutoSave(selectedTemplate.id, { sections });

    setShowFieldConfigModal(false);
    setEditingField(null);
    setSelectedSectionId(null);
  };

  const handleRemoveField = (sectionId: string, fieldId: string) => {
    if (!selectedTemplate) return;

    if (window.confirm('Remove this field from the section?')) {
      const sections = [...selectedTemplate.sections];
      const sectionIndex = sections.findIndex(s => s.id === sectionId);

      if (sectionIndex === -1) return;

      const section = sections[sectionIndex];
      section.fields = section.fields.filter(f => f.fieldId !== fieldId);

      sections[sectionIndex] = section;

      // Trigger auto-save
      setHasUnsavedChanges(true);
      setPendingChanges({ sections });
      debouncedAutoSave(selectedTemplate.id, { sections });
    }
  };

  const handleReorderField = (sectionId: string, fieldId: string, direction: 'up' | 'down') => {
    if (!selectedTemplate) return;

    const sections = [...selectedTemplate.sections];
    const sectionIndex = sections.findIndex(s => s.id === sectionId);

    if (sectionIndex === -1) return;

    const section = sections[sectionIndex];
    const fields = [...section.fields];
    const fieldIndex = fields.findIndex(f => f.fieldId === fieldId);

    if (fieldIndex === -1) return;

    if (direction === 'up' && fieldIndex > 0) {
      [fields[fieldIndex - 1], fields[fieldIndex]] = [fields[fieldIndex], fields[fieldIndex - 1]];
    } else if (direction === 'down' && fieldIndex < fields.length - 1) {
      [fields[fieldIndex], fields[fieldIndex + 1]] = [fields[fieldIndex + 1], fields[fieldIndex]];
    }

    // Update order numbers
    fields.forEach((field, idx) => {
      field.order = idx + 1;
    });

    section.fields = fields;
    sections[sectionIndex] = section;

    // Trigger auto-save
    setHasUnsavedChanges(true);
    setPendingChanges({ sections });
    debouncedAutoSave(selectedTemplate.id, { sections });
  };

  // PII configuration handlers
  const handleOpenPIIConfig = (sectionId: string, field: PageLayoutField) => {
    setConfiguringPIIField({ sectionId, field });
    setShowPIIConfigModal(true);
  };

  const handleSavePIIConfig = (piiConfig: {
    isPII: boolean;
    encryption?: 'standard' | 'high' | 'maximum';
    masking?: {
      enabled: boolean;
      pattern: string;
      unmaskRoles?: string[];
    };
    accessRoles?: string[];
  }) => {
    if (!selectedTemplate || !configuringPIIField) return;

    const { sectionId, field } = configuringPIIField;
    const sections = [...selectedTemplate.sections];
    const sectionIndex = sections.findIndex(s => s.id === sectionId);

    if (sectionIndex === -1) return;

    const section = sections[sectionIndex];
    const fieldIndex = section.fields.findIndex(f => f.fieldId === field.fieldId);

    if (fieldIndex === -1) return;

    // Update field with PII configuration
    section.fields[fieldIndex] = {
      ...section.fields[fieldIndex],
      pii: piiConfig.isPII,
      encryption: piiConfig.encryption,
      masking: piiConfig.masking,
      accessRoles: piiConfig.accessRoles
    };

    sections[sectionIndex] = section;

    // Trigger auto-save
    setHasUnsavedChanges(true);
    setPendingChanges({ sections });
    debouncedAutoSave(selectedTemplate.id, { sections });

    setShowPIIConfigModal(false);
    setConfiguringPIIField(null);
  };

  const handleRemovePIIConfig = (sectionId: string, fieldId: string) => {
    if (!selectedTemplate) return;

    if (window.confirm('Remove PII configuration from this field?')) {
      const sections = [...selectedTemplate.sections];
      const sectionIndex = sections.findIndex(s => s.id === sectionId);

      if (sectionIndex === -1) return;

      const section = sections[sectionIndex];
      const fieldIndex = section.fields.findIndex(f => f.fieldId === fieldId);

      if (fieldIndex === -1) return;

      // Remove PII configuration
      const updatedField = { ...section.fields[fieldIndex] };
      delete updatedField.pii;
      delete updatedField.encryption;
      delete updatedField.masking;
      delete updatedField.accessRoles;
      section.fields[fieldIndex] = updatedField;

      sections[sectionIndex] = section;

      // Trigger auto-save
      setHasUnsavedChanges(true);
      setPendingChanges({ sections });
      debouncedAutoSave(selectedTemplate.id, { sections });
    }
  };

  // Section drag-and-drop handlers
  const handleSectionDragStart = (e: React.DragEvent, sectionId: string) => {
    setDraggedSectionId(sectionId);
    e.dataTransfer.effectAllowed = 'move';
    // Add a slight delay for better visual feedback
    setTimeout(() => {
      if (e.target instanceof HTMLElement) {
        e.target.style.opacity = '0.5';
      }
    }, 0);
  };

  const handleSectionDragEnd = (e: React.DragEvent) => {
    if (e.target instanceof HTMLElement) {
      e.target.style.opacity = '1';
    }
    setDraggedSectionId(null);
    setDragOverSectionId(null);
  };

  const handleSectionDragOver = (e: React.DragEvent, sectionId: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';

    if (draggedSectionId && draggedSectionId !== sectionId) {
      setDragOverSectionId(sectionId);
    }
  };

  const handleSectionDragLeave = (e: React.DragEvent) => {
    setDragOverSectionId(null);
  };

  const handleSectionDrop = (e: React.DragEvent, targetSectionId: string) => {
    e.preventDefault();

    if (!selectedTemplate || !draggedSectionId || draggedSectionId === targetSectionId) {
      setDraggedSectionId(null);
      setDragOverSectionId(null);
      return;
    }

    const sections = [...selectedTemplate.sections];
    const draggedIndex = sections.findIndex(s => s.id === draggedSectionId);
    const targetIndex = sections.findIndex(s => s.id === targetSectionId);

    if (draggedIndex === -1 || targetIndex === -1) return;

    // Remove dragged section and insert at target position
    const [draggedSection] = sections.splice(draggedIndex, 1);
    sections.splice(targetIndex, 0, draggedSection);

    // Update order numbers
    sections.forEach((section, idx) => {
      section.order = idx + 1;
    });

    // Trigger auto-save
    setHasUnsavedChanges(true);
    setPendingChanges({ sections });
    debouncedAutoSave(selectedTemplate.id, { sections });

    setDraggedSectionId(null);
    setDragOverSectionId(null);
  };

  // Field drag-and-drop handlers
  const handleFieldDragStart = (e: React.DragEvent, sectionId: string, fieldId: string) => {
    setSelectedSectionId(sectionId);
    setDraggedFieldId(fieldId);
    e.dataTransfer.effectAllowed = 'move';
    // Stop propagation to prevent section drag
    e.stopPropagation();
    setTimeout(() => {
      if (e.target instanceof HTMLElement) {
        e.target.style.opacity = '0.5';
      }
    }, 0);
  };

  const handleFieldDragEnd = (e: React.DragEvent) => {
    if (e.target instanceof HTMLElement) {
      e.target.style.opacity = '1';
    }
    setDraggedFieldId(null);
    setDragOverFieldId(null);
    setSelectedSectionId(null);
  };

  const handleFieldDragOver = (e: React.DragEvent, fieldId: string) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';

    if (draggedFieldId && draggedFieldId !== fieldId) {
      setDragOverFieldId(fieldId);
    }
  };

  const handleFieldDragLeave = (e: React.DragEvent) => {
    e.stopPropagation();
    setDragOverFieldId(null);
  };

  const handleFieldDrop = (e: React.DragEvent, sectionId: string, targetFieldId: string) => {
    e.preventDefault();
    e.stopPropagation();

    if (!selectedTemplate || !draggedFieldId || draggedFieldId === targetFieldId) {
      setDraggedFieldId(null);
      setDragOverFieldId(null);
      return;
    }

    const sections = [...selectedTemplate.sections];
    const sectionIndex = sections.findIndex(s => s.id === sectionId);

    if (sectionIndex === -1) return;

    const section = sections[sectionIndex];
    const fields = [...section.fields];

    const draggedIndex = fields.findIndex(f => f.fieldId === draggedFieldId);
    const targetIndex = fields.findIndex(f => f.fieldId === targetFieldId);

    if (draggedIndex === -1 || targetIndex === -1) return;

    // Remove dragged field and insert at target position
    const [draggedField] = fields.splice(draggedIndex, 1);
    fields.splice(targetIndex, 0, draggedField);

    // Update order numbers
    fields.forEach((field, idx) => {
      field.order = idx + 1;
    });

    section.fields = fields;
    sections[sectionIndex] = section;

    // Trigger auto-save
    setHasUnsavedChanges(true);
    setPendingChanges({ sections });
    debouncedAutoSave(selectedTemplate.id, { sections });

    setDraggedFieldId(null);
    setDragOverFieldId(null);
  };

  // Helper function to format time ago
  const formatTimeAgo = (date: Date): string => {
    const seconds = Math.floor((new Date().getTime() - date.getTime()) / 1000);
    if (seconds < 10) return 'just now';
    if (seconds < 60) return `${seconds} seconds ago`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    const days = Math.floor(hours / 24);
    return `${days} day${days > 1 ? 's' : ''} ago`;
  };

  // Loading state
  if (isLoading) {
    return <StandardLoadingState message="Loading page layouts..." />;
  }

  // Error state
  if (error) {
    return <StandardErrorState title="Failed to load page layouts" message="Please try again." />;
  }

  return (
    <div className="page-layout-manager">
      <Joyride
        steps={tourSteps}
        run={runTour}
        continuous
        showProgress
        showSkipButton
        callback={handleTourCallback}
        styles={{
          options: {
            primaryColor: '#3B82F6',
            zIndex: 10000
          }
        }}
      />
      {/* Drag-and-drop styles */}
      <style dangerouslySetInnerHTML={{ __html: dragDropStyles }} />

      {/* Development Mode Banner */}
      <DevModeBanner
        dataSource="uiConfigurationApi.ts (Page Layouts)"
        itemCount={`${templates.length} page layout template${templates.length !== 1 ? 's' : ''}`}
        dismissible={true}
      />

      {/* Header */}
      <div style={{ marginBottom: '1.5rem' }}>
        <h3 style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: '0.5rem' }}>
          Page Layout Management
        </h3>
        <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
          Configure page layouts, sections, and field assignments for different applications
        </p>
        {selectedTemplate && (
          <button
            onClick={() => setRunTour(true)}
            className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
            style={{ marginTop: '0.5rem' }}
          >
            Take Tour
          </button>
        )}
      </div>

      {/* Application Selector */}
      <div style={{ marginBottom: '1.5rem', display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
        <label style={{ fontWeight: 500, fontSize: '0.875rem' }}>Application:</label>
        <select
          className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          value={selectedApp}
          onChange={(e) => {
            setSelectedApp(e.target.value as Application);
            setSelectedTemplateId(null);
          }}
        >
          <option value="cybersweep">CyberSweep</option>
          <option value="credit-repair">Credit Repair</option>
          <option value="id-theft">ID Theft Recovery</option>
          <option value="credit-development">Credit Development</option>
          <option value="credit-block">Credit Block</option>
        </select>

        <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
          {selectedTemplate && (
            <div style={{ position: 'relative' }}>
              <button
                data-tour="apply-template"
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2"
                onClick={() => setShowTemplateSelector(!showTemplateSelector)}
              >
                Apply Template
                {showTemplateSelector ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>
              {showTemplateSelector && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  right: 0,
                  marginTop: '0.5rem',
                  background: 'var(--background-primary)',
                  border: '1px solid var(--border-color)',
                  borderRadius: '8px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  zIndex: 100,
                  minWidth: '250px',
                  padding: '0.5rem'
                }}>
                  <div style={{ fontSize: '0.75rem', fontWeight: 600, padding: '0.5rem', color: 'var(--text-secondary)' }}>
                    Layout Templates
                  </div>
                  {Object.entries(layoutTemplates).map(([key, template]) => (
                    <button
                      key={key}
                      onClick={() => applyTemplate(key as any)}
                      className="w-full text-left px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded text-sm"
                      style={{ display: 'block' }}
                    >
                      <div style={{ fontWeight: 600, marginBottom: '0.25rem' }}>{template.name}</div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>{template.description}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          <button
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
            onClick={handleMigrateLayouts}
            disabled={isMigrating}
            title="Import hardcoded layouts into System Admin"
          >
            {isMigrating ? (
              <>
                <span className="spinner" style={{ width: '16px', height: '16px', borderWidth: '2px' }}></span>
                Migrating...
              </>
            ) : (
              <>
                🔄 Import Layouts
              </>
            )}
          </button>
          <button
            data-tour="create-template"
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center"
            onClick={() => setShowCreateModal(true)}
          >
            + Create Template
          </button>
        </div>
      </div>

      {/* Two Column Layout */}
      <div style={{ display: 'grid', gridTemplateColumns: '300px 1fr', gap: '1.5rem' }}>

        {/* Left: Template List */}
        <div data-tour="template-selector" style={{
          border: '1px solid var(--border-color)',
          borderRadius: '8px',
          padding: '1rem',
          background: 'var(--background-primary)',
          maxHeight: '600px',
          overflowY: 'auto'
        }}>
          <h4 style={{
            marginBottom: '1rem',
            fontSize: '0.75rem',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            fontWeight: 600,
            color: 'var(--text-secondary)'
          }}>
            Templates ({templates.length})
          </h4>

          {templates.length === 0 ? (
            <p style={{
              color: 'var(--text-secondary)',
              fontSize: '0.875rem',
              textAlign: 'center',
              padding: '2rem 1rem'
            }}>
              No templates found. Create one to get started.
            </p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {templates.map(template => (
                <div
                  key={template.id}
                  onClick={() => setSelectedTemplateId(template.id)}
                  style={{
                    padding: '0.75rem',
                    background: selectedTemplateId === template.id ? '#3B82F6' : 'transparent',
                    color: selectedTemplateId === template.id ? 'white' : 'var(--text-primary)',
                    border: selectedTemplateId === template.id ? '1px solid #3B82F6' : '1px solid var(--border-color)',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    if (selectedTemplateId !== template.id) {
                      e.currentTarget.style.background = 'var(--background-secondary)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (selectedTemplateId !== template.id) {
                      e.currentTarget.style.background = 'transparent';
                    }
                  }}
                >
                  <div style={{ fontWeight: 600, fontSize: '0.875rem', marginBottom: '0.25rem' }}>
                    {template.name}
                    {template.isDefault && (
                      <span style={{
                        marginLeft: '0.5rem',
                        fontSize: '0.7rem',
                        opacity: 0.8
                      }}>
                        (Default)
                      </span>
                    )}
                  </div>
                  {template.description && (
                    <div style={{
                      fontSize: '0.75rem',
                      opacity: 0.8,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      marginBottom: '0.25rem'
                    }}>
                      {template.description}
                    </div>
                  )}
                  <div style={{ fontSize: '0.7rem', opacity: 0.7 }}>
                    {template.sections?.length || 0} sections • v{template.version}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Right: Template Details */}
        <div style={{
          border: '1px solid var(--border-color)',
          borderRadius: '8px',
          padding: '1.5rem',
          background: 'var(--background-primary)',
          minHeight: '400px'
        }}>
          {templateLoading && (
            <div style={{ textAlign: 'center', padding: '3rem' }}>
              <div className="spinner" style={{ margin: '0 auto' }}></div>
            </div>
          )}

          {!selectedTemplateId && !templateLoading && (
            <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-secondary)' }}>
              <div style={{ fontSize: '3rem', marginBottom: '1rem', opacity: 0.3 }}>📋</div>
              <p>Select a template from the list to view details</p>
            </div>
          )}

          {selectedTemplate && !templateLoading && (
            <div>
              {/* Drag-and-drop tip banner */}
              {!isTouchDevice && (
                <div style={{
                  padding: '0.75rem',
                  background: 'var(--primary-color)10',
                  border: '1px solid var(--primary-color)',
                  borderRadius: '6px',
                  marginBottom: '1rem',
                  fontSize: '0.875rem',
                  color: 'var(--text-primary)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <span>💡</span>
                  <span>
                    <strong>Tip:</strong> Drag sections and fields by the ⋮⋮ handle to reorder them,
                    or use the arrow buttons.
                  </span>
                </div>
              )}

              {/* Save Status Indicator */}
              {!checkRequiredFieldsVisibility.allVisible && (
                <div style={{
                  padding: '1rem',
                  background: '#FEF3C7',
                  border: '1px solid #F59E0B',
                  borderRadius: '8px',
                  marginBottom: '1rem'
                }}>
                  <div style={{ display: 'flex', alignItems: 'start', gap: '0.75rem' }}>
                    <div style={{ fontSize: '1.25rem' }}>⚠️</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, marginBottom: '0.5rem', color: '#92400E' }}>
                        Required Fields Hidden
                      </div>
                      <div style={{ fontSize: '0.875rem', color: '#78350F', marginBottom: '0.75rem' }}>
                        The following required fields are not visible in any section:
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '0.75rem' }}>
                        {checkRequiredFieldsVisibility.hidden.map(field => (
                          <button
                            key={field.id}
                            onClick={() => {
                              // Add field to first section
                              if (selectedTemplate && selectedTemplate.sections.length > 0) {
                                const firstSection = selectedTemplate.sections[0];
                                const newField: PageLayoutField = {
                                  fieldId: field.id,
                                  label: field.label,
                                  fieldKey: field.fieldKey,
                                  type: field.type,
                                  required: true,
                                  order: (firstSection.fields?.length || 0) + 1,
                                  width: 'full' as const
                                };
                                const updatedSections = selectedTemplate.sections.map(s =>
                                  s.id === firstSection.id
                                    ? { ...s, fields: [...(s.fields || []), newField] }
                                    : s
                                );
                                setHasUnsavedChanges(true);
                                setPendingChanges({ sections: updatedSections });
                                debouncedAutoSave(selectedTemplate.id, { sections: updatedSections });
                              }
                            }}
                            style={{
                              padding: '0.25rem 0.5rem',
                              background: '#FEF3C7',
                              border: '1px solid #F59E0B',
                              borderRadius: '4px',
                              fontSize: '0.75rem',
                              color: '#92400E',
                              cursor: 'pointer',
                              fontWeight: 600
                            }}
                          >
                            {field.label} (Click to add)
                          </button>
                        ))}
                      </div>
                      <div style={{ fontSize: '0.75rem', color: '#78350F' }}>
                        Click on a field above to add it to the first section, or manually add it to a section of your choice.
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {saveError && (
                <div style={{
                  padding: '0.75rem',
                  background: '#FEE2E2',
                  border: '1px solid #DC2626',
                  borderRadius: '6px',
                  marginBottom: '1rem',
                  fontSize: '0.875rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '0.5rem'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#DC2626' }}>
                    <span>❌</span>
                    <span>
                      <strong>Error:</strong> {saveError}
                    </span>
                  </div>
                  <button
                    onClick={handleManualSave}
                    style={{
                      padding: '0.25rem 0.75rem',
                      background: '#DC2626',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      fontSize: '0.75rem',
                      cursor: 'pointer',
                      fontWeight: 500
                    }}
                  >
                    Retry
                  </button>
                </div>
              )}

              {isSaving && !saveError && (
                <div style={{
                  padding: '0.75rem',
                  background: '#DBEAFE',
                  border: '1px solid #3B82F6',
                  borderRadius: '6px',
                  marginBottom: '1rem',
                  fontSize: '0.875rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  color: '#1E40AF'
                }}>
                  <span className="spinner" style={{ width: '14px', height: '14px', borderWidth: '2px' }}></span>
                  <span><strong>Saving...</strong></span>
                </div>
              )}

              {!isSaving && !saveError && hasUnsavedChanges && (
                <div style={{
                  padding: '0.75rem',
                  background: '#FEF3C7',
                  border: '1px solid #F59E0B',
                  borderRadius: '6px',
                  marginBottom: '1rem',
                  fontSize: '0.875rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '0.5rem'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#92400E' }}>
                    <span>⚠️</span>
                    <span>
                      <strong>Unsaved changes</strong> - Changes will auto-save in 2 seconds
                    </span>
                  </div>
                  <button
                    data-tour="save"
                    onClick={handleManualSave}
                    style={{
                      padding: '0.25rem 0.75rem',
                      background: '#F59E0B',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      fontSize: '0.75rem',
                      cursor: 'pointer',
                      fontWeight: 500
                    }}
                  >
                    Save Now (Ctrl+S)
                  </button>
                </div>
              )}

              {!isSaving && !saveError && !hasUnsavedChanges && lastSavedAt && (
                <div style={{
                  padding: '0.75rem',
                  background: '#D1FAE5',
                  border: '1px solid #10B981',
                  borderRadius: '6px',
                  marginBottom: '1rem',
                  fontSize: '0.875rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  color: '#065F46'
                }}>
                  <span>✅</span>
                  <span>
                    <strong>Saved</strong> {formatTimeAgo(lastSavedAt)}
                  </span>
                </div>
              )}

              {/* Template Header */}
              <div style={{ marginBottom: '2rem' }}>
                <h3 style={{ fontSize: '1.25rem', fontWeight: 600, marginBottom: '0.5rem' }}>
                  {selectedTemplate.name}
                </h3>
                {selectedTemplate.description && (
                  <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
                    {selectedTemplate.description}
                  </p>
                )}

                {/* Action Buttons */}
                <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
                  {/* Version History button */}
                  <button
                    className="px-3 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
                    onClick={handleViewVersionHistory}
                    disabled={versionsLoading}
                  >
                    {versionsLoading ? '⏳ Loading...' : `📜 Version History (${versions.length})`}
                  </button>

                  {/* Preview button */}
                  <button
                    className="px-3 py-1.5 bg-purple-600 text-white rounded hover:bg-purple-700 text-sm"
                    onClick={handleOpenPreview}
                    title="Preview layout (Ctrl+P)"
                  >
                    👁️ Preview
                  </button>

                  <button
                    className="px-3 py-1.5 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded hover:bg-gray-300 dark:hover:bg-gray-600 text-sm"
                    onClick={() => handleCloneTemplate(selectedTemplate.id)}
                    disabled={cloneMutation.isPending}
                  >
                    {cloneMutation.isPending ? 'Cloning...' : '📋 Clone'}
                  </button>
                  {!selectedTemplate.isDefault && (
                    <button
                      className="px-3 py-1.5 bg-red-600 text-white rounded hover:bg-red-700 text-sm"
                      onClick={() => handleDeleteTemplate(selectedTemplate.id)}
                      disabled={deleteMutation.isPending}
                    >
                      {deleteMutation.isPending ? 'Deleting...' : '🗑️ Delete'}
                    </button>
                  )}
                </div>
              </div>

              {/* Template Metadata */}
              <div style={{
                padding: '1rem',
                background: 'var(--background-secondary)',
                border: '1px solid var(--border-color)',
                borderRadius: '6px',
                marginBottom: '1.5rem'
              }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', fontSize: '0.875rem' }}>
                  <div>
                    <span style={{ color: 'var(--text-secondary)', display: 'block', marginBottom: '0.25rem' }}>
                      Application:
                    </span>
                    <div style={{ fontWeight: 600, textTransform: 'capitalize' }}>
                      {selectedTemplate.application.replace('-', ' ')}
                    </div>
                  </div>
                  <div>
                    <span style={{ color: 'var(--text-secondary)', display: 'block', marginBottom: '0.25rem' }}>
                      Version:
                    </span>
                    <div style={{ fontWeight: 600 }}>
                      v{selectedTemplate.version}
                    </div>
                  </div>
                  <div>
                    <span style={{ color: 'var(--text-secondary)', display: 'block', marginBottom: '0.25rem' }}>
                      Visibility:
                    </span>
                    <div style={{ fontWeight: 600 }}>
                      {selectedTemplate.isPublic ? 'Public' : 'Private'}
                    </div>
                  </div>
                  <div>
                    <span style={{ color: 'var(--text-secondary)', display: 'block', marginBottom: '0.25rem' }}>
                      Sections:
                    </span>
                    <div style={{ fontWeight: 600 }}>
                      {selectedTemplate.sections?.length || 0}
                    </div>
                  </div>
                  <div>
                    <span style={{ color: 'var(--text-secondary)', display: 'block', marginBottom: '0.25rem' }}>
                      PII Fields:
                    </span>
                    <div style={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      {(() => {
                        const piiCount = selectedTemplate.sections?.reduce((count, section) =>
                          count + (section.fields?.filter(f => f.pii).length || 0), 0
                        ) || 0;
                        return (
                          <>
                            <span>{piiCount}</span>
                            {piiCount > 0 && (
                              <span style={{
                                fontSize: '0.65rem',
                                padding: '0.1rem 0.3rem',
                                background: '#FEF3C7',
                                color: '#92400E',
                                borderRadius: '3px',
                                fontWeight: 600
                              }}>
                                PII
                              </span>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  </div>
                  <div>
                    <span style={{ color: 'var(--text-secondary)', display: 'block', marginBottom: '0.25rem' }}>
                      Encrypted:
                    </span>
                    <div style={{ fontWeight: 600 }}>
                      {(() => {
                        const encryptedCount = selectedTemplate.sections?.reduce((count, section) =>
                          count + (section.fields?.filter(f => f.encryption).length || 0), 0
                        ) || 0;
                        return encryptedCount;
                      })()}
                    </div>
                  </div>
                  <div>
                    <span style={{ color: 'var(--text-secondary)', display: 'block', marginBottom: '0.25rem' }}>
                      Masked:
                    </span>
                    <div style={{ fontWeight: 600 }}>
                      {(() => {
                        const maskedCount = selectedTemplate.sections?.reduce((count, section) =>
                          count + (section.fields?.filter(f => f.masking?.enabled).length || 0), 0
                        ) || 0;
                        return maskedCount;
                      })()}
                    </div>
                  </div>
                  <div>
                    <span style={{ color: 'var(--text-secondary)', display: 'block', marginBottom: '0.25rem' }}>
                      Access Restricted:
                    </span>
                    <div style={{ fontWeight: 600 }}>
                      {(() => {
                        const restrictedCount = selectedTemplate.sections?.reduce((count, section) =>
                          count + (section.fields?.filter(f => f.accessRoles && f.accessRoles.length > 0).length || 0), 0
                        ) || 0;
                        return restrictedCount;
                      })()}
                    </div>
                  </div>
                </div>
              </div>

              {/* Section Management Mode Tabs */}
              <div style={{
                marginBottom: '1.5rem',
                borderBottom: '2px solid var(--border-color)'
              }}>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button
                    onClick={() => setSectionManagementMode('layout')}
                    style={{
                      padding: '0.75rem 1.5rem',
                      background: sectionManagementMode === 'layout' ? 'var(--primary-color)' : 'transparent',
                      color: sectionManagementMode === 'layout' ? 'white' : 'var(--text-secondary)',
                      border: 'none',
                      borderBottom: sectionManagementMode === 'layout' ? '3px solid var(--primary-color)' : 'none',
                      cursor: 'pointer',
                      fontWeight: sectionManagementMode === 'layout' ? 600 : 400,
                      fontSize: '0.875rem',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    📐 Layout Sections (New API)
                  </button>
                  <button
                    onClick={() => setSectionManagementMode('template')}
                    style={{
                      padding: '0.75rem 1.5rem',
                      background: sectionManagementMode === 'template' ? 'var(--primary-color)' : 'transparent',
                      color: sectionManagementMode === 'template' ? 'white' : 'var(--text-secondary)',
                      border: 'none',
                      borderBottom: sectionManagementMode === 'template' ? '3px solid var(--primary-color)' : 'none',
                      cursor: 'pointer',
                      fontWeight: sectionManagementMode === 'template' ? 600 : 400,
                      fontSize: '0.875rem',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    📄 Template Sections (Legacy)
                  </button>
                  <button
                    onClick={() => setSectionManagementMode('rules')}
                    style={{
                      padding: '0.75rem 1.5rem',
                      background: sectionManagementMode === 'rules' ? 'var(--primary-color)' : 'transparent',
                      color: sectionManagementMode === 'rules' ? 'white' : 'var(--text-secondary)',
                      border: 'none',
                      borderBottom: sectionManagementMode === 'rules' ? '3px solid var(--primary-color)' : 'none',
                      cursor: 'pointer',
                      fontWeight: sectionManagementMode === 'rules' ? 600 : 400,
                      fontSize: '0.875rem',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    🔒 Visibility Rules ({visibilityRules.length})
                  </button>
                  <button
                    onClick={() => setSectionManagementMode('actions')}
                    style={{
                      padding: '0.75rem 1.5rem',
                      background: sectionManagementMode === 'actions' ? 'var(--primary-color)' : 'transparent',
                      color: sectionManagementMode === 'actions' ? 'white' : 'var(--text-secondary)',
                      border: 'none',
                      borderBottom: sectionManagementMode === 'actions' ? '3px solid var(--primary-color)' : 'none',
                      cursor: 'pointer',
                      fontWeight: sectionManagementMode === 'actions' ? 600 : 400,
                      fontSize: '0.875rem',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    🔗 Related Lists & Actions ({relatedLists.length + quickActions.length})
                  </button>
                  <button
                    onClick={() => setSectionManagementMode('assignments')}
                    style={{
                      padding: '0.75rem 1.5rem',
                      background: sectionManagementMode === 'assignments' ? 'var(--primary-color)' : 'transparent',
                      color: sectionManagementMode === 'assignments' ? 'white' : 'var(--text-secondary)',
                      border: 'none',
                      borderBottom: sectionManagementMode === 'assignments' ? '3px solid var(--primary-color)' : 'none',
                      cursor: 'pointer',
                      fontWeight: sectionManagementMode === 'assignments' ? 600 : 400,
                      fontSize: '0.875rem',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    🎯 Assignments & Testing ({layoutAssignments.length})
                  </button>
                </div>
              </div>

              {/* Layout Sections (New API) */}
              {sectionManagementMode === 'layout' && (
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
                    <h4 style={{ fontSize: '1rem', fontWeight: 600, margin: 0 }}>
                      Layout Sections ({layoutSections.length})
                      {sectionsLoading && (
                        <span className="spinner" style={{
                          marginLeft: '0.5rem',
                          width: '14px',
                          height: '14px',
                          borderWidth: '2px',
                          display: 'inline-block'
                        }}></span>
                      )}
                    </h4>
                    <button
                      className="px-3 py-1.5 bg-green-600 text-white rounded hover:bg-green-700 text-sm flex items-center gap-1"
                      onClick={handleAddLayoutSection}
                    >
                      + Add Layout Section
                    </button>
                  </div>

                  {/* Info banner for new API */}
                  <div style={{
                    padding: '0.75rem 1rem',
                    background: '#E3F2FD',
                    borderRadius: '6px',
                    fontSize: '0.875rem',
                    color: '#1565C0',
                    marginBottom: '1rem',
                    border: '1px solid #90CAF9'
                  }}>
                    <span>ℹ️</span>
                    <span style={{ marginLeft: '0.5rem' }}>
                      <strong>New Section Management API:</strong> These sections are managed independently with dedicated CRUD endpoints and field assignment capabilities.
                    </span>
                  </div>

                  {layoutSections.length > 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                      {layoutSections.map((section, index) => (
                        <div
                          key={section.id}
                          style={{
                            padding: '1rem',
                            background: 'var(--background-primary)',
                            border: '2px solid var(--border-color)',
                            borderRadius: '6px'
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'start', gap: '1rem' }}>
                            {/* Section Content */}
                            <div style={{ flex: 1 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                                <div style={{ fontWeight: 600, fontSize: '0.875rem' }}>
                                  {section.label}
                                </div>
                                {section.collapsible && (
                                  <span style={{
                                    fontSize: '0.65rem',
                                    padding: '0.15rem 0.4rem',
                                    background: '#DBEAFE',
                                    color: '#1D4ED8',
                                    borderRadius: '3px',
                                    fontWeight: 600
                                  }}>
                                    COLLAPSIBLE
                                  </span>
                                )}
                                <span style={{
                                  fontSize: '0.65rem',
                                  padding: '0.15rem 0.4rem',
                                  background: '#E0E7FF',
                                  color: '#3730A3',
                                  borderRadius: '3px',
                                  fontWeight: 600
                                }}>
                                  {section.columns} COLUMN{section.columns > 1 ? 'S' : ''}
                                </span>
                              </div>
                              <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '0.75rem' }}>
                                Name: <span style={{ fontWeight: 500 }}>{section.name}</span> •
                                Order: <span style={{ fontWeight: 500 }}>{section.order}</span>
                              </div>

                              {/* Fields Preview - Column-based Layout */}
                              <div style={{ marginTop: '0.75rem', paddingTop: '0.75rem', borderTop: '1px solid var(--border-color)' }}>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
                                  <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-secondary)' }}>
                                    Fields ({section.fields?.length || 0})
                                  </div>
                                  <button
                                    onClick={() => {
                                      setSelectedSectionId(section.id);
                                      setShowFieldSelectorModal(true);
                                    }}
                                    className="px-2 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700"
                                    style={{ border: 'none', cursor: 'pointer' }}
                                    title="Add fields to section"
                                  >
                                    + Add Fields
                                  </button>
                                </div>

                                {section.fields && section.fields.length > 0 ? (
                                  <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: section.columns === 2 ? '1fr 1fr' : '1fr',
                                    gap: '1rem'
                                  }}>
                                    {/* Column 1 */}
                                    <div>
                                      {section.columns === 2 && (
                                        <div style={{
                                          fontSize: '0.7rem',
                                          fontWeight: 600,
                                          color: 'var(--text-secondary)',
                                          marginBottom: '0.5rem',
                                          textTransform: 'uppercase',
                                          letterSpacing: '0.05em'
                                        }}>
                                          Column 1
                                        </div>
                                      )}
                                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                        {section.fields
                                          .filter(f => f.column === 1)
                                          .sort((a, b) => a.order - b.order)
                                          .map((field) => (
                                            <div
                                              key={field.id}
                                              draggable
                                              style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                padding: '0.5rem',
                                                background: 'var(--background-primary)',
                                                border: '1px solid var(--border-color)',
                                                borderRadius: '4px',
                                                fontSize: '0.75rem',
                                                cursor: 'move'
                                              }}
                                            >
                                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flex: 1 }}>
                                                <span style={{ color: 'var(--text-secondary)', fontSize: '1rem' }}>⋮⋮</span>
                                                <div style={{ flex: 1 }}>
                                                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', flexWrap: 'wrap' }}>
                                                    <span style={{ fontWeight: 600 }}>{field.fieldLabel}</span>
                                                    {field.required && (
                                                      <span style={{ color: '#DC2626', fontWeight: 600 }}>*</span>
                                                    )}
                                                    <span style={{
                                                      fontSize: '0.65rem',
                                                      padding: '0.1rem 0.3rem',
                                                      background: '#E0E7FF',
                                                      color: '#3730A3',
                                                      borderRadius: '3px',
                                                      fontWeight: 600
                                                    }}>
                                                      {field.fieldType}
                                                    </span>
                                                    <span style={{
                                                      fontSize: '0.65rem',
                                                      padding: '0.1rem 0.3rem',
                                                      background: '#DBEAFE',
                                                      color: '#1E40AF',
                                                      borderRadius: '3px',
                                                      fontWeight: 600
                                                    }}>
                                                      {field.width}
                                                    </span>
                                                    {field.readonly && (
                                                      <span style={{
                                                        fontSize: '0.65rem',
                                                        padding: '0.1rem 0.3rem',
                                                        background: '#FEE2E2',
                                                        color: '#DC2626',
                                                        borderRadius: '3px',
                                                        fontWeight: 600
                                                      }}>
                                                        READ-ONLY
                                                      </span>
                                                    )}
                                                  </div>
                                                </div>
                                              </div>
                                              <button
                                                onClick={() => handleRemoveFieldFromLayoutSection(section.id, field.id)}
                                                className="p-1 text-gray-400 hover:text-red-600"
                                                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.875rem' }}
                                                title="Remove field"
                                              >
                                                ✕
                                              </button>
                                            </div>
                                          ))}
                                        {section.fields.filter(f => f.column === 1).length === 0 && section.columns === 2 && (
                                          <div style={{
                                            padding: '1rem',
                                            textAlign: 'center',
                                            background: 'var(--background-primary)',
                                            border: '2px dashed var(--border-color)',
                                            borderRadius: '4px',
                                            color: 'var(--text-secondary)',
                                            fontSize: '0.75rem'
                                          }}>
                                            Drop fields here
                                          </div>
                                        )}
                                      </div>
                                    </div>

                                    {/* Column 2 (only if 2-column section) */}
                                    {section.columns === 2 && (
                                      <div>
                                        <div style={{
                                          fontSize: '0.7rem',
                                          fontWeight: 600,
                                          color: 'var(--text-secondary)',
                                          marginBottom: '0.5rem',
                                          textTransform: 'uppercase',
                                          letterSpacing: '0.05em'
                                        }}>
                                          Column 2
                                        </div>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                          {section.fields
                                            .filter(f => f.column === 2)
                                            .sort((a, b) => a.order - b.order)
                                            .map((field) => (
                                              <div
                                                key={field.id}
                                                draggable
                                                style={{
                                                  display: 'flex',
                                                  alignItems: 'center',
                                                  justifyContent: 'space-between',
                                                  padding: '0.5rem',
                                                  background: 'var(--background-primary)',
                                                  border: '1px solid var(--border-color)',
                                                  borderRadius: '4px',
                                                  fontSize: '0.75rem',
                                                  cursor: 'move'
                                                }}
                                              >
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flex: 1 }}>
                                                  <span style={{ color: 'var(--text-secondary)', fontSize: '1rem' }}>⋮⋮</span>
                                                  <div style={{ flex: 1 }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', flexWrap: 'wrap' }}>
                                                      <span style={{ fontWeight: 600 }}>{field.fieldLabel}</span>
                                                      {field.required && (
                                                        <span style={{ color: '#DC2626', fontWeight: 600 }}>*</span>
                                                      )}
                                                      <span style={{
                                                        fontSize: '0.65rem',
                                                        padding: '0.1rem 0.3rem',
                                                        background: '#E0E7FF',
                                                        color: '#3730A3',
                                                        borderRadius: '3px',
                                                        fontWeight: 600
                                                      }}>
                                                        {field.fieldType}
                                                      </span>
                                                      <span style={{
                                                        fontSize: '0.65rem',
                                                        padding: '0.1rem 0.3rem',
                                                        background: '#DBEAFE',
                                                        color: '#1E40AF',
                                                        borderRadius: '3px',
                                                        fontWeight: 600
                                                      }}>
                                                        {field.width}
                                                      </span>
                                                      {field.readonly && (
                                                        <span style={{
                                                          fontSize: '0.65rem',
                                                          padding: '0.1rem 0.3rem',
                                                          background: '#FEE2E2',
                                                          color: '#DC2626',
                                                          borderRadius: '3px',
                                                          fontWeight: 600
                                                        }}>
                                                          READ-ONLY
                                                        </span>
                                                      )}
                                                    </div>
                                                  </div>
                                                </div>
                                                <button
                                                  onClick={() => handleRemoveFieldFromLayoutSection(section.id, field.id)}
                                                  className="p-1 text-gray-400 hover:text-red-600"
                                                  style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.875rem' }}
                                                  title="Remove field"
                                                >
                                                  ✕
                                                </button>
                                              </div>
                                            ))}
                                          {section.fields.filter(f => f.column === 2).length === 0 && (
                                            <div style={{
                                              padding: '1rem',
                                              textAlign: 'center',
                                              background: 'var(--background-primary)',
                                              border: '2px dashed var(--border-color)',
                                              borderRadius: '4px',
                                              color: 'var(--text-secondary)',
                                              fontSize: '0.75rem'
                                            }}>
                                              Drop fields here
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                ) : (
                                  <div style={{
                                    padding: '1.5rem',
                                    textAlign: 'center',
                                    background: 'var(--background-primary)',
                                    border: '2px dashed var(--border-color)',
                                    borderRadius: '4px',
                                    color: 'var(--text-secondary)',
                                    fontSize: '0.75rem'
                                  }}>
                                    <div style={{ marginBottom: '0.75rem' }}>No fields assigned to this section yet</div>
                                    <button
                                      onClick={() => {
                                        setSelectedSectionId(section.id);
                                        setShowFieldSelectorModal(true);
                                      }}
                                      className="px-2 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700"
                                      style={{ border: 'none', cursor: 'pointer' }}
                                    >
                                      + Add Fields to Section
                                    </button>
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Action Buttons */}
                            <div style={{ display: 'flex', gap: '0.25rem' }}>
                              <button
                                onClick={() => handleEditLayoutSection(section)}
                                className="px-2 py-1 text-blue-600 hover:bg-blue-50 rounded text-sm"
                                style={{ background: 'none', border: '1px solid #3B82F6', cursor: 'pointer' }}
                                title="Edit section"
                              >
                                ✏️ Edit
                              </button>
                              <button
                                onClick={() => handleDeleteLayoutSection(section.id)}
                                className="px-2 py-1 text-red-600 hover:bg-red-50 rounded text-sm"
                                style={{ background: 'none', border: '1px solid #DC2626', cursor: 'pointer' }}
                                title="Delete section"
                              >
                                🗑️
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{
                      padding: '2rem',
                      textAlign: 'center',
                      background: 'var(--background-secondary)',
                      border: '1px solid var(--border-color)',
                      borderRadius: '6px',
                      color: 'var(--text-secondary)'
                    }}>
                      <div style={{ fontSize: '2rem', marginBottom: '0.5rem', opacity: 0.3 }}>📦</div>
                      <div>No layout sections defined yet.</div>
                      <div style={{ marginTop: '1rem' }}>
                        <button
                          className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 text-sm"
                          onClick={handleAddLayoutSection}
                        >
                          + Add First Layout Section
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Template Sections (Legacy) */}
              {sectionManagementMode === 'template' && (
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
                    <h4 style={{ fontSize: '1rem', fontWeight: 600, margin: 0 }}>
                      Template Sections ({selectedTemplate.sections?.length || 0})
                    </h4>
                    <button
                      data-tour="sections"
                      className="px-3 py-1.5 bg-green-600 text-white rounded hover:bg-green-700 text-sm flex items-center gap-1"
                      onClick={handleAddSection}
                    >
                      + Add Section
                    </button>
                  </div>
                </div>
              )}

              {/* Original sections list for template mode */}
              {sectionManagementMode === 'template' && (
                <div>

                {selectedTemplate.sections && selectedTemplate.sections.length > 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    {selectedTemplate.sections.map((section, index) => (
                      <div
                        key={section.id}
                        draggable={true}
                        onDragStart={(e) => handleSectionDragStart(e, section.id)}
                        onDragEnd={handleSectionDragEnd}
                        onDragOver={(e) => handleSectionDragOver(e, section.id)}
                        onDragLeave={handleSectionDragLeave}
                        onDrop={(e) => handleSectionDrop(e, section.id)}
                        style={{
                          padding: '1rem',
                          background: 'var(--background-primary)',
                          border: `2px solid ${
                            dragOverSectionId === section.id
                              ? 'var(--primary-color)'
                              : draggedSectionId === section.id
                              ? 'var(--primary-color)'
                              : 'var(--border-color)'
                          }`,
                          borderRadius: '6px',
                          cursor: 'move',
                          transition: 'all 0.2s ease',
                          opacity: draggedSectionId === section.id ? 0.5 : 1,
                          transform: dragOverSectionId === section.id ? 'translateY(-2px)' : 'none',
                          boxShadow: dragOverSectionId === section.id
                            ? '0 4px 12px rgba(0,0,0,0.15)'
                            : 'none'
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'start', gap: '1rem' }}>
                          {/* Drag Handle - only show if not touch device */}
                          {!isTouchDevice && (
                            <div style={{
                              marginRight: '0.5rem',
                              color: 'var(--text-secondary)',
                              cursor: 'grab',
                              fontSize: '1.2rem',
                              alignSelf: 'center'
                            }}>
                              ⋮⋮
                            </div>
                          )}

                          {/* Reorder Buttons */}
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', paddingTop: '0.25rem' }}>
                            <button
                              onClick={() => handleReorderSection(section.id, 'up')}
                              disabled={index === 0}
                              className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
                              title="Move up"
                              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.75rem' }}
                            >
                              ▲
                            </button>
                            <button
                              onClick={() => handleReorderSection(section.id, 'down')}
                              disabled={index === selectedTemplate.sections.length - 1}
                              className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
                              title="Move down"
                              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.75rem' }}
                            >
                              ▼
                            </button>
                          </div>

                          {/* Section Content */}
                          <div style={{ flex: 1 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                              <div style={{ fontWeight: 600, fontSize: '0.875rem' }}>
                                {section.name}
                              </div>
                              {!section.visible && (
                                <span style={{
                                  fontSize: '0.65rem',
                                  padding: '0.15rem 0.4rem',
                                  background: '#FEE2E2',
                                  color: '#DC2626',
                                  borderRadius: '3px',
                                  fontWeight: 600
                                }}>
                                  HIDDEN
                                </span>
                              )}
                              {section.collapsible && (
                                <span style={{
                                  fontSize: '0.65rem',
                                  padding: '0.15rem 0.4rem',
                                  background: '#DBEAFE',
                                  color: '#1D4ED8',
                                  borderRadius: '3px',
                                  fontWeight: 600
                                }}>
                                  COLLAPSIBLE
                                </span>
                              )}
                            </div>
                            {section.description && (
                              <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '0.5rem' }}>
                                {section.description}
                              </div>
                            )}
                            <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '0.75rem' }}>
                              Page: <span style={{ fontWeight: 500 }}>{section.page}</span> •
                              Tab: <span style={{ fontWeight: 500 }}>{section.tab}</span> •
                              Columns: <span style={{ fontWeight: 500 }}>{section.columns}</span>
                            </div>

                            {/* Fields Preview */}
                            <div style={{ marginTop: '0.75rem', paddingTop: '0.75rem', borderTop: '1px solid var(--border-color)' }}>
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                                <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-secondary)' }}>
                                  Fields ({section.fields?.length || 0})
                                </div>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleAddFieldsToSection(section.id);
                                  }}
                                  className="px-2 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700"
                                  style={{ border: 'none', cursor: 'pointer' }}
                                  title="Add fields to section"
                                >
                                  + Add Fields
                                </button>
                              </div>

                              {section.fields && section.fields.length > 0 ? (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                  {section.fields.map((field, fieldIndex) => (
                                    <div
                                      key={field.fieldId}
                                      draggable={true}
                                      onDragStart={(e) => handleFieldDragStart(e, section.id, field.fieldId)}
                                      onDragEnd={handleFieldDragEnd}
                                      onDragOver={(e) => handleFieldDragOver(e, field.fieldId)}
                                      onDragLeave={handleFieldDragLeave}
                                      onDrop={(e) => handleFieldDrop(e, section.id, field.fieldId)}
                                      style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        padding: '0.5rem',
                                        background: 'var(--background-secondary)',
                                        border: `2px solid ${
                                          dragOverFieldId === field.fieldId
                                            ? 'var(--primary-color)'
                                            : draggedFieldId === field.fieldId
                                            ? 'var(--primary-color)'
                                            : 'var(--border-color)'
                                        }`,
                                        borderRadius: '4px',
                                        fontSize: '0.75rem',
                                        cursor: 'move',
                                        transition: 'all 0.2s ease',
                                        opacity: draggedFieldId === field.fieldId ? 0.5 : 1,
                                        transform: dragOverFieldId === field.fieldId ? 'translateY(-2px)' : 'none',
                                        boxShadow: dragOverFieldId === field.fieldId
                                          ? '0 2px 8px rgba(0,0,0,0.1)'
                                          : 'none'
                                      }}
                                    >
                                      {/* Drag Handle - only show if not touch device */}
                                      {!isTouchDevice && (
                                        <div style={{
                                          marginRight: '0.5rem',
                                          color: 'var(--text-secondary)',
                                          cursor: 'grab',
                                          fontSize: '1rem'
                                        }}>
                                          ⋮⋮
                                        </div>
                                      )}

                                      {/* Reorder Buttons */}
                                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.1rem' }}>
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleReorderField(section.id, field.fieldId, 'up');
                                          }}
                                          disabled={fieldIndex === 0}
                                          className="text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
                                          style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.6rem', padding: '0', lineHeight: '1' }}
                                          title="Move up"
                                        >
                                          ▲
                                        </button>
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleReorderField(section.id, field.fieldId, 'down');
                                          }}
                                          disabled={fieldIndex === section.fields.length - 1}
                                          className="text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
                                          style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.6rem', padding: '0', lineHeight: '1' }}
                                          title="Move down"
                                        >
                                          ▼
                                        </button>
                                      </div>

                                      {/* Field Info */}
                                      <div style={{ flex: 1 }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', flexWrap: 'wrap' }}>
                                          <span style={{ fontWeight: 600 }}>{field.label}</span>
                                          {field.required && (
                                            <span style={{ color: '#DC2626', fontWeight: 600 }}>*</span>
                                          )}
                                          <span style={{
                                            fontSize: '0.65rem',
                                            padding: '0.1rem 0.3rem',
                                            background: '#E0E7FF',
                                            color: '#3730A3',
                                            borderRadius: '3px',
                                            fontWeight: 600
                                          }}>
                                            {field.type}
                                          </span>
                                          <span style={{
                                            fontSize: '0.65rem',
                                            padding: '0.1rem 0.3rem',
                                            background: '#DBEAFE',
                                            color: '#1E40AF',
                                            borderRadius: '3px',
                                            fontWeight: 600
                                          }}>
                                            {field.width}
                                          </span>
                                          {field.pii && (
                                            <span style={{
                                              fontSize: '0.65rem',
                                              padding: '0.1rem 0.3rem',
                                              background: '#FEF3C7',
                                              color: '#92400E',
                                              borderRadius: '3px',
                                              fontWeight: 600
                                            }}>
                                              PII
                                            </span>
                                          )}
                                          {field.encryption && (
                                            <span style={{
                                              fontSize: '0.65rem',
                                              padding: '0.1rem 0.3rem',
                                              background: '#DBEAFE',
                                              color: '#1E40AF',
                                              borderRadius: '3px',
                                              fontWeight: 600
                                            }}>
                                              🔒 {field.encryption.toUpperCase()}
                                            </span>
                                          )}
                                          {field.masking?.enabled && (
                                            <span style={{
                                              fontSize: '0.65rem',
                                              padding: '0.1rem 0.3rem',
                                              background: '#E0E7FF',
                                              color: '#3730A3',
                                              borderRadius: '3px',
                                              fontWeight: 600
                                            }}>
                                              👁️ MASKED
                                            </span>
                                          )}
                                        </div>
                                      </div>

                                      {/* Action Buttons */}
                                      <div style={{ display: 'flex', gap: '0.25rem' }}>
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleOpenPIIConfig(section.id, field);
                                          }}
                                          className="p-1 text-gray-400 hover:text-yellow-600"
                                          style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.875rem' }}
                                          title="Configure PII settings"
                                        >
                                          🔒
                                        </button>
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleEditField(section.id, field);
                                          }}
                                          className="p-1 text-gray-400 hover:text-blue-600"
                                          style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.875rem' }}
                                          title="Configure field"
                                        >
                                          ⚙️
                                        </button>
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleRemoveField(section.id, field.fieldId);
                                          }}
                                          className="p-1 text-gray-400 hover:text-red-600"
                                          style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.875rem' }}
                                          title="Remove field"
                                        >
                                          ✕
                                        </button>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <div style={{
                                  padding: '1rem',
                                  textAlign: 'center',
                                  background: 'var(--background-primary)',
                                  border: '1px dashed var(--border-color)',
                                  borderRadius: '4px',
                                  color: 'var(--text-secondary)',
                                  fontSize: '0.75rem'
                                }}>
                                  <div style={{ marginBottom: '0.5rem' }}>No fields assigned</div>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleAddFieldsToSection(section.id);
                                    }}
                                    className="px-2 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700"
                                    style={{ border: 'none', cursor: 'pointer' }}
                                  >
                                    + Add Fields to Section
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Action Buttons */}
                          <div style={{ display: 'flex', gap: '0.25rem' }}>
                            <button
                              onClick={() => handleEditSection(section)}
                              className="px-2 py-1 text-blue-600 hover:bg-blue-50 rounded text-sm"
                              style={{ background: 'none', border: '1px solid #3B82F6', cursor: 'pointer' }}
                              title="Edit section"
                            >
                              ✏️ Edit
                            </button>
                            <button
                              onClick={() => handleDeleteSection(section.id)}
                              className="px-2 py-1 text-red-600 hover:bg-red-50 rounded text-sm"
                              style={{ background: 'none', border: '1px solid #DC2626', cursor: 'pointer' }}
                              title="Delete section"
                            >
                              🗑️
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{
                    padding: '2rem',
                    textAlign: 'center',
                    background: 'var(--background-secondary)',
                    border: '1px solid var(--border-color)',
                    borderRadius: '6px',
                    color: 'var(--text-secondary)'
                  }}>
                    <div style={{ fontSize: '2rem', marginBottom: '0.5rem', opacity: 0.3 }}>📦</div>
                    <div>No sections defined yet.</div>
                    <div style={{ marginTop: '1rem' }}>
                      <button
                        className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 text-sm"
                        onClick={handleAddSection}
                      >
                        + Add First Section
                      </button>
                    </div>
                  </div>
                )}
                </div>
              )}

              {/* Visibility Rules */}
              {sectionManagementMode === 'rules' && (
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
                    <h4 style={{ fontSize: '1rem', fontWeight: 600, margin: 0 }}>
                      Visibility Rules ({visibilityRules.length})
                      {rulesLoading && (
                        <span className="spinner" style={{
                          marginLeft: '0.5rem',
                          width: '14px',
                          height: '14px',
                          borderWidth: '2px',
                          display: 'inline-block'
                        }}></span>
                      )}
                    </h4>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button
                        className="px-3 py-1.5 bg-purple-600 text-white rounded hover:bg-purple-700 text-sm flex items-center gap-1"
                        onClick={() => {
                          setShowRulePreviewModal(true);
                          setRulePreviewData({});
                        }}
                        disabled={visibilityRules.length === 0}
                      >
                        🔍 Test Rules
                      </button>
                      <button
                        className="px-3 py-1.5 bg-green-600 text-white rounded hover:bg-green-700 text-sm flex items-center gap-1"
                        onClick={() => {
                          setEditingRule(null);
                          setShowRuleModal(true);
                        }}
                      >
                        + Add Visibility Rule
                      </button>
                    </div>
                  </div>

                  {/* Info banner */}
                  <div style={{
                    padding: '0.75rem 1rem',
                    background: '#F3E8FF',
                    borderRadius: '6px',
                    fontSize: '0.875rem',
                    color: '#6B21A8',
                    marginBottom: '1rem',
                    border: '1px solid #D8B4FE'
                  }}>
                    <span>ℹ️</span>
                    <span style={{ marginLeft: '0.5rem' }}>
                      <strong>Conditional Visibility:</strong> Rules control when sections and fields are shown, hidden, required, or read-only based on field values. Lower priority number = higher priority.
                    </span>
                  </div>

                  {visibilityRules.length > 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                      {visibilityRules
                        .sort((a, b) => a.priority - b.priority)
                        .map((rule, index) => (
                          <div
                            key={rule.id}
                            draggable={true}
                            onDragStart={() => setDraggedRuleId(rule.id)}
                            onDragEnd={() => setDraggedRuleId(null)}
                            onDragOver={(e) => {
                              e.preventDefault();
                              e.currentTarget.style.borderColor = 'var(--primary-color)';
                            }}
                            onDragLeave={(e) => {
                              e.currentTarget.style.borderColor = 'var(--border-color)';
                            }}
                            onDrop={(e) => {
                              e.preventDefault();
                              e.currentTarget.style.borderColor = 'var(--border-color)';

                              if (!draggedRuleId || draggedRuleId === rule.id) return;

                              const draggedIndex = visibilityRules.findIndex(r => r.id === draggedRuleId);
                              const targetIndex = index;

                              const reordered = [...visibilityRules];
                              const [draggedRule] = reordered.splice(draggedIndex, 1);
                              reordered.splice(targetIndex, 0, draggedRule);

                              const ruleOrders = reordered.map((r, i) => ({
                                ruleId: r.id,
                                priority: i + 1
                              }));

                              if (selectedTemplateId) {
                                reorderRulesMutation.mutate({
                                  layoutId: selectedTemplateId,
                                  ruleOrders
                                });
                              }
                            }}
                            style={{
                              padding: '1rem',
                              background: rule.enabled ? 'var(--background-primary)' : 'var(--background-secondary)',
                              border: `2px solid ${draggedRuleId === rule.id ? 'var(--primary-color)' : 'var(--border-color)'}`,
                              borderRadius: '6px',
                              opacity: rule.enabled ? 1 : 0.6,
                              cursor: 'move',
                              transition: 'all 0.2s ease'
                            }}
                          >
                            <div style={{ display: 'flex', alignItems: 'start', gap: '1rem' }}>
                              {/* Drag Handle */}
                              {!isTouchDevice && (
                                <div style={{
                                  marginRight: '0.5rem',
                                  color: 'var(--text-secondary)',
                                  cursor: 'grab',
                                  fontSize: '1.2rem',
                                  alignSelf: 'center'
                                }}>
                                  ⋮⋮
                                </div>
                              )}

                              {/* Rule Info */}
                              <div style={{ flex: 1 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                                  <span style={{
                                    fontSize: '0.75rem',
                                    fontWeight: 600,
                                    padding: '0.25rem 0.5rem',
                                    background: 'var(--background-secondary)',
                                    borderRadius: '4px',
                                    color: 'var(--text-secondary)'
                                  }}>
                                    Priority: {rule.priority}
                                  </span>
                                  <span style={{
                                    fontSize: '0.75rem',
                                    fontWeight: 600,
                                    padding: '0.25rem 0.5rem',
                                    background: rule.action === 'show' ? '#DCFCE7' :
                                              rule.action === 'hide' ? '#FEE2E2' :
                                              rule.action === 'require' ? '#FEF3C7' : '#E0E7FF',
                                    color: rule.action === 'show' ? '#166534' :
                                           rule.action === 'hide' ? '#991B1B' :
                                           rule.action === 'require' ? '#92400E' : '#3730A3',
                                    borderRadius: '4px'
                                  }}>
                                    {rule.action.toUpperCase()}
                                  </span>
                                  <span style={{
                                    fontSize: '0.75rem',
                                    fontWeight: 600,
                                    padding: '0.25rem 0.5rem',
                                    background: '#DBEAFE',
                                    color: '#1E40AF',
                                    borderRadius: '4px'
                                  }}>
                                    {rule.target === 'section' ? '📐 Section' : '📝 Field'}
                                  </span>
                                </div>

                                <div style={{ fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.25rem' }}>
                                  {rule.name}
                                </div>

                                {rule.description && (
                                  <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '0.5rem' }}>
                                    {rule.description}
                                  </div>
                                )}

                                <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                                  <strong>Target:</strong> {rule.targetName}
                                </div>

                                <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '0.25rem' }}>
                                  <strong>Conditions ({rule.logic}):</strong> {rule.conditions.length} condition(s)
                                </div>

                                <div style={{ marginTop: '0.5rem', fontSize: '0.75rem' }}>
                                  {rule.conditions.map((cond, idx) => (
                                    <div key={cond.id} style={{
                                      padding: '0.5rem',
                                      background: 'var(--background-secondary)',
                                      borderRadius: '4px',
                                      marginTop: idx > 0 ? '0.25rem' : 0
                                    }}>
                                      <span style={{ fontWeight: 600 }}>{cond.fieldLabel}</span>
                                      {' '}<span style={{ color: 'var(--text-secondary)' }}>{cond.operator}</span>{' '}
                                      {cond.operator !== 'is_empty' && cond.operator !== 'is_not_empty' && (
                                        <span style={{ fontWeight: 600 }}>{JSON.stringify(cond.value)}</span>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </div>

                              {/* Actions */}
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                <label style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '0.5rem',
                                  fontSize: '0.75rem',
                                  cursor: 'pointer'
                                }}>
                                  <input
                                    type="checkbox"
                                    checked={rule.enabled}
                                    onChange={(e) => {
                                      if (selectedTemplateId) {
                                        toggleRuleMutation.mutate({
                                          layoutId: selectedTemplateId,
                                          ruleId: rule.id,
                                          enabled: e.target.checked
                                        });
                                      }
                                    }}
                                  />
                                  Enabled
                                </label>

                                <div style={{ display: 'flex', gap: '0.25rem' }}>
                                  <button
                                    onClick={() => {
                                      setEditingRule(rule);
                                      setShowRuleModal(true);
                                    }}
                                    className="px-2 py-1 text-blue-600 hover:bg-blue-50 rounded text-sm"
                                    style={{ background: 'none', border: '1px solid #3B82F6', cursor: 'pointer' }}
                                    title="Edit rule"
                                  >
                                    ✏️
                                  </button>
                                  <button
                                    onClick={() => {
                                      if (window.confirm(`Delete rule "${rule.name}"?`)) {
                                        if (selectedTemplateId) {
                                          deleteRuleMutation.mutate({
                                            layoutId: selectedTemplateId,
                                            ruleId: rule.id
                                          });
                                        }
                                      }
                                    }}
                                    className="px-2 py-1 text-red-600 hover:bg-red-50 rounded text-sm"
                                    style={{ background: 'none', border: '1px solid #DC2626', cursor: 'pointer' }}
                                    title="Delete rule"
                                  >
                                    🗑️
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                    </div>
                  ) : (
                    <div style={{
                      padding: '2rem',
                      textAlign: 'center',
                      background: 'var(--background-secondary)',
                      border: '1px solid var(--border-color)',
                      borderRadius: '6px',
                      color: 'var(--text-secondary)'
                    }}>
                      <div style={{ fontSize: '2rem', marginBottom: '0.5rem', opacity: 0.3 }}>🔒</div>
                      <div>No visibility rules defined yet.</div>
                      <div style={{ marginTop: '1rem' }}>
                        <button
                          className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 text-sm"
                          onClick={() => {
                            setEditingRule(null);
                            setShowRuleModal(true);
                          }}
                        >
                          + Add First Rule
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Related Lists & Actions View */}
              {sectionManagementMode === 'actions' && (
                <div>
                  {/* Quick Actions Section */}
                  <div style={{ marginBottom: '2rem' }}>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '1rem'
                    }}>
                      <div>
                        <h4 style={{ margin: 0, marginBottom: '0.25rem', color: 'var(--text-primary)' }}>
                          Quick Actions
                        </h4>
                        <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                          Action buttons displayed at the top of the record page
                        </p>
                      </div>
                      <button
                        className="px-3 py-1.5 bg-green-600 text-white rounded hover:bg-green-700 text-sm flex items-center gap-1"
                        onClick={() => {
                          setEditingQuickAction(null);
                          setShowQuickActionModal(true);
                        }}
                      >
                        + Add Action
                      </button>
                    </div>

                    {quickActionsLoading ? (
                      <div style={{ padding: '2rem', textAlign: 'center' }}>
                        <span className="spinner" style={{
                          width: '24px',
                          height: '24px',
                          borderWidth: '3px',
                          display: 'inline-block'
                        }}></span>
                        <p style={{ marginTop: '1rem', color: 'var(--text-secondary)' }}>
                          Loading actions...
                        </p>
                      </div>
                    ) : quickActions.length === 0 ? (
                      <div style={{
                        padding: '2rem',
                        textAlign: 'center',
                        background: 'var(--background-secondary)',
                        borderRadius: '8px',
                        border: '2px dashed var(--border-color)'
                      }}>
                        <div style={{ fontSize: '2rem', marginBottom: '0.5rem', opacity: 0.3 }}>⚡</div>
                        <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
                          No quick actions yet
                        </p>
                      </div>
                    ) : (
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '1rem' }}>
                        {quickActions
                          .sort((a, b) => a.order - b.order)
                          .map((action) => (
                            <QuickActionCard
                              key={action.id}
                              action={action}
                              layoutId={selectedTemplateId!}
                              onEdit={() => {
                                setEditingQuickAction(action);
                                setShowQuickActionModal(true);
                              }}
                              onDelete={() => {
                                if (window.confirm(`Delete action "${action.label}"?`)) {
                                  deleteQuickActionMutation.mutate({
                                    layoutId: selectedTemplateId!,
                                    actionId: action.id
                                  });
                                }
                              }}
                            />
                          ))}
                      </div>
                    )}
                  </div>

                  {/* Related Lists Section */}
                  <div>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '1rem'
                    }}>
                      <div>
                        <h4 style={{ margin: 0, marginBottom: '0.25rem', color: 'var(--text-primary)' }}>
                          Related Lists
                        </h4>
                        <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                          Lists of related records displayed on the page
                        </p>
                      </div>
                      <button
                        className="px-3 py-1.5 bg-green-600 text-white rounded hover:bg-green-700 text-sm flex items-center gap-1"
                        onClick={() => {
                          setEditingRelatedList(null);
                          setShowRelatedListModal(true);
                        }}
                      >
                        + Add Related List
                      </button>
                    </div>

                    {relatedListsLoading ? (
                      <div style={{ padding: '2rem', textAlign: 'center' }}>
                        <span className="spinner" style={{
                          width: '24px',
                          height: '24px',
                          borderWidth: '3px',
                          display: 'inline-block'
                        }}></span>
                        <p style={{ marginTop: '1rem', color: 'var(--text-secondary)' }}>
                          Loading related lists...
                        </p>
                      </div>
                    ) : relatedLists.length === 0 ? (
                      <div style={{
                        padding: '3rem',
                        textAlign: 'center',
                        background: 'var(--background-secondary)',
                        borderRadius: '8px',
                        border: '2px dashed var(--border-color)'
                      }}>
                        <div style={{ fontSize: '3rem', marginBottom: '1rem', opacity: 0.3 }}>🔗</div>
                        <p style={{ color: 'var(--text-secondary)', marginBottom: '1rem' }}>
                          No related lists yet
                        </p>
                        <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: '1.5rem' }}>
                          Add related lists to show related records on this page
                        </p>
                        <button
                          className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 text-sm"
                          onClick={() => setShowRelatedListModal(true)}
                        >
                          Add First Related List
                        </button>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                        {relatedLists
                          .sort((a, b) => a.order - b.order)
                          .map((list, index) => (
                            <RelatedListCard
                              key={list.id}
                              list={list}
                              listIndex={index}
                              layoutId={selectedTemplateId!}
                              onEdit={() => {
                                setEditingRelatedList(list);
                                setShowRelatedListModal(true);
                              }}
                              onDelete={() => {
                                if (window.confirm(`Delete related list "${list.label}"?`)) {
                                  deleteRelatedListMutation.mutate({
                                    layoutId: selectedTemplateId!,
                                    listId: list.id
                                  });
                                }
                              }}
                            />
                          ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Assignments & Testing View (Phase 8) */}
              {sectionManagementMode === 'assignments' && (
                <div>
                  {/* Action Bar */}
                  <div style={{
                    display: 'flex',
                    gap: '0.5rem',
                    marginBottom: '1.5rem',
                    padding: '1rem',
                    background: 'var(--background-secondary)',
                    borderRadius: '8px',
                    border: '1px solid var(--border-color)'
                  }}>
                    <button
                      className="px-3 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
                      onClick={async () => {
                        const result = await previewLayout(selectedTemplateId!);
                        setLayoutPreviewData(result.data);
                        setShowLayoutPreviewModal(true);
                      }}
                    >
                      👁️ Preview Layout
                    </button>

                    <button
                      className="px-3 py-1.5 bg-gray-600 text-white rounded hover:bg-gray-700 text-sm"
                      onClick={async () => {
                        const result = await validateLayout(selectedTemplateId!);
                        setValidationResult(result.data);
                        setShowValidationModal(true);
                      }}
                    >
                      ✅ Validate
                    </button>

                    <button
                      className="px-3 py-1.5 bg-gray-600 text-white rounded hover:bg-gray-700 text-sm"
                      onClick={async () => {
                        const result = await getLayoutHealthCheck(selectedTemplateId!);
                        setHealthCheck(result.data);
                        setShowHealthModal(true);
                      }}
                    >
                      🏥 Health Check
                    </button>

                    <button
                      className="px-3 py-1.5 bg-gray-600 text-white rounded hover:bg-gray-700 text-sm"
                      onClick={() => setShowCloneModal(true)}
                    >
                      📋 Clone Layout
                    </button>
                  </div>

                  {/* Layout Assignments Section */}
                  <div>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '1rem'
                    }}>
                      <div>
                        <h4 style={{ margin: 0, marginBottom: '0.25rem', color: 'var(--text-primary)' }}>
                          Layout Assignments
                        </h4>
                        <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                          Assign this layout to profiles and record types
                        </p>
                      </div>
                      <button
                        className="px-3 py-1.5 bg-green-600 text-white rounded hover:bg-green-700 text-sm"
                        onClick={() => setShowAssignmentModal(true)}
                      >
                        + Add Assignment
                      </button>
                    </div>

                    {assignmentsLoading ? (
                      <div style={{ padding: '2rem', textAlign: 'center' }}>
                        <span className="spinner" style={{
                          width: '24px',
                          height: '24px',
                          borderWidth: '3px',
                          display: 'inline-block'
                        }}></span>
                        <p style={{ marginTop: '1rem', color: 'var(--text-secondary)' }}>
                          Loading assignments...
                        </p>
                      </div>
                    ) : layoutAssignments.length === 0 ? (
                      <div style={{
                        padding: '3rem',
                        textAlign: 'center',
                        background: 'var(--background-secondary)',
                        borderRadius: '8px',
                        border: '2px dashed var(--border-color)'
                      }}>
                        <div style={{ fontSize: '3rem', marginBottom: '1rem', opacity: 0.3 }}>🎯</div>
                        <p style={{ color: 'var(--text-secondary)', marginBottom: '1rem' }}>
                          No assignments yet
                        </p>
                        <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: '1.5rem' }}>
                          Assign this layout to profiles or record types to make it available
                        </p>
                        <button
                          className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 text-sm"
                          onClick={() => setShowAssignmentModal(true)}
                        >
                          Create First Assignment
                        </button>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                        {layoutAssignments
                          .sort((a, b) => a.priority - b.priority)
                          .map((assignment) => (
                            <div
                              key={assignment.id}
                              style={{
                                padding: '1rem',
                                background: 'var(--background-secondary)',
                                border: '1px solid var(--border-color)',
                                borderRadius: '6px',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center'
                              }}
                            >
                              <div style={{ flex: 1 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.25rem' }}>
                                  <span style={{ fontSize: '1.25rem' }}>
                                    {assignment.profileId ? '👤' : '📄'}
                                  </span>
                                  <div>
                                    <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
                                      {assignment.profileName || assignment.recordType || 'Unknown'}
                                    </div>
                                    <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                                      {assignment.profileId ? 'Profile Assignment' : 'Record Type Assignment'}
                                      {assignment.isDefault && ' • Default'}
                                    </div>
                                  </div>
                                </div>
                              </div>

                              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                                <div style={{
                                  fontSize: '0.75rem',
                                  color: 'var(--text-secondary)',
                                  textAlign: 'right'
                                }}>
                                  Priority: {assignment.priority}
                                </div>

                                {assignment.isDefault && (
                                  <span style={{
                                    padding: '0.25rem 0.75rem',
                                    background: 'rgba(33, 150, 243, 0.15)',
                                    color: '#2196F3',
                                    borderRadius: '4px',
                                    fontSize: '0.75rem',
                                    fontWeight: 600,
                                    textTransform: 'uppercase'
                                  }}>
                                    Default
                                  </span>
                                )}

                                <button
                                  onClick={() => {
                                    if (window.confirm('Delete this assignment?')) {
                                      deleteAssignmentMutation.mutate({
                                        layoutId: selectedTemplateId!,
                                        assignmentId: assignment.id
                                      });
                                    }
                                  }}
                                  style={{
                                    padding: '0.5rem',
                                    background: 'transparent',
                                    border: 'none',
                                    color: 'var(--danger-color)',
                                    cursor: 'pointer',
                                    fontSize: '1.1rem'
                                  }}
                                  title="Delete assignment"
                                >
                                  🗑️
                                </button>
                              </div>
                            </div>
                          ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Create Template Modal */}
      {showCreateModal && (
        <CreateTemplateModal
          application={selectedApp}
          onClose={() => setShowCreateModal(false)}
          onCreate={handleCreateTemplate}
          isCreating={createMutation.isPending}
        />
      )}

      {/* Section Modal */}
      {showSectionModal && (
        <SectionModal
          section={editingSection}
          availableTabs={availableTabs}
          onClose={() => {
            setShowSectionModal(false);
            setEditingSection(null);
          }}
          onSave={handleSaveSection}
          isSaving={updateMutation.isPending}
        />
      )}

      {/* Layout Section Modal (New API) */}
      {showLayoutSectionModal && (
        <LayoutSectionModal
          section={editingLayoutSection}
          onClose={() => {
            setShowLayoutSectionModal(false);
            setEditingLayoutSection(null);
          }}
          onSave={handleSaveLayoutSection}
          isSaving={createSectionMutation.isPending || updateSectionMutation.isPending}
        />
      )}

      {/* Field Selector Modal */}
      {showFieldSelectorModal && selectedSectionId && (
        <FieldSelectorModal
          availableFields={availableFields}
          existingFieldIds={
            sectionManagementMode === 'layout'
              ? layoutSections
                  .find(s => s.id === selectedSectionId)
                  ?.fields?.map(f => f.fieldId) || []
              : selectedTemplate?.sections
                  .find(s => s.id === selectedSectionId)
                  ?.fields?.map(f => f.fieldId) || []
          }
          onClose={() => {
            setShowFieldSelectorModal(false);
            setSelectedSectionId(null);
          }}
          onAssign={(fieldIds) => {
            if (sectionManagementMode === 'layout' && selectedSectionId) {
              handleAddFieldToLayoutSection(selectedSectionId, fieldIds);
              setShowFieldSelectorModal(false);
              setSelectedSectionId(null);
            } else {
              handleAssignFields(fieldIds);
            }
          }}
        />
      )}

      {/* Field Config Modal */}
      {showFieldConfigModal && editingField && (
        <FieldConfigModal
          field={editingField}
          onClose={() => {
            setShowFieldConfigModal(false);
            setEditingField(null);
            setSelectedSectionId(null);
          }}
          onSave={handleSaveFieldConfig}
        />
      )}

      {/* PII Configuration Modal */}
      {showPIIConfigModal && configuringPIIField && (
        <PIIConfigModal
          field={configuringPIIField.field}
          onClose={() => {
            setShowPIIConfigModal(false);
            setConfiguringPIIField(null);
          }}
          onSave={handleSavePIIConfig}
        />
      )}

      {/* Visibility Rule Modal */}
      {showRuleModal && (
        <RuleBuilderModal
          rule={editingRule}
          layoutId={selectedTemplateId || ''}
          sections={layoutSections}
          fields={availableFields}
          onClose={() => {
            setShowRuleModal(false);
            setEditingRule(null);
          }}
          onSave={(ruleData) => {
            if (!selectedTemplateId) return;

            if (editingRule) {
              updateRuleMutation.mutate({
                layoutId: selectedTemplateId,
                ruleId: editingRule.id,
                updates: ruleData
              });
            } else {
              createRuleMutation.mutate({
                layoutId: selectedTemplateId,
                ruleData
              });
            }
          }}
        />
      )}

      {/* Rule Preview/Test Modal */}
      {showRulePreviewModal && (
        <RulePreviewModal
          isOpen={showRulePreviewModal}
          layoutId={selectedTemplateId || ''}
          rules={visibilityRules}
          fields={availableFields}
          onClose={() => setShowRulePreviewModal(false)}
        />
      )}

      {/* Version History Modal */}
      {showVersionHistoryModal && (
        <VersionHistoryModal
          isOpen={showVersionHistoryModal}
          template={selectedTemplate || null}
          versions={versions}
          isLoading={versionsLoading}
          onClose={() => setShowVersionHistoryModal(false)}
          onRollback={handleRollbackVersion}
          onCompare={handleCompareVersion}
          isRollingBack={rollbackMutation.isPending}
        />
      )}

      {/* Version Compare Modal */}
      {showCompareModal && (
        <VersionCompareModal
          isOpen={showCompareModal}
          currentTemplate={selectedTemplate || null}
          compareVersion={compareVersion || null}
          onClose={() => {
            setShowCompareModal(false);
            setCompareVersionId(null);
          }}
        />
      )}

      {/* Preview Modal */}
      {showPreviewModal && (
        <PreviewModal
          isOpen={showPreviewModal}
          template={selectedTemplate || null}
          mode={previewMode}
          showSampleData={showSampleData}
          onClose={handleClosePreview}
          onChangeMode={handleChangePreviewMode}
          onToggleSampleData={handleToggleSampleData}
        />
      )}

      {/* Quick Action Modal */}
      {showQuickActionModal && (
        <QuickActionModal
          action={editingQuickAction}
          layoutId={selectedTemplateId!}
          onClose={() => {
            setShowQuickActionModal(false);
            setEditingQuickAction(null);
          }}
          onCreate={(actionData) => {
            if (!selectedTemplateId) return;
            createQuickActionMutation.mutate({
              layoutId: selectedTemplateId,
              actionData
            });
          }}
          onUpdate={(updates) => {
            if (!selectedTemplateId || !editingQuickAction) return;
            updateQuickActionMutation.mutate({
              layoutId: selectedTemplateId,
              actionId: editingQuickAction.id,
              updates
            });
          }}
          isCreating={createQuickActionMutation.isPending}
          isUpdating={updateQuickActionMutation.isPending}
        />
      )}

      {/* Related List Modal */}
      {showRelatedListModal && (
        <RelatedListModal
          list={editingRelatedList}
          layoutId={selectedTemplateId!}
          onClose={() => {
            setShowRelatedListModal(false);
            setEditingRelatedList(null);
          }}
          onCreate={(listData) => {
            if (!selectedTemplateId) return;
            createRelatedListMutation.mutate({
              layoutId: selectedTemplateId,
              listData
            });
          }}
          onUpdate={(updates) => {
            if (!selectedTemplateId || !editingRelatedList) return;
            updateRelatedListMutation.mutate({
              layoutId: selectedTemplateId,
              listId: editingRelatedList.id,
              updates
            });
          }}
          isCreating={createRelatedListMutation.isPending}
          isUpdating={updateRelatedListMutation.isPending}
        />
      )}

      {/* Phase 8: Assignment Modal */}
      {showAssignmentModal && selectedTemplateId && (
        <AssignmentModal
          layoutId={selectedTemplateId}
          onClose={() => setShowAssignmentModal(false)}
          onCreate={(assignmentData) => {
            createAssignmentMutation.mutate({
              layoutId: selectedTemplateId,
              assignmentData
            });
          }}
          isCreating={createAssignmentMutation.isPending}
        />
      )}

      {/* Phase 8: Layout Preview Modal */}
      {showLayoutPreviewModal && layoutPreviewData && (
        <LayoutPreviewModal
          previewData={layoutPreviewData}
          onClose={() => {
            setShowLayoutPreviewModal(false);
            setLayoutPreviewData(null);
          }}
        />
      )}

      {/* Phase 8: Validation Modal */}
      {showValidationModal && validationResult && (
        <ValidationModal
          result={validationResult}
          onClose={() => {
            setShowValidationModal(false);
            setValidationResult(null);
          }}
        />
      )}

      {/* Phase 8: Health Check Modal */}
      {showHealthModal && healthCheck && (
        <HealthCheckModal
          healthCheck={healthCheck}
          onClose={() => {
            setShowHealthModal(false);
            setHealthCheck(null);
          }}
        />
      )}

      {/* Phase 8: Clone Modal */}
      {showCloneModal && selectedTemplateId && (
        <CloneModal
          sourceLayoutId={selectedTemplateId}
          onClose={() => setShowCloneModal(false)}
          onClone={(cloneRequest) => {
            cloneLayoutMutation.mutate(cloneRequest);
          }}
          isCloning={cloneLayoutMutation.isPending}
        />
      )}
    </div>
  );
};

// Create Template Modal Component
const CreateTemplateModal: React.FC<{
  application: Application;
  onClose: () => void;
  onCreate: (data: any) => void;
  isCreating: boolean;
}> = ({ application, onClose, onCreate, isCreating }) => {
  const { warning } = useToast();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isPublic, setIsPublic] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      warning('Template name required', 'Please enter a template name');
      return;
    }
    onCreate({ name, description, isPublic });
  };

  return (
    <div
      className="modal-overlay"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
      }}
      onClick={onClose}
    >
      <div
        className="modal-content bg-white dark:bg-gray-800 rounded-lg shadow-xl"
        style={{ maxWidth: '500px', width: '90%' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header" style={{
          padding: '1.5rem',
          borderBottom: '1px solid var(--border-color)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <h2 style={{ fontSize: '1.25rem', fontWeight: 600, margin: 0 }}>
            Create Page Layout Template
          </h2>
          <button
            className="modal-close-button text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            style={{ fontSize: '1.5rem', lineHeight: 1, border: 'none', background: 'none', cursor: 'pointer' }}
            onClick={onClose}
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body" style={{ padding: '1.5rem' }}>
            <div className="form-group" style={{ marginBottom: '1rem' }}>
              <label className="form-label" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
                Template Name *
              </label>
              <input
                type="text"
                className="form-input w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., Custom Client Layout"
                required
              />
            </div>

            <div className="form-group" style={{ marginBottom: '1rem' }}>
              <label className="form-label" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
                Description
              </label>
              <textarea
                className="form-textarea w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                rows={3}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe this layout template..."
              />
            </div>

            <div className="form-group" style={{ marginBottom: '1rem' }}>
              <label className="form-label" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
                Application
              </label>
              <input
                type="text"
                className="form-input w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                value={application}
                disabled
                style={{ cursor: 'not-allowed' }}
              />
              <p className="form-help-text text-sm text-gray-500 dark:text-gray-400" style={{ marginTop: '0.25rem' }}>
                Template will be created for {application} application
              </p>
            </div>

            <div className="form-group">
              <label className="form-checkbox flex items-center" style={{ cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={isPublic}
                  onChange={(e) => setIsPublic(e.target.checked)}
                  style={{ marginRight: '0.5rem' }}
                />
                <span>Make this template public (visible to all users)</span>
              </label>
            </div>
          </div>

          <div className="modal-footer" style={{
            padding: '1.5rem',
            borderTop: '1px solid var(--border-color)',
            display: 'flex',
            gap: '0.5rem',
            justifyContent: 'flex-end'
          }}>
            <button
              type="button"
              className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600"
              onClick={onClose}
              disabled={isCreating}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              disabled={isCreating}
            >
              {isCreating ? 'Creating...' : 'Create Template'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Section Modal Component
const SectionModal: React.FC<{
  section: PageLayoutSection | null;
  availableTabs: DynamicTab[];
  onClose: () => void;
  onSave: (data: any) => void;
  isSaving: boolean;
}> = ({ section, availableTabs, onClose, onSave, isSaving }) => {
  const { warning } = useToast();
  const [name, setName] = useState(section?.name || '');
  const [description, setDescription] = useState(section?.description || '');
  const [page, setPage] = useState(section?.page || '');
  const [tab, setTab] = useState(section?.tab || '');
  const [columns, setColumns] = useState<1 | 2 | 3 | 4>(section?.columns || 2);
  const [collapsible, setCollapsible] = useState(section?.collapsible || false);
  const [defaultCollapsed, setDefaultCollapsed] = useState(section?.defaultCollapsed || false);
  const [visible, setVisible] = useState(section?.visible !== undefined ? section.visible : true);

  // Get unique pages from available tabs
  const availablePages = Array.from(new Set(availableTabs.map(t => t.page)));

  // Get tabs for selected page
  const tabsForPage = availableTabs.filter(t => t.page === page);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      warning('Section name required', 'Please enter a section name');
      return;
    }
    if (!page) {
      warning('Page required', 'Please select a page');
      return;
    }
    if (!tab) {
      warning('Tab required', 'Please select a tab');
      return;
    }

    onSave({
      name: name.trim(),
      description: description.trim(),
      page,
      tab,
      columns,
      collapsible,
      defaultCollapsed: collapsible ? defaultCollapsed : false,
      visible,
      order: section?.order || 0
    });
  };

  return (
    <div
      className="modal-overlay"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
      }}
      onClick={onClose}
    >
      <div
        className="modal-content bg-white dark:bg-gray-800 rounded-lg shadow-xl"
        style={{ maxWidth: '600px', width: '90%', maxHeight: '90vh', overflowY: 'auto' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header" style={{
          padding: '1.5rem',
          borderBottom: '1px solid var(--border-color)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <h2 style={{ fontSize: '1.25rem', fontWeight: 600, margin: 0 }}>
            {section ? 'Edit Section' : 'Add New Section'}
          </h2>
          <button
            className="modal-close-button text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            style={{ fontSize: '1.5rem', lineHeight: 1, border: 'none', background: 'none', cursor: 'pointer' }}
            onClick={onClose}
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body" style={{ padding: '1.5rem' }}>
            {/* Section Name */}
            <div className="form-group" style={{ marginBottom: '1rem' }}>
              <label className="form-label" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
                Section Name *
              </label>
              <input
                type="text"
                className="form-input w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., Contact Information"
                required
              />
            </div>

            {/* Description */}
            <div className="form-group" style={{ marginBottom: '1rem' }}>
              <label className="form-label" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
                Description
              </label>
              <textarea
                className="form-textarea w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                rows={2}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe this section..."
              />
            </div>

            {/* Page Selection */}
            <div className="form-group" style={{ marginBottom: '1rem' }}>
              <label className="form-label" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
                Page *
              </label>
              <select
                className="form-select w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                value={page}
                onChange={(e) => {
                  setPage(e.target.value);
                  setTab(''); // Reset tab when page changes
                }}
                required
              >
                <option value="">Select Page...</option>
                {availablePages.map(p => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
            </div>

            {/* Tab Selection */}
            <div className="form-group" style={{ marginBottom: '1rem' }}>
              <label className="form-label" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
                Tab *
              </label>
              <select
                className="form-select w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                value={tab}
                onChange={(e) => setTab(e.target.value)}
                disabled={!page}
                required
              >
                <option value="">Select Tab...</option>
                {tabsForPage.map(t => (
                  <option key={t.id} value={t.name}>{t.name}</option>
                ))}
              </select>
              {!page && (
                <p className="form-help-text text-sm text-gray-500 dark:text-gray-400" style={{ marginTop: '0.25rem' }}>
                  Select a page first
                </p>
              )}
            </div>

            {/* Columns */}
            <div className="form-group" style={{ marginBottom: '1rem' }}>
              <label className="form-label" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
                Columns *
              </label>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                {[1, 2, 3, 4].map(col => (
                  <button
                    key={col}
                    type="button"
                    className={`px-4 py-2 rounded-lg border-2 ${
                      columns === col
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900 text-blue-700 dark:text-blue-200'
                        : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                    }`}
                    onClick={() => setColumns(col as 1 | 2 | 3 | 4)}
                  >
                    {col}
                  </button>
                ))}
              </div>
              <p className="form-help-text text-sm text-gray-500 dark:text-gray-400" style={{ marginTop: '0.25rem' }}>
                Number of columns for field layout
              </p>
            </div>

            {/* Collapsible */}
            <div className="form-group" style={{ marginBottom: '1rem' }}>
              <label className="form-checkbox flex items-center" style={{ cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={collapsible}
                  onChange={(e) => setCollapsible(e.target.checked)}
                  style={{ marginRight: '0.5rem' }}
                />
                <span>Allow section to be collapsed by users</span>
              </label>
            </div>

            {/* Default Collapsed (only if collapsible) */}
            {collapsible && (
              <div className="form-group" style={{ marginBottom: '1rem', marginLeft: '1.5rem' }}>
                <label className="form-checkbox flex items-center" style={{ cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={defaultCollapsed}
                    onChange={(e) => setDefaultCollapsed(e.target.checked)}
                    style={{ marginRight: '0.5rem' }}
                  />
                  <span>Collapsed by default</span>
                </label>
              </div>
            )}

            {/* Visible */}
            <div className="form-group">
              <label className="form-checkbox flex items-center" style={{ cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={visible}
                  onChange={(e) => setVisible(e.target.checked)}
                  style={{ marginRight: '0.5rem' }}
                />
                <span>Section is visible</span>
              </label>
              <p className="form-help-text text-sm text-gray-500 dark:text-gray-400" style={{ marginTop: '0.25rem', marginLeft: '1.5rem' }}>
                Uncheck to hide this section from all users
              </p>
            </div>
          </div>

          <div className="modal-footer" style={{
            padding: '1.5rem',
            borderTop: '1px solid var(--border-color)',
            display: 'flex',
            gap: '0.5rem',
            justifyContent: 'flex-end'
          }}>
            <button
              type="button"
              className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600"
              onClick={onClose}
              disabled={isSaving}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              disabled={isSaving}
            >
              {isSaving ? 'Saving...' : section ? 'Update Section' : 'Add Section'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Layout Section Modal Component (New API)
const LayoutSectionModal: React.FC<{
  section: LayoutSection | null;
  onClose: () => void;
  onSave: (data: {
    name: string;
    label: string;
    order: number;
    columns: 1 | 2;
    collapsible: boolean;
    collapsed: boolean;
  }) => void;
  isSaving: boolean;
}> = ({ section, onClose, onSave, isSaving }) => {
  const { warning } = useToast();
  const [name, setName] = useState(section?.name || '');
  const [label, setLabel] = useState(section?.label || '');
  const [order, setOrder] = useState(section?.order || 1);
  const [columns, setColumns] = useState<1 | 2>(section?.columns || 2);
  const [collapsible, setCollapsible] = useState(section?.collapsible || false);
  const [collapsed, setCollapsed] = useState(section?.collapsed || false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      warning('Section name required', 'Please enter a section name');
      return;
    }
    if (!label.trim()) {
      warning('Section label required', 'Please enter a section label');
      return;
    }

    onSave({
      name: name.trim(),
      label: label.trim(),
      order,
      columns,
      collapsible,
      collapsed: collapsible ? collapsed : false
    });
  };

  return (
    <div
      className="modal-overlay"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
      }}
      onClick={onClose}
    >
      <div
        className="modal-content bg-white dark:bg-gray-800 rounded-lg shadow-xl"
        style={{ maxWidth: '500px', width: '90%' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header" style={{
          padding: '1.5rem',
          borderBottom: '1px solid var(--border-color)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <h2 style={{ fontSize: '1.25rem', fontWeight: 600, margin: 0 }}>
            {section ? 'Edit Layout Section' : 'Create Layout Section'}
          </h2>
          <button
            className="modal-close-button text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            style={{ fontSize: '1.5rem', lineHeight: 1, border: 'none', background: 'none', cursor: 'pointer' }}
            onClick={onClose}
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body" style={{ padding: '1.5rem' }}>
            {/* Name */}
            <div className="form-group" style={{ marginBottom: '1rem' }}>
              <label className="form-label" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
                Section Name * <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>(API name, e.g., basic_info)</span>
              </label>
              <input
                type="text"
                className="form-input w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., basic_info"
                required
              />
            </div>

            {/* Label */}
            <div className="form-group" style={{ marginBottom: '1rem' }}>
              <label className="form-label" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
                Section Label * <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>(Display name)</span>
              </label>
              <input
                type="text"
                className="form-input w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="e.g., Basic Information"
                required
              />
            </div>

            {/* Order */}
            <div className="form-group" style={{ marginBottom: '1rem' }}>
              <label className="form-label" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
                Display Order
              </label>
              <input
                type="number"
                className="form-input w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                value={order}
                onChange={(e) => setOrder(parseInt(e.target.value) || 1)}
                min="1"
              />
            </div>

            {/* Columns */}
            <div className="form-group" style={{ marginBottom: '1rem' }}>
              <label className="form-label" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
                Number of Columns
              </label>
              <select
                className="form-select w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                value={columns}
                onChange={(e) => setColumns(parseInt(e.target.value) as 1 | 2)}
              >
                <option value={1}>1 Column</option>
                <option value={2}>2 Columns</option>
              </select>
            </div>

            {/* Collapsible */}
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', gap: '0.5rem' }}>
                <input
                  type="checkbox"
                  checked={collapsible}
                  onChange={(e) => {
                    setCollapsible(e.target.checked);
                    if (!e.target.checked) {
                      setCollapsed(false);
                    }
                  }}
                />
                <span>Section is collapsible</span>
              </label>

              {/* Collapsed by default - nested under collapsible */}
              {collapsible && (
                <div style={{ marginTop: '0.5rem', marginLeft: '1.75rem', paddingLeft: '0.5rem', borderLeft: '2px solid var(--border-color)' }}>
                  <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', gap: '0.5rem' }}>
                    <input
                      type="checkbox"
                      checked={collapsed}
                      onChange={(e) => setCollapsed(e.target.checked)}
                    />
                    <span>Collapsed by default</span>
                  </label>
                </div>
              )}
            </div>
          </div>

          <div className="modal-footer" style={{
            padding: '1rem 1.5rem',
            borderTop: '1px solid var(--border-color)',
            display: 'flex',
            gap: '0.5rem',
            justifyContent: 'flex-end'
          }}>
            <button
              type="button"
              className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600"
              onClick={onClose}
              disabled={isSaving}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              disabled={isSaving}
            >
              {isSaving ? 'Saving...' : section ? 'Update Section' : 'Create Section'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Field Selector Modal Component
const FieldSelectorModal: React.FC<{
  availableFields: CustomField[];
  existingFieldIds: string[];
  onClose: () => void;
  onAssign: (selectedIds: string[]) => void;
}> = ({ availableFields, existingFieldIds, onClose, onAssign }) => {
  const { warning } = useToast();
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');

  // Filter fields that are not already assigned
  const assignableFields = availableFields.filter(f => !existingFieldIds.includes(f.id));

  // Filter by search query
  const filteredFields = assignableFields.filter(f =>
    f.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
    f.fieldKey.toLowerCase().includes(searchQuery.toLowerCase()) ||
    f.type.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const toggleField = (fieldId: string) => {
    setSelectedIds(prev =>
      prev.includes(fieldId)
        ? prev.filter(id => id !== fieldId)
        : [...prev, fieldId]
    );
  };

  const handleAssign = () => {
    if (selectedIds.length === 0) {
      warning('No fields selected', 'Please select at least one field');
      return;
    }
    onAssign(selectedIds);
  };

  return (
    <div
      className="modal-overlay"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
      }}
      onClick={onClose}
    >
      <div
        className="modal-content bg-white dark:bg-gray-800 rounded-lg shadow-xl"
        style={{ maxWidth: '600px', width: '90%', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header" style={{
          padding: '1.5rem',
          borderBottom: '1px solid var(--border-color)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <h2 style={{ fontSize: '1.25rem', fontWeight: 600, margin: 0 }}>
            Add Fields to Section
          </h2>
          <button
            className="modal-close-button text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            style={{ fontSize: '1.5rem', lineHeight: 1, border: 'none', background: 'none', cursor: 'pointer' }}
            onClick={onClose}
          >
            ✕
          </button>
        </div>

        {/* Search Bar */}
        <div style={{ padding: '1rem', borderBottom: '1px solid var(--border-color)' }}>
          <div style={{ marginBottom: '0.5rem' }}>
            <input
              type="text"
              className="form-input w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              placeholder="Search fields by name, key, or type..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
            Showing {filteredFields.length} of {assignableFields.length} field{assignableFields.length !== 1 ? 's' : ''}
          </div>
        </div>

        {/* Fields List */}
        <div style={{
          flex: 1,
          overflowY: 'auto',
          padding: '1rem',
          minHeight: '200px',
          maxHeight: '400px'
        }}>
          {filteredFields.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '2rem',
              color: 'var(--text-secondary)'
            }}>
              {assignableFields.length === 0 ? (
                <>
                  <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>✓</div>
                  <div>All available fields are already assigned to this section</div>
                </>
              ) : (
                <>
                  <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>🔍</div>
                  <div>No fields match your search</div>
                </>
              )}
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {filteredFields.map(field => (
                <label
                  key={field.id}
                  className="field-checkbox-item"
                  style={{
                    padding: '0.75rem',
                    border: '1px solid var(--border-color)',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    background: selectedIds.includes(field.id) ? '#EFF6FF' : 'var(--background-primary)',
                    transition: 'all 0.2s'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'start', gap: '0.75rem' }}>
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(field.id)}
                      onChange={() => toggleField(field.id)}
                      style={{ marginTop: '0.25rem' }}
                    />
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                        <span style={{ fontWeight: 600, fontSize: '0.875rem' }}>{field.label}</span>
                        {field.isRequired && (
                          <span style={{ color: '#DC2626', fontWeight: 600 }}>*</span>
                        )}
                        <span style={{
                          fontSize: '0.65rem',
                          padding: '0.1rem 0.3rem',
                          background: '#E0E7FF',
                          color: '#3730A3',
                          borderRadius: '3px',
                          fontWeight: 600
                        }}>
                          {field.type}
                        </span>
                      </div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                        Key: <span style={{ fontFamily: 'monospace', fontSize: '0.7rem' }}>{field.fieldKey}</span>
                      </div>
                      {field.helpText && (
                        <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '0.25rem' }}>
                          {field.helpText}
                        </div>
                      )}
                    </div>
                  </div>
                </label>
              ))}
            </div>
          )}
        </div>

        {/* Selection Summary */}
        {selectedIds.length > 0 && (
          <div style={{
            padding: '0.75rem 1rem',
            background: '#EFF6FF',
            borderTop: '1px solid var(--border-color)',
            fontSize: '0.875rem',
            color: '#1E40AF'
          }}>
            {selectedIds.length} field{selectedIds.length !== 1 ? 's' : ''} selected
          </div>
        )}

        <div className="modal-footer" style={{
          padding: '1rem 1.5rem',
          borderTop: '1px solid var(--border-color)',
          display: 'flex',
          gap: '0.5rem',
          justifyContent: 'flex-end'
        }}>
          <button
            type="button"
            className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="button"
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            onClick={handleAssign}
            disabled={selectedIds.length === 0}
            style={{ opacity: selectedIds.length === 0 ? 0.5 : 1, cursor: selectedIds.length === 0 ? 'not-allowed' : 'pointer' }}
          >
            Add {selectedIds.length > 0 ? `(${selectedIds.length})` : ''} Field{selectedIds.length !== 1 ? 's' : ''}
          </button>
        </div>
      </div>
    </div>
  );
};

// Field Config Modal Component
const FieldConfigModal: React.FC<{
  field: PageLayoutField;
  onClose: () => void;
  onSave: (config: Partial<PageLayoutField>) => void;
}> = ({ field, onClose, onSave }) => {
  const { warning } = useToast();
  const [label, setLabel] = useState(field.label);
  const [required, setRequired] = useState(field.required || false);
  const [width, setWidth] = useState<'full' | 'half' | 'third' | 'quarter'>(field.width || 'half');
  const [placeholder, setPlaceholder] = useState(field.placeholder || '');
  const [helpText, setHelpText] = useState(field.helpText || '');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!label.trim()) {
      warning('Field label required', 'Please enter a field label');
      return;
    }
    onSave({
      label: label.trim(),
      required,
      width,
      placeholder: placeholder.trim(),
      helpText: helpText.trim()
    });
  };

  return (
    <div
      className="modal-overlay"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
      }}
      onClick={onClose}
    >
      <div
        className="modal-content bg-white dark:bg-gray-800 rounded-lg shadow-xl"
        style={{ maxWidth: '500px', width: '90%' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header" style={{
          padding: '1.5rem',
          borderBottom: '1px solid var(--border-color)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <h2 style={{ fontSize: '1.25rem', fontWeight: 600, margin: 0 }}>
            Configure Field
          </h2>
          <button
            className="modal-close-button text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            style={{ fontSize: '1.5rem', lineHeight: 1, border: 'none', background: 'none', cursor: 'pointer' }}
            onClick={onClose}
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body" style={{ padding: '1.5rem' }}>
            {/* Field Info */}
            <div style={{
              padding: '0.75rem',
              background: 'var(--background-secondary)',
              border: '1px solid var(--border-color)',
              borderRadius: '6px',
              marginBottom: '1rem',
              fontSize: '0.875rem'
            }}>
              <div style={{ color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>Field Key:</div>
              <div style={{ fontFamily: 'monospace', fontWeight: 600 }}>{field.fieldKey}</div>
              <div style={{ color: 'var(--text-secondary)', marginTop: '0.5rem', marginBottom: '0.25rem' }}>Field Type:</div>
              <div style={{ fontWeight: 600 }}>{field.type}</div>
            </div>

            {/* Label */}
            <div className="form-group" style={{ marginBottom: '1rem' }}>
              <label className="form-label" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
                Display Label *
              </label>
              <input
                type="text"
                className="form-input w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="e.g., Client Name"
                required
              />
              <p className="form-help-text text-sm text-gray-500 dark:text-gray-400" style={{ marginTop: '0.25rem' }}>
                The label shown to users
              </p>
            </div>

            {/* Width */}
            <div className="form-group" style={{ marginBottom: '1rem' }}>
              <label className="form-label" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
                Field Width *
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.5rem' }}>
                {(['full', 'half', 'third', 'quarter'] as const).map(w => (
                  <button
                    key={w}
                    type="button"
                    className={`px-3 py-2 rounded-lg border-2 text-sm ${
                      width === w
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900 text-blue-700 dark:text-blue-200'
                        : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                    }`}
                    onClick={() => setWidth(w)}
                  >
                    {w === 'full' && '100% (Full)'}
                    {w === 'half' && '50% (Half)'}
                    {w === 'third' && '33% (Third)'}
                    {w === 'quarter' && '25% (Quarter)'}
                  </button>
                ))}
              </div>
            </div>

            {/* Required */}
            <div className="form-group" style={{ marginBottom: '1rem' }}>
              <label className="form-checkbox flex items-center" style={{ cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={required}
                  onChange={(e) => setRequired(e.target.checked)}
                  style={{ marginRight: '0.5rem' }}
                />
                <span>This field is required</span>
              </label>
            </div>

            {/* Placeholder */}
            <div className="form-group" style={{ marginBottom: '1rem' }}>
              <label className="form-label" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
                Placeholder Text
              </label>
              <input
                type="text"
                className="form-input w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                value={placeholder}
                onChange={(e) => setPlaceholder(e.target.value)}
                placeholder="e.g., Enter client name..."
              />
              <p className="form-help-text text-sm text-gray-500 dark:text-gray-400" style={{ marginTop: '0.25rem' }}>
                Hint text shown in empty field
              </p>
            </div>

            {/* Help Text */}
            <div className="form-group">
              <label className="form-label" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
                Help Text
              </label>
              <textarea
                className="form-textarea w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                rows={2}
                value={helpText}
                onChange={(e) => setHelpText(e.target.value)}
                placeholder="Additional guidance for this field..."
              />
              <p className="form-help-text text-sm text-gray-500 dark:text-gray-400" style={{ marginTop: '0.25rem' }}>
                Additional information shown below the field
              </p>
            </div>
          </div>

          <div className="modal-footer" style={{
            padding: '1.5rem',
            borderTop: '1px solid var(--border-color)',
            display: 'flex',
            gap: '0.5rem',
            justifyContent: 'flex-end'
          }}>
            <button
              type="button"
              className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Save Configuration
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// PII Configuration Modal Component
const PIIConfigModal: React.FC<{
  field: PageLayoutField;
  onClose: () => void;
  onSave: (config: {
    isPII: boolean;
    encryption?: 'standard' | 'high' | 'maximum';
    masking?: {
      enabled: boolean;
      pattern: string;
      unmaskRoles?: string[];
    };
    accessRoles?: string[];
  }) => void;
}> = ({ field, onClose, onSave }) => {
  const { warning } = useToast();
  const [isPII, setIsPII] = useState(field.pii || false);
  const [encryption, setEncryption] = useState<'standard' | 'high' | 'maximum' | ''>( field.encryption || '');
  const [maskingEnabled, setMaskingEnabled] = useState(field.masking?.enabled || false);
  const [maskingPattern, setMaskingPattern] = useState(field.masking?.pattern || '');
  const [unmaskRoles, setUnmaskRoles] = useState<string[]>(field.masking?.unmaskRoles || []);
  const [accessRoles, setAccessRoles] = useState<string[]>(field.accessRoles || []);

  // Available roles for selection
  const availableRoles = ['Admin', 'Manager', 'User', 'Support', 'Auditor'];

  const toggleUnmaskRole = (role: string) => {
    setUnmaskRoles(prev =>
      prev.includes(role) ? prev.filter(r => r !== role) : [...prev, role]
    );
  };

  const toggleAccessRole = (role: string) => {
    setAccessRoles(prev =>
      prev.includes(role) ? prev.filter(r => r !== role) : [...prev, role]
    );
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (isPII && maskingEnabled && !maskingPattern.trim()) {
      warning('Masking pattern required', 'Please provide a masking pattern when masking is enabled');
      return;
    }

    onSave({
      isPII,
      encryption: isPII && encryption ? (encryption as 'standard' | 'high' | 'maximum') : undefined,
      masking: isPII && maskingEnabled ? {
        enabled: true,
        pattern: maskingPattern.trim(),
        unmaskRoles: unmaskRoles.length > 0 ? unmaskRoles : undefined
      } : undefined,
      accessRoles: isPII && accessRoles.length > 0 ? accessRoles : undefined
    });
  };

  return (
    <div
      className="modal-overlay"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
      }}
      onClick={onClose}
    >
      <div
        className="modal-content bg-white dark:bg-gray-800 rounded-lg shadow-xl"
        style={{ maxWidth: '600px', width: '90%', maxHeight: '90vh', overflowY: 'auto' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header" style={{
          padding: '1.5rem',
          borderBottom: '1px solid var(--border-color)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <h2 style={{ fontSize: '1.25rem', fontWeight: 600, margin: 0 }}>
            Configure PII & Security Settings
          </h2>
          <button
            className="modal-close-button text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            style={{ fontSize: '1.5rem', lineHeight: 1, border: 'none', background: 'none', cursor: 'pointer' }}
            onClick={onClose}
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body" style={{ padding: '1.5rem' }}>
            {/* Field Info */}
            <div style={{
              padding: '0.75rem',
              background: 'var(--background-secondary)',
              border: '1px solid var(--border-color)',
              borderRadius: '6px',
              marginBottom: '1.5rem',
              fontSize: '0.875rem'
            }}>
              <div style={{ fontWeight: 600, marginBottom: '0.25rem' }}>{field.label}</div>
              <div style={{ color: 'var(--text-secondary)' }}>
                Key: <span style={{ fontFamily: 'monospace' }}>{field.fieldKey}</span> • Type: {field.type}
              </div>
            </div>

            {/* PII Toggle */}
            <div className="form-group" style={{ marginBottom: '1.5rem' }}>
              <label className="form-checkbox flex items-center" style={{ cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={isPII}
                  onChange={(e) => setIsPII(e.target.checked)}
                  style={{ marginRight: '0.5rem' }}
                />
                <span style={{ fontWeight: 600 }}>This field contains Personally Identifiable Information (PII)</span>
              </label>
              <p className="form-help-text text-sm text-gray-500 dark:text-gray-400" style={{ marginTop: '0.5rem', marginLeft: '1.5rem' }}>
                Enable this for fields containing sensitive personal data such as SSN, credit card numbers, etc.
              </p>
            </div>

            {isPII && (
              <>
                {/* Compliance Badges */}
                <div style={{
                  padding: '0.75rem',
                  background: '#EFF6FF',
                  border: '1px solid #DBEAFE',
                  borderRadius: '6px',
                  marginBottom: '1.5rem',
                  fontSize: '0.75rem'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                    <span style={{ color: '#1E40AF', fontWeight: 600 }}>Compliance:</span>
                    <span style={{
                      padding: '0.2rem 0.5rem',
                      background: '#DBEAFE',
                      color: '#1E40AF',
                      borderRadius: '4px',
                      fontWeight: 600
                    }}>
                      GDPR
                    </span>
                    <span style={{
                      padding: '0.2rem 0.5rem',
                      background: '#DBEAFE',
                      color: '#1E40AF',
                      borderRadius: '4px',
                      fontWeight: 600
                    }}>
                      HIPAA
                    </span>
                    <span style={{
                      padding: '0.2rem 0.5rem',
                      background: '#DBEAFE',
                      color: '#1E40AF',
                      borderRadius: '4px',
                      fontWeight: 600
                    }}>
                      SOC2
                    </span>
                  </div>
                </div>

                {/* Encryption Level */}
                <div className="form-group" style={{ marginBottom: '1.5rem' }}>
                  <label className="form-label" style={{ display: 'block', marginBottom: '0.75rem', fontWeight: 600 }}>
                    Encryption Level
                  </label>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    <label style={{
                      padding: '0.75rem',
                      border: encryption === 'standard' ? '2px solid #3B82F6' : '1px solid var(--border-color)',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      background: encryption === 'standard' ? '#EFF6FF' : 'transparent',
                      transition: 'all 0.2s'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'start', gap: '0.5rem' }}>
                        <input
                          type="radio"
                          name="encryption"
                          value="standard"
                          checked={encryption === 'standard'}
                          onChange={(e) => setEncryption(e.target.value as 'standard')}
                          style={{ marginTop: '0.25rem' }}
                        />
                        <div>
                          <div style={{ fontWeight: 600, fontSize: '0.875rem' }}>Standard (AES-128)</div>
                          <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '0.25rem' }}>
                            Basic encryption suitable for most PII fields
                          </div>
                        </div>
                      </div>
                    </label>

                    <label style={{
                      padding: '0.75rem',
                      border: encryption === 'high' ? '2px solid #3B82F6' : '1px solid var(--border-color)',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      background: encryption === 'high' ? '#EFF6FF' : 'transparent',
                      transition: 'all 0.2s'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'start', gap: '0.5rem' }}>
                        <input
                          type="radio"
                          name="encryption"
                          value="high"
                          checked={encryption === 'high'}
                          onChange={(e) => setEncryption(e.target.value as 'high')}
                          style={{ marginTop: '0.25rem' }}
                        />
                        <div>
                          <div style={{ fontWeight: 600, fontSize: '0.875rem' }}>High (AES-256)</div>
                          <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '0.25rem' }}>
                            Strong encryption for highly sensitive data
                          </div>
                        </div>
                      </div>
                    </label>

                    <label style={{
                      padding: '0.75rem',
                      border: encryption === 'maximum' ? '2px solid #3B82F6' : '1px solid var(--border-color)',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      background: encryption === 'maximum' ? '#EFF6FF' : 'transparent',
                      transition: 'all 0.2s'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'start', gap: '0.5rem' }}>
                        <input
                          type="radio"
                          name="encryption"
                          value="maximum"
                          checked={encryption === 'maximum'}
                          onChange={(e) => setEncryption(e.target.value as 'maximum')}
                          style={{ marginTop: '0.25rem' }}
                        />
                        <div>
                          <div style={{ fontWeight: 600, fontSize: '0.875rem' }}>Maximum (AES-256 + Field-Level Keys)</div>
                          <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '0.25rem' }}>
                            Maximum security with dedicated field-level encryption keys
                          </div>
                        </div>
                      </div>
                    </label>
                  </div>
                </div>

                {/* Data Masking */}
                <div className="form-group" style={{ marginBottom: '1.5rem' }}>
                  <label className="form-checkbox flex items-center" style={{ cursor: 'pointer', marginBottom: '0.75rem' }}>
                    <input
                      type="checkbox"
                      checked={maskingEnabled}
                      onChange={(e) => setMaskingEnabled(e.target.checked)}
                      style={{ marginRight: '0.5rem' }}
                    />
                    <span style={{ fontWeight: 600 }}>Enable Data Masking</span>
                  </label>

                  {maskingEnabled && (
                    <div style={{ marginLeft: '1.5rem' }}>
                      <div style={{ marginBottom: '1rem' }}>
                        <label className="form-label" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
                          Masking Pattern *
                        </label>
                        <input
                          type="text"
                          className="form-input w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                          value={maskingPattern}
                          onChange={(e) => setMaskingPattern(e.target.value)}
                          placeholder="e.g., ***-**-1234 or ****4567"
                          required={maskingEnabled}
                        />
                        <p className="form-help-text text-sm text-gray-500 dark:text-gray-400" style={{ marginTop: '0.25rem' }}>
                          Pattern to display masked value (e.g., ***-**-1234 for SSN)
                        </p>
                      </div>

                      <div>
                        <label className="form-label" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
                          Roles That Can Unmask
                        </label>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                          {availableRoles.map(role => (
                            <label
                              key={role}
                              style={{
                                padding: '0.5rem 0.75rem',
                                border: unmaskRoles.includes(role) ? '2px solid #3B82F6' : '1px solid var(--border-color)',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                background: unmaskRoles.includes(role) ? '#EFF6FF' : 'transparent',
                                fontSize: '0.875rem',
                                transition: 'all 0.2s',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem'
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={unmaskRoles.includes(role)}
                                onChange={() => toggleUnmaskRole(role)}
                                style={{ margin: 0 }}
                              />
                              <span>{role}</span>
                            </label>
                          ))}
                        </div>
                        <p className="form-help-text text-sm text-gray-500 dark:text-gray-400" style={{ marginTop: '0.5rem' }}>
                          Select roles that can view unmasked data
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Access Control */}
                <div className="form-group">
                  <label className="form-label" style={{ display: 'block', marginBottom: '0.75rem', fontWeight: 600 }}>
                    Role-Based Access Control
                  </label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                    {availableRoles.map(role => (
                      <label
                        key={role}
                        style={{
                          padding: '0.5rem 0.75rem',
                          border: accessRoles.includes(role) ? '2px solid #3B82F6' : '1px solid var(--border-color)',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          background: accessRoles.includes(role) ? '#EFF6FF' : 'transparent',
                          fontSize: '0.875rem',
                          transition: 'all 0.2s',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem'
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={accessRoles.includes(role)}
                          onChange={() => toggleAccessRole(role)}
                          style={{ margin: 0 }}
                        />
                        <span>{role}</span>
                      </label>
                    ))}
                  </div>
                  <p className="form-help-text text-sm text-gray-500 dark:text-gray-400" style={{ marginTop: '0.5rem' }}>
                    Select roles that can access this field. Leave empty to allow all roles.
                  </p>
                </div>
              </>
            )}
          </div>

          <div className="modal-footer" style={{
            padding: '1.5rem',
            borderTop: '1px solid var(--border-color)',
            display: 'flex',
            gap: '0.5rem',
            justifyContent: 'flex-end'
          }}>
            <button
              type="button"
              className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Save PII Configuration
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Helper function to format time ago
function formatTimeAgo(date: Date): string {
  const now = new Date();
  const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (seconds < 60) return 'Just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)} minutes ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)} hours ago`;
  if (seconds < 604800) return `${Math.floor(seconds / 86400)} days ago`;
  if (seconds < 2592000) return `${Math.floor(seconds / 604800)} weeks ago`;
  if (seconds < 31536000) return `${Math.floor(seconds / 2592000)} months ago`;
  return `${Math.floor(seconds / 31536000)} years ago`;
}

// Version History Modal Component
const VersionHistoryModal: React.FC<{
  isOpen: boolean;
  template: PageLayoutTemplate | null;
  versions: PageLayoutVersion[];
  isLoading: boolean;
  onClose: () => void;
  onRollback: (versionId: string) => void;
  onCompare: (versionId: string) => void;
  isRollingBack: boolean;
}> = ({ isOpen, template, versions, isLoading, onClose, onRollback, onCompare, isRollingBack }) => {
  if (!isOpen || !template) return null;

  // Sort versions by version number (newest first)
  const sortedVersions = [...versions].sort((a, b) => b.version - a.version);
  const currentVersion = template.version;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">📜 Version History</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
              {template.name} • Current Version: <strong>v{currentVersion}</strong>
            </p>
          </div>
          <button
            className="absolute top-6 right-6 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
            onClick={onClose}
          >
            ✕
          </button>
        </div>

        <div className="p-6 overflow-y-auto flex-1">
          {/* Info Banner */}
          <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md mb-4 text-sm text-blue-900 dark:text-blue-100">
            <strong>ℹ️ About Version History:</strong> Every save creates a new version.
            You can rollback to any previous version, and the current state will be saved as a new version.
          </div>

          {isLoading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <p className="mt-4 text-gray-500 dark:text-gray-400">
                Loading version history...
              </p>
            </div>
          ) : sortedVersions.length === 0 ? (
            <div className="text-center py-12 text-gray-500 dark:text-gray-400">
              <div className="text-5xl mb-4 opacity-30">📜</div>
              <p>No version history available yet.</p>
            </div>
          ) : (
            <div className="flex flex-col gap-4 relative pl-8">
              {/* Vertical line */}
              <div className="absolute left-2 top-0 bottom-0 w-0.5 bg-gray-300 dark:bg-gray-600" />

              {sortedVersions.map((version, index) => {
                const isCurrent = version.version === currentVersion;
                const date = new Date(version.createdAt);
                const timeAgo = formatTimeAgo(date);

                return (
                  <div
                    key={version.id}
                    className="relative"
                  >
                    {/* Timeline dot */}
                    <div className={`absolute left-[-1.5rem] top-2 w-4 h-4 rounded-full border-2 z-10 ${
                      isCurrent
                        ? 'bg-blue-600 border-blue-600'
                        : 'bg-gray-200 dark:bg-gray-700 border-gray-300 dark:border-gray-600'
                    }`} />

                    {/* Version card */}
                    <div className={`p-4 rounded-lg border-2 ${
                      isCurrent
                        ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-600 dark:border-blue-500'
                        : 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700'
                    }`}>
                      {/* Version header */}
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-semibold text-base text-gray-900 dark:text-gray-100">
                              Version {version.version}
                            </span>
                            {isCurrent && (
                              <span className="text-xs px-2 py-0.5 bg-blue-600 text-white rounded font-semibold">
                                CURRENT
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {timeAgo} • {date.toLocaleString()}
                          </div>
                          {version.createdBy && (
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              By: {version.createdBy}
                            </div>
                          )}
                        </div>

                        {!isCurrent && (
                          <div className="flex gap-2">
                            <button
                              className="px-3 py-1 text-xs bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded hover:bg-gray-300 dark:hover:bg-gray-600"
                              onClick={() => onCompare(version.id)}
                            >
                              👁️ Compare
                            </button>
                            <button
                              className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
                              onClick={() => onRollback(version.id)}
                              disabled={isRollingBack}
                            >
                              {isRollingBack ? '⏳' : '⏪ Rollback'}
                            </button>
                          </div>
                        )}
                      </div>

                      {/* Changes description */}
                      {version.changes && (
                        <div className="mt-3 p-2 bg-white dark:bg-gray-900 rounded text-sm text-gray-700 dark:text-gray-300">
                          <strong>Changes:</strong> {version.changes}
                        </div>
                      )}

                      {/* Section count */}
                      <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                        {version.sections?.length || 0} section{version.sections?.length !== 1 ? 's' : ''}
                        {' • '}
                        {version.sections?.reduce((sum, s) => sum + (s.fields?.length || 0), 0) || 0} field{version.sections?.reduce((sum, s) => sum + (s.fields?.length || 0), 0) !== 1 ? 's' : ''}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end">
          <button
            type="button"
            className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600"
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// Version Compare Modal Component
const VersionCompareModal: React.FC<{
  isOpen: boolean;
  currentTemplate: PageLayoutTemplate | null;
  compareVersion: PageLayoutVersion | null;
  onClose: () => void;
}> = ({ isOpen, currentTemplate, compareVersion, onClose }) => {
  if (!isOpen || !currentTemplate || !compareVersion) return null;

  const currentSections = currentTemplate.sections || [];
  const compareSections = compareVersion.sections || [];

  // Calculate differences
  const addedSections = currentSections.filter(
    cs => !compareSections.find(vs => vs.id === cs.id)
  );
  const removedSections = compareSections.filter(
    vs => !currentSections.find(cs => cs.id === vs.id)
  );
  const modifiedSections = currentSections.filter(cs => {
    const vs = compareSections.find(s => s.id === cs.id);
    if (!vs) return false;
    return JSON.stringify(cs) !== JSON.stringify(vs);
  });

  const currentFieldCount = currentSections.reduce((sum, s) => sum + (s.fields?.length || 0), 0);
  const compareFieldCount = compareSections.reduce((sum, s) => sum + (s.fields?.length || 0), 0);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">👁️ Compare Versions</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
              Current v{currentTemplate.version} vs Version {compareVersion.version}
            </p>
          </div>
          <button
            className="absolute top-6 right-6 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
            onClick={onClose}
          >
            ✕
          </button>
        </div>

        <div className="p-6 overflow-y-auto flex-1">
          {/* Summary */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            {/* Current version */}
            <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border-2 border-blue-600 dark:border-blue-500 rounded-lg">
              <h4 className="font-semibold mb-2 text-gray-900 dark:text-gray-100">Current Version {currentTemplate.version}</h4>
              <div className="text-sm text-gray-500 dark:text-gray-400 space-y-1">
                <div>{currentSections.length} sections</div>
                <div>{currentFieldCount} fields</div>
                <div>{new Date(currentTemplate.updatedAt).toLocaleDateString()}</div>
              </div>
            </div>

            {/* Compare version */}
            <div className="p-4 bg-gray-50 dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 rounded-lg">
              <h4 className="font-semibold mb-2 text-gray-900 dark:text-gray-100">Version {compareVersion.version}</h4>
              <div className="text-sm text-gray-500 dark:text-gray-400 space-y-1">
                <div>{compareSections.length} sections</div>
                <div>{compareFieldCount} fields</div>
                <div>{new Date(compareVersion.createdAt).toLocaleDateString()}</div>
              </div>
            </div>
          </div>

          {/* Changes Summary */}
          <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg mb-6">
            <h4 className="font-semibold mb-3 text-gray-900 dark:text-gray-100">📊 Changes Summary</h4>
            <div className="flex flex-col gap-2 text-sm">
              {addedSections.length > 0 && (
                <div className="text-green-600 dark:text-green-400">
                  + {addedSections.length} section{addedSections.length !== 1 ? 's' : ''} added
                </div>
              )}
              {removedSections.length > 0 && (
                <div className="text-red-600 dark:text-red-400">
                  - {removedSections.length} section{removedSections.length !== 1 ? 's' : ''} removed
                </div>
              )}
              {modifiedSections.length > 0 && (
                <div className="text-orange-600 dark:text-orange-400">
                  ✏️ {modifiedSections.length} section{modifiedSections.length !== 1 ? 's' : ''} modified
                </div>
              )}
              {addedSections.length === 0 && removedSections.length === 0 && modifiedSections.length === 0 && (
                <div className="text-gray-500 dark:text-gray-400">
                  No changes detected
                </div>
              )}
            </div>
          </div>

          {/* Detailed Changes */}
          <div>
            <h4 className="font-semibold mb-4 text-gray-900 dark:text-gray-100">Detailed Changes</h4>

            {/* Added Sections */}
            {addedSections.length > 0 && (
              <div className="mb-6">
                <h5 className="text-green-600 dark:text-green-400 mb-3 text-sm font-medium">
                  + Added Sections ({addedSections.length})
                </h5>
                <div className="flex flex-col gap-2">
                  {addedSections.map(section => (
                    <div
                      key={section.id}
                      className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-md text-sm"
                    >
                      <strong className="text-gray-900 dark:text-gray-100">{section.name}</strong>
                      <div className="text-gray-500 dark:text-gray-400 text-xs mt-1">
                        {section.fields?.length || 0} fields • {section.page} • {section.tab}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Removed Sections */}
            {removedSections.length > 0 && (
              <div className="mb-6">
                <h5 className="text-red-600 dark:text-red-400 mb-3 text-sm font-medium">
                  - Removed Sections ({removedSections.length})
                </h5>
                <div className="flex flex-col gap-2">
                  {removedSections.map(section => (
                    <div
                      key={section.id}
                      className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md text-sm"
                    >
                      <strong className="text-gray-900 dark:text-gray-100">{section.name}</strong>
                      <div className="text-gray-500 dark:text-gray-400 text-xs mt-1">
                        {section.fields?.length || 0} fields • {section.page} • {section.tab}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Modified Sections */}
            {modifiedSections.length > 0 && (
              <div>
                <h5 className="text-orange-600 dark:text-orange-400 mb-3 text-sm font-medium">
                  ✏️ Modified Sections ({modifiedSections.length})
                </h5>
                <div className="flex flex-col gap-2">
                  {modifiedSections.map(section => {
                    const oldSection = compareSections.find(s => s.id === section.id);
                    const fieldDiff = (section.fields?.length || 0) - (oldSection?.fields?.length || 0);

                    return (
                      <div
                        key={section.id}
                        className="p-3 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-md text-sm"
                      >
                        <strong className="text-gray-900 dark:text-gray-100">{section.name}</strong>
                        <div className="text-gray-500 dark:text-gray-400 text-xs mt-1">
                          {fieldDiff !== 0 && (
                            <span>
                              {fieldDiff > 0 ? `+${fieldDiff}` : fieldDiff} field{Math.abs(fieldDiff) !== 1 ? 's' : ''} •
                            </span>
                          )}
                          {section.page} • {section.tab}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end">
          <button
            type="button"
            className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600"
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// ===========================
// PREVIEW MODAL COMPONENT
// ===========================

const PreviewModal: React.FC<{
  isOpen: boolean;
  template: PageLayoutTemplate | null;
  mode: 'desktop' | 'tablet' | 'mobile';
  showSampleData: boolean;
  onClose: () => void;
  onChangeMode: (mode: 'desktop' | 'tablet' | 'mobile') => void;
  onToggleSampleData: () => void;
}> = ({ isOpen, template, mode, showSampleData, onClose, onChangeMode, onToggleSampleData }) => {
  if (!isOpen || !template) return null;

  // Sample data generator
  const generateSampleData = (field: PageLayoutField): string => {
    if (!showSampleData) return '-';

    switch (field.type) {
      case 'text':
        if (field.label === 'First Name') return 'John';
        if (field.label === 'Last Name') return 'Doe';
        if (field.label === 'Name') return 'John Doe';
        if (field.label === 'City') return 'New York';
        if (field.label === 'State') return 'NY';
        if (field.label === 'Address Line 1') return '123 Main Street';
        // Handle SSN fields (stored as text type)
        if (field.label.includes('SSN') || field.label.includes('Social Security')) {
          if (field.masking?.enabled) {
            return field.masking.pattern;
          }
          return '***-**-1234';
        }
        return 'Sample Text';
      case 'email':
        return 'john.doe@example.com';
      case 'phone':
        // Apply masking if enabled
        if (field.masking?.enabled) {
          return field.masking.pattern;
        }
        return '(555) 123-4567';
      case 'date':
        return new Date().toLocaleDateString();
      case 'datetime':
        return new Date().toLocaleString();
      case 'currency':
        return '$125,000';
      case 'number':
        if (field.label === 'Credit Score') return '720';
        if (field.label === 'ZIP Code') return '10001';
        return '12345';
      case 'select':
        return 'Option 1';
      case 'textarea':
        return 'Sample notes or description text that spans multiple lines...';
      default:
        return 'Sample Value';
    }
  };

  // Get preview width based on mode
  const getPreviewWidth = () => {
    switch (mode) {
      case 'desktop':
        return '100%';
      case 'tablet':
        return '768px';
      case 'mobile':
        return '375px';
      default:
        return '100%';
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full h-full flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
              👁️ Preview: {template.name}
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Application: {template.application} | Version: {template.version}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Toolbar */}
        <div className="px-6 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between bg-gray-50 dark:bg-gray-900">
          {/* Device selector */}
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Device:</span>
            <div className="flex gap-1">
              <button
                onClick={() => onChangeMode('desktop')}
                className={`px-3 py-1.5 text-sm rounded ${
                  mode === 'desktop'
                    ? 'bg-purple-600 text-white'
                    : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                }`}
              >
                🖥️ Desktop
              </button>
              <button
                onClick={() => onChangeMode('tablet')}
                className={`px-3 py-1.5 text-sm rounded ${
                  mode === 'tablet'
                    ? 'bg-purple-600 text-white'
                    : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                }`}
              >
                📱 Tablet
              </button>
              <button
                onClick={() => onChangeMode('mobile')}
                className={`px-3 py-1.5 text-sm rounded ${
                  mode === 'mobile'
                    ? 'bg-purple-600 text-white'
                    : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                }`}
              >
                📱 Mobile
              </button>
            </div>
          </div>

          {/* Sample data toggle */}
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={showSampleData}
                onChange={onToggleSampleData}
                className="w-4 h-4"
              />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Show Sample Data
              </span>
            </label>
          </div>
        </div>

        {/* Preview Area */}
        <div className="flex-1 overflow-auto bg-gray-100 dark:bg-gray-900 p-6">
          <div
            className="mx-auto bg-white dark:bg-gray-800 shadow-lg rounded-lg transition-all duration-300"
            style={{ width: getPreviewWidth() }}
          >
            <div className="p-6">
              {template.sections
                .filter((section) => section.visible)
                .sort((a, b) => a.order - b.order)
                .map((section) => (
                  <PreviewSection
                    key={section.id}
                    section={section}
                    generateSampleData={generateSampleData}
                  />
                ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-between items-center bg-gray-50 dark:bg-gray-900">
          <div className="text-sm text-gray-500 dark:text-gray-400">
            {template.sections.length} sections • {template.sections.reduce((acc, s) => acc + s.fields.length, 0)} fields
          </div>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded hover:bg-gray-300 dark:hover:bg-gray-600"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// Preview Section Component
const PreviewSection: React.FC<{
  section: PageLayoutSection;
  generateSampleData: (field: PageLayoutField) => string;
}> = ({ section, generateSampleData }) => {
  const [isCollapsed, setIsCollapsed] = useState(false);

  return (
    <div className="mb-6 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
      {/* Section Header */}
      <div
        className={`px-4 py-3 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between ${
          section.collapsible ? 'cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800' : ''
        }`}
        onClick={() => section.collapsible && setIsCollapsed(!isCollapsed)}
      >
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{section.name}</h3>
        {section.collapsible && (
          <button className="text-gray-500 dark:text-gray-400">
            {isCollapsed ? <ChevronDown className="w-5 h-5" /> : <ChevronUp className="w-5 h-5" />}
          </button>
        )}
      </div>

      {/* Section Content */}
      {!isCollapsed && (
        <div className="p-4">
          <div
            className="grid gap-4"
            style={{
              gridTemplateColumns: `repeat(${section.columns}, 1fr)`,
            }}
          >
            {section.fields.map((field) => (
              <PreviewField
                key={field.fieldId}
                field={field}
                value={generateSampleData(field)}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Preview Field Component
const PreviewField: React.FC<{
  field: PageLayoutField;
  value: string;
}> = ({ field, value }) => {
  // Convert width to percentage
  const getWidthPercentage = (width?: 'full' | 'half' | 'third' | 'quarter'): string => {
    switch (width) {
      case 'full': return '100%';
      case 'half': return '50%';
      case 'third': return '33.333%';
      case 'quarter': return '25%';
      default: return '100%';
    }
  };

  return (
    <div className="mb-2" style={{ width: getWidthPercentage(field.width) }}>
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
        {field.label}
        {field.required && <span className="text-red-500 ml-1">*</span>}
        {field.pii && (
          <span className="ml-2 text-xs bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded">
            PII
          </span>
        )}
        {field.encryption && (
          <span className="ml-2 text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded">
            Encrypted
          </span>
        )}
      </label>
      <div className="px-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-md text-gray-900 dark:text-gray-100">
        {value}
      </div>
    </div>
  );
};

// Rule Builder Modal Component
const RuleBuilderModal: React.FC<{
  rule: VisibilityRule | null;
  layoutId: string;
  sections: LayoutSection[];
  fields: CustomField[];
  onClose: () => void;
  onSave: (ruleData: any) => void;
}> = ({ rule, layoutId, sections, fields, onClose, onSave }) => {
  const { warning } = useToast();
  const [ruleName, setRuleName] = useState(rule?.name || '');
  const [ruleDescription, setRuleDescription] = useState(rule?.description || '');
  const [ruleAction, setRuleAction] = useState<RuleAction>(rule?.action || 'hide');
  const [ruleTarget, setRuleTarget] = useState<RuleTarget>(rule?.target || 'field');
  const [targetId, setTargetId] = useState(rule?.targetId || '');
  const [logic, setLogic] = useState<RuleLogic>(rule?.logic || 'AND');
  const [conditions, setConditions] = useState<RuleCondition[]>(rule?.conditions || []);
  const [priority, setPriority] = useState(rule?.priority || 1);

  const operators: RuleConditionOperator[] = [
    'equals', 'not_equals', 'contains', 'not_contains',
    'starts_with', 'ends_with', 'greater_than', 'less_than',
    'is_empty', 'is_not_empty'
  ];

  const addCondition = () => {
    const newCondition: RuleCondition = {
      id: `cond_${Date.now()}`,
      fieldName: '',
      fieldLabel: '',
      operator: 'equals',
      value: ''
    };
    setConditions([...conditions, newCondition]);
  };

  const removeCondition = (conditionId: string) => {
    setConditions(conditions.filter(c => c.id !== conditionId));
  };

  const updateCondition = (conditionId: string, updates: Partial<RuleCondition>) => {
    setConditions(conditions.map(c =>
      c.id === conditionId ? { ...c, ...updates } : c
    ));
  };

  const handleSubmit = () => {
    if (!ruleName || !targetId || conditions.length === 0) {
      warning('Required fields missing', 'Please fill in all required fields');
      return;
    }

    const targetName = ruleTarget === 'section'
      ? sections.find(s => s.id === targetId)?.label || ''
      : fields.find(f => f.fieldKey === targetId)?.label || '';

    const ruleData = {
      name: ruleName,
      description: ruleDescription,
      enabled: true,
      priority,
      logic,
      conditions,
      action: ruleAction,
      target: ruleTarget,
      targetId,
      targetName
    };

    onSave(ruleData);
  };

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      background: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        background: 'white',
        borderRadius: '8px',
        width: '90%',
        maxWidth: '800px',
        maxHeight: '90vh',
        overflow: 'auto',
        padding: '1.5rem'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <h3 style={{ fontSize: '1.25rem', fontWeight: 600, margin: 0 }}>
            {rule ? 'Edit Visibility Rule' : 'Create Visibility Rule'}
          </h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer' }}>
            ×
          </button>
        </div>

        {/* Basic Info */}
        <div style={{ marginBottom: '1.5rem' }}>
          <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem' }}>
            Rule Name <span style={{ color: 'red' }}>*</span>
          </label>
          <input
            type="text"
            value={ruleName}
            onChange={(e) => setRuleName(e.target.value)}
            placeholder="Enter rule name..."
            style={{
              width: '100%',
              padding: '0.5rem',
              border: '1px solid #ccc',
              borderRadius: '4px'
            }}
          />
        </div>

        <div style={{ marginBottom: '1.5rem' }}>
          <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem' }}>
            Description
          </label>
          <textarea
            value={ruleDescription}
            onChange={(e) => setRuleDescription(e.target.value)}
            placeholder="Optional description..."
            rows={2}
            style={{
              width: '100%',
              padding: '0.5rem',
              border: '1px solid #ccc',
              borderRadius: '4px'
            }}
          />
        </div>

        {/* Priority */}
        <div style={{ marginBottom: '1.5rem' }}>
          <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem' }}>
            Priority (lower = higher priority)
          </label>
          <input
            type="number"
            value={priority}
            onChange={(e) => setPriority(parseInt(e.target.value) || 1)}
            min="1"
            style={{
              width: '100px',
              padding: '0.5rem',
              border: '1px solid #ccc',
              borderRadius: '4px'
            }}
          />
        </div>

        {/* Action & Target */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
          <div>
            <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem' }}>
              Action <span style={{ color: 'red' }}>*</span>
            </label>
            <select
              value={ruleAction}
              onChange={(e) => setRuleAction(e.target.value as RuleAction)}
              style={{
                width: '100%',
                padding: '0.5rem',
                border: '1px solid #ccc',
                borderRadius: '4px'
              }}
            >
              <option value="show">Show</option>
              <option value="hide">Hide</option>
              <option value="require">Require</option>
              <option value="readonly">Read-only</option>
            </select>
          </div>

          <div>
            <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem' }}>
              Target Type <span style={{ color: 'red' }}>*</span>
            </label>
            <select
              value={ruleTarget}
              onChange={(e) => {
                setRuleTarget(e.target.value as RuleTarget);
                setTargetId('');
              }}
              style={{
                width: '100%',
                padding: '0.5rem',
                border: '1px solid #ccc',
                borderRadius: '4px'
              }}
            >
              <option value="field">Field</option>
              <option value="section">Section</option>
            </select>
          </div>
        </div>

        {/* Target Selection */}
        <div style={{ marginBottom: '1.5rem' }}>
          <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem' }}>
            Target {ruleTarget === 'section' ? 'Section' : 'Field'} <span style={{ color: 'red' }}>*</span>
          </label>
          <select
            value={targetId}
            onChange={(e) => setTargetId(e.target.value)}
            style={{
              width: '100%',
              padding: '0.5rem',
              border: '1px solid #ccc',
              borderRadius: '4px'
            }}
          >
            <option value="">-- Select {ruleTarget} --</option>
            {ruleTarget === 'section'
              ? sections.map(s => (
                  <option key={s.id} value={s.id}>{s.label}</option>
                ))
              : fields.map(f => (
                  <option key={f.fieldKey} value={f.fieldKey}>{f.label}</option>
                ))}
          </select>
        </div>

        {/* Condition Logic */}
        <div style={{ marginBottom: '1rem' }}>
          <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem' }}>
            Condition Logic
          </label>
          <div style={{ display: 'flex', gap: '1rem' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
              <input
                type="radio"
                value="AND"
                checked={logic === 'AND'}
                onChange={(e) => setLogic(e.target.value as RuleLogic)}
              />
              AND (all conditions must match)
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
              <input
                type="radio"
                value="OR"
                checked={logic === 'OR'}
                onChange={(e) => setLogic(e.target.value as RuleLogic)}
              />
              OR (any condition can match)
            </label>
          </div>
        </div>

        {/* Conditions */}
        <div style={{ marginBottom: '1.5rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
            <label style={{ fontWeight: 600 }}>
              Conditions <span style={{ color: 'red' }}>*</span>
            </label>
            <button
              onClick={addCondition}
              style={{
                padding: '0.5rem 1rem',
                background: '#3B82F6',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              + Add Condition
            </button>
          </div>

          {conditions.length === 0 && (
            <div style={{
              padding: '1rem',
              background: '#F3F4F6',
              borderRadius: '4px',
              textAlign: 'center',
              color: '#6B7280'
            }}>
              No conditions yet. Click "Add Condition" to create one.
            </div>
          )}

          {conditions.map((condition, index) => (
            <div key={condition.id} style={{
              padding: '1rem',
              background: '#F9FAFB',
              border: '1px solid #E5E7EB',
              borderRadius: '4px',
              marginBottom: '0.75rem'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                <span style={{ fontSize: '0.875rem', fontWeight: 600, color: '#6B7280' }}>
                  Condition {index + 1}
                </span>
                <button
                  onClick={() => removeCondition(condition.id)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#EF4444',
                    cursor: 'pointer',
                    fontSize: '0.875rem'
                  }}
                >
                  Remove
                </button>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 2fr', gap: '0.5rem' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.75rem', marginBottom: '0.25rem' }}>
                    Field
                  </label>
                  <select
                    value={condition.fieldName}
                    onChange={(e) => {
                      const field = fields.find(f => f.fieldKey === e.target.value);
                      updateCondition(condition.id, {
                        fieldName: e.target.value,
                        fieldLabel: field?.label || e.target.value
                      });
                    }}
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      border: '1px solid #D1D5DB',
                      borderRadius: '4px',
                      fontSize: '0.875rem'
                    }}
                  >
                    <option value="">-- Select field --</option>
                    {fields.map(f => (
                      <option key={f.fieldKey} value={f.fieldKey}>{f.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '0.75rem', marginBottom: '0.25rem' }}>
                    Operator
                  </label>
                  <select
                    value={condition.operator}
                    onChange={(e) => updateCondition(condition.id, { operator: e.target.value as RuleConditionOperator })}
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      border: '1px solid #D1D5DB',
                      borderRadius: '4px',
                      fontSize: '0.875rem'
                    }}
                  >
                    {operators.map(op => (
                      <option key={op} value={op}>
                        {op.replace(/_/g, ' ')}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '0.75rem', marginBottom: '0.25rem' }}>
                    Value
                  </label>
                  <input
                    type="text"
                    value={condition.value || ''}
                    onChange={(e) => updateCondition(condition.id, { value: e.target.value })}
                    disabled={condition.operator === 'is_empty' || condition.operator === 'is_not_empty'}
                    placeholder={condition.operator === 'is_empty' || condition.operator === 'is_not_empty' ? 'N/A' : 'Enter value...'}
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      border: '1px solid #D1D5DB',
                      borderRadius: '4px',
                      fontSize: '0.875rem',
                      background: (condition.operator === 'is_empty' || condition.operator === 'is_not_empty') ? '#F3F4F6' : 'white'
                    }}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', paddingTop: '1rem', borderTop: '1px solid #E5E7EB' }}>
          <button
            onClick={onClose}
            style={{
              padding: '0.5rem 1rem',
              background: 'white',
              border: '1px solid #D1D5DB',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            style={{
              padding: '0.5rem 1rem',
              background: '#10B981',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            {rule ? 'Update Rule' : 'Create Rule'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Rule Preview Modal Component
const RulePreviewModal: React.FC<{
  isOpen: boolean;
  layoutId: string;
  rules: VisibilityRule[];
  fields: CustomField[];
  onClose: () => void;
}> = ({ isOpen, layoutId, rules, fields, onClose }) => {
  const { error: showError } = useToast();
  const [fieldValues, setFieldValues] = useState<Record<string, any>>({});
  const [evaluationResults, setEvaluationResults] = useState<RuleEvaluationResult[]>([]);
  const [isEvaluating, setIsEvaluating] = useState(false);

  const handleEvaluate = async () => {
    setIsEvaluating(true);
    try {
      const response = await evaluateVisibilityRules(layoutId, fieldValues);
      setEvaluationResults(response.data);
    } catch (error) {
      console.error('Failed to evaluate rules:', error);
      showError('Failed to evaluate rules', 'Please try again');
    } finally {
      setIsEvaluating(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      background: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        background: 'white',
        borderRadius: '8px',
        width: '90%',
        maxWidth: '900px',
        maxHeight: '90vh',
        overflow: 'auto',
        padding: '1.5rem'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <h3 style={{ fontSize: '1.25rem', fontWeight: 600, margin: 0 }}>
            🔍 Test Visibility Rules
          </h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer' }}>
            ×
          </button>
        </div>

        <div style={{
          padding: '1rem',
          background: '#EFF6FF',
          borderRadius: '6px',
          marginBottom: '1.5rem',
          border: '1px solid #DBEAFE'
        }}>
          <p style={{ margin: 0, fontSize: '0.875rem', color: '#1E40AF' }}>
            Enter field values to test how visibility rules will be applied. Rules are evaluated in priority order.
          </p>
        </div>

        {/* Field Inputs */}
        <div style={{ marginBottom: '1.5rem' }}>
          <h4 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '0.75rem' }}>
            Field Values
          </h4>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '1rem' }}>
            {fields.slice(0, 10).map((field) => (
              <div key={field.fieldKey}>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.25rem' }}>
                  {field.label}
                </label>
                <input
                  type={field.type === 'number' ? 'number' : field.type === 'date' ? 'date' : 'text'}
                  value={fieldValues[field.fieldKey] || ''}
                  onChange={(e) => setFieldValues({
                    ...fieldValues,
                    [field.fieldKey]: e.target.value
                  })}
                  placeholder={`Enter ${field.label.toLowerCase()}...`}
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    border: '1px solid #D1D5DB',
                    borderRadius: '4px',
                    fontSize: '0.875rem'
                  }}
                />
              </div>
            ))}
          </div>
        </div>

        {/* Evaluate Button */}
        <div style={{ marginBottom: '1.5rem', textAlign: 'center' }}>
          <button
            onClick={handleEvaluate}
            disabled={isEvaluating}
            style={{
              padding: '0.75rem 2rem',
              background: isEvaluating ? '#9CA3AF' : '#8B5CF6',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: isEvaluating ? 'not-allowed' : 'pointer',
              fontSize: '1rem',
              fontWeight: 600
            }}
          >
            {isEvaluating ? 'Evaluating...' : '🔍 Evaluate Rules'}
          </button>
        </div>

        {/* Results */}
        {evaluationResults.length > 0 && (
          <div>
            <h4 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '0.75rem' }}>
              Evaluation Results
            </h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {evaluationResults.map((result) => (
                <div
                  key={result.ruleId}
                  style={{
                    padding: '1rem',
                    background: result.matched ? '#D1FAE5' : '#FEE2E2',
                    border: `1px solid ${result.matched ? '#6EE7B7' : '#FCA5A5'}`,
                    borderRadius: '6px'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                    <span style={{ fontSize: '1.25rem' }}>
                      {result.matched ? '✅' : '❌'}
                    </span>
                    <span style={{ fontWeight: 600 }}>
                      {result.ruleName}
                    </span>
                  </div>
                  <div style={{ fontSize: '0.875rem', color: '#374151' }}>
                    {result.matched ? (
                      <span>
                        <strong>Action:</strong> {result.action.toUpperCase()} the {result.target} "{result.targetId}"
                      </span>
                    ) : (
                      <span>Rule conditions not met - no action taken</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Close Button */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: '1rem', marginTop: '1.5rem', borderTop: '1px solid #E5E7EB' }}>
          <button
            onClick={onClose}
            style={{
              padding: '0.5rem 1rem',
              background: 'white',
              border: '1px solid #D1D5DB',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================
// QUICK ACTION CARD COMPONENT
// ============================================

const QuickActionCard: React.FC<{
  action: QuickAction;
  layoutId: string;
  onEdit: () => void;
  onDelete: () => void;
}> = ({ action, onEdit, onDelete }) => {
  const getActionTypeColor = (type: ActionType) => {
    const colors = {
      standard: { bg: 'rgba(33, 150, 243, 0.15)', color: '#2196F3' },
      custom: { bg: 'rgba(156, 39, 176, 0.15)', color: '#9C27B0' },
      link: { bg: 'rgba(255, 152, 0, 0.15)', color: '#FF9800' },
      javascript: { bg: 'rgba(244, 67, 54, 0.15)', color: '#F44336' }
    };
    return colors[type];
  };

  const colors = getActionTypeColor(action.type);

  return (
    <div style={{
      border: `1px solid ${action.enabled ? 'var(--border-color)' : 'var(--border-color)'}`,
      borderRadius: '8px',
      background: action.enabled ? 'var(--background-secondary)' : 'rgba(158, 158, 158, 0.05)',
      overflow: 'hidden',
      opacity: action.enabled ? 1 : 0.6,
      transition: 'all 0.2s',
      position: 'relative'
    }}>
      <div style={{
        padding: '1.5rem',
        textAlign: 'center',
        background: 'var(--background-primary)',
        borderBottom: '1px solid var(--border-color)'
      }}>
        <div style={{
          fontSize: '2rem',
          marginBottom: '0.5rem',
          opacity: action.enabled ? 1 : 0.4
        }}>
          {action.icon || '⚡'}
        </div>
        <button
          className="px-3 py-1.5 bg-blue-600 text-white rounded text-sm font-medium"
          style={{
            pointerEvents: 'none',
            opacity: action.enabled ? 1 : 0.5
          }}
        >
          {action.label}
        </button>
      </div>

      <div style={{ padding: '1rem' }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '0.5rem'
        }}>
          <span style={{
            padding: '0.25rem 0.5rem',
            background: colors.bg,
            color: colors.color,
            borderRadius: '4px',
            fontSize: '0.65rem',
            fontWeight: 600,
            textTransform: 'uppercase'
          }}>
            {action.type}
          </span>
          <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
            Order: {action.order}
          </span>
        </div>

        {action.description && (
          <p style={{
            margin: '0.5rem 0',
            fontSize: '0.75rem',
            color: 'var(--text-secondary)',
            lineHeight: 1.4
          }}>
            {action.description}
          </p>
        )}

        {action.visibleForRoles && action.visibleForRoles.length > 0 && (
          <div style={{
            marginTop: '0.5rem',
            fontSize: '0.7rem',
            color: 'var(--text-secondary)'
          }}>
            👥 {action.visibleForRoles.length} role(s)
          </div>
        )}

        <div style={{
          display: 'flex',
          gap: '0.5rem',
          marginTop: '1rem',
          paddingTop: '0.75rem',
          borderTop: '1px solid var(--border-color)'
        }}>
          <button
            className="flex-1 px-3 py-1.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded hover:bg-gray-200 dark:hover:bg-gray-600 text-sm flex items-center justify-center gap-1"
            onClick={onEdit}
          >
            ✏️ Edit
          </button>
          <button
            className="px-3 py-1.5 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded hover:bg-red-100 dark:hover:bg-red-900/40 text-sm"
            onClick={onDelete}
            title="Delete action"
          >
            🗑️
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================
// RELATED LIST CARD COMPONENT
// ============================================

const RelatedListCard: React.FC<{
  list: RelatedList;
  listIndex: number;
  layoutId: string;
  onEdit: () => void;
  onDelete: () => void;
}> = ({ list, listIndex, onEdit, onDelete }) => {
  return (
    <div style={{
      border: `1px solid ${list.enabled ? 'var(--border-color)' : 'var(--border-color)'}`,
      borderRadius: '8px',
      background: list.enabled ? 'var(--background-secondary)' : 'rgba(158, 158, 158, 0.05)',
      overflow: 'hidden',
      opacity: list.enabled ? 1 : 0.6
    }}>
      <div style={{
        padding: '1rem',
        background: 'var(--background-primary)',
        borderBottom: '1px solid var(--border-color)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <span style={{
            fontSize: '0.875rem',
            color: 'var(--text-secondary)',
            fontWeight: 600
          }}>
            {listIndex + 1}
          </span>
          <div>
            <h4 style={{ margin: 0, marginBottom: '0.25rem', color: 'var(--text-primary)' }}>
              {list.label}
            </h4>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
              {list.relatedObject} • {list.columns.length} column{list.columns.length !== 1 ? 's' : ''}
              {list.sortField && ` • Sorted by ${list.sortField}`}
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button
            className="px-3 py-1.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded hover:bg-gray-200 dark:hover:bg-gray-600 text-sm"
            onClick={onEdit}
            title="Edit related list"
          >
            ✏️
          </button>
          <button
            className="px-3 py-1.5 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded hover:bg-red-100 dark:hover:bg-red-900/40 text-sm"
            onClick={onDelete}
            title="Delete related list"
          >
            🗑️
          </button>
        </div>
      </div>

      <div style={{ padding: '1rem' }}>
        <div style={{ marginBottom: '1rem' }}>
          <div style={{
            fontSize: '0.75rem',
            color: 'var(--text-secondary)',
            fontWeight: 600,
            textTransform: 'uppercase',
            marginBottom: '0.5rem'
          }}>
            Columns ({list.columns.length})
          </div>
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '0.5rem'
          }}>
            {list.columns.slice(0, 6).map((col, idx) => (
              <span
                key={idx}
                style={{
                  padding: '0.25rem 0.75rem',
                  background: 'var(--background-primary)',
                  border: '1px solid var(--border-color)',
                  borderRadius: '4px',
                  fontSize: '0.75rem',
                  color: 'var(--text-primary)'
                }}
              >
                {col}
              </span>
            ))}
            {list.columns.length > 6 && (
              <span style={{
                padding: '0.25rem 0.75rem',
                color: 'var(--text-secondary)',
                fontSize: '0.75rem'
              }}>
                +{list.columns.length - 6} more
              </span>
            )}
          </div>
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: '0.75rem',
          padding: '0.75rem',
          background: 'var(--background-primary)',
          borderRadius: '6px',
          fontSize: '0.75rem'
        }}>
          <div>
            <span style={{ color: 'var(--text-secondary)' }}>Row Limit:</span>
            <span style={{ marginLeft: '0.5rem', fontWeight: 600, color: 'var(--text-primary)' }}>
              {list.rowLimit}
            </span>
          </div>
          <div>
            <span style={{ color: 'var(--text-secondary)' }}>New Button:</span>
            <span style={{ marginLeft: '0.5rem', fontWeight: 600, color: 'var(--text-primary)' }}>
              {list.showNewButton ? '✓ Yes' : '✕ No'}
            </span>
          </div>
          <div>
            <span style={{ color: 'var(--text-secondary)' }}>View All:</span>
            <span style={{ marginLeft: '0.5rem', fontWeight: 600, color: 'var(--text-primary)' }}>
              {list.showViewAllLink ? '✓ Yes' : '✕ No'}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

// Quick Action Modal Component
const QuickActionModal: React.FC<{
  action: QuickAction | null;
  layoutId: string;
  onClose: () => void;
  onCreate: (actionData: any) => void;
  onUpdate: (updates: any) => void;
  isCreating: boolean;
  isUpdating: boolean;
}> = ({ action, layoutId, onClose, onCreate, onUpdate, isCreating, isUpdating }) => {
  const isEditMode = action !== null;

  const [label, setLabel] = useState(action?.label || '');
  const [type, setType] = useState<ActionType>(action?.type || 'standard');
  const [icon, setIcon] = useState<ActionIcon>(action?.icon || '➕');
  const [description, setDescription] = useState(action?.description || '');
  const [url, setUrl] = useState(action?.url || '');
  const [script, setScript] = useState(action?.script || '');
  const [confirmationMessage, setConfirmationMessage] = useState(action?.confirmationMessage || '');
  const [successMessage, setSuccessMessage] = useState(action?.successMessage || '');
  const [order, setOrder] = useState(action?.order || 1);
  const [enabled, setEnabled] = useState(action?.enabled !== false);
  const [visibleForRoles, setVisibleForRoles] = useState<string[]>(
    action?.visibleForRoles || ['system_admin', 'cs_supervisor', 'cs_analyst', 'cm_supervisor', 'cm_analyst', 'auditor', 'viewer']
  );

  const availableIcons: ActionIcon[] = [
    '➕', '✏️', '📧', '📞', '📅', '💬', '📄', '🔗',
    '✓', '✕', '🗑️', '⚙️', '🔍', '📤', '📥'
  ];

  const roles = [
    { id: 'system_admin', label: 'System Admin' },
    { id: 'cs_supervisor', label: 'CS Supervisor' },
    { id: 'cs_analyst', label: 'CS Analyst' },
    { id: 'cm_supervisor', label: 'CM Supervisor' },
    { id: 'cm_analyst', label: 'CM Analyst' },
    { id: 'auditor', label: 'Auditor' },
    { id: 'viewer', label: 'Viewer' }
  ];

  const handleRoleToggle = (roleId: string) => {
    setVisibleForRoles(prev =>
      prev.includes(roleId)
        ? prev.filter(id => id !== roleId)
        : [...prev, roleId]
    );
  };

  const handleSave = () => {
    const actionData = {
      label,
      type,
      icon,
      description,
      url: type === 'link' ? url : undefined,
      script: type === 'javascript' ? script : undefined,
      confirmationMessage,
      successMessage,
      order,
      enabled,
      visibleForRoles
    };

    if (isEditMode) {
      onUpdate(actionData);
    } else {
      onCreate(actionData);
    }
  };

  const isValid = label.trim() !== '' &&
    (type !== 'link' || url.trim() !== '') &&
    (type !== 'javascript' || script.trim() !== '');

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      background: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999,
      padding: '1rem'
    }}>
      <div style={{
        background: 'var(--background-primary)',
        borderRadius: '8px',
        maxWidth: '700px',
        width: '100%',
        maxHeight: '90vh',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
      }}>
        {/* Header */}
        <div style={{
          padding: '1.5rem',
          borderBottom: '1px solid var(--border-color)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <h2 style={{ fontSize: '1.25rem', fontWeight: 600, color: 'var(--text-primary)' }}>
            {isEditMode ? 'Edit Quick Action' : 'Create Quick Action'}
          </h2>
          <button
            onClick={onClose}
            style={{
              padding: '0.5rem',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              color: 'var(--text-secondary)',
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '1.5rem', overflowY: 'auto', flex: 1 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
            {/* Action Label */}
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-primary)' }}>
                Action Label *
              </label>
              <input
                type="text"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="e.g., Send Email, Create Task, etc."
                style={{
                  width: '100%',
                  padding: '0.625rem',
                  border: '1px solid var(--border-color)',
                  borderRadius: '6px',
                  fontSize: '0.875rem',
                  background: 'var(--background-primary)',
                  color: 'var(--text-primary)'
                }}
              />
            </div>

            {/* Action Type and Icon */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-primary)' }}>
                  Action Type *
                </label>
                <select
                  value={type}
                  onChange={(e) => setType(e.target.value as ActionType)}
                  style={{
                    width: '100%',
                    padding: '0.625rem',
                    border: '1px solid var(--border-color)',
                    borderRadius: '6px',
                    fontSize: '0.875rem',
                    background: 'var(--background-primary)',
                    color: 'var(--text-primary)'
                  }}
                >
                  <option value="standard">Standard Action</option>
                  <option value="custom">Custom Action</option>
                  <option value="link">Link/URL</option>
                  <option value="javascript">JavaScript</option>
                </select>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-primary)' }}>
                  Icon
                </label>
                <select
                  value={icon}
                  onChange={(e) => setIcon(e.target.value as ActionIcon)}
                  style={{
                    width: '100%',
                    padding: '0.625rem',
                    border: '1px solid var(--border-color)',
                    borderRadius: '6px',
                    fontSize: '0.875rem',
                    background: 'var(--background-primary)',
                    color: 'var(--text-primary)'
                  }}
                >
                  {availableIcons.map(iconName => (
                    <option key={iconName} value={iconName}>{iconName}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Description */}
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-primary)' }}>
                Description
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Brief description of what this action does..."
                rows={2}
                style={{
                  width: '100%',
                  padding: '0.625rem',
                  border: '1px solid var(--border-color)',
                  borderRadius: '6px',
                  fontSize: '0.875rem',
                  background: 'var(--background-primary)',
                  color: 'var(--text-primary)',
                  resize: 'vertical'
                }}
              />
            </div>

            {/* Type-specific fields */}
            {type === 'link' && (
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-primary)' }}>
                  Link URL *
                </label>
                <input
                  type="text"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="https://example.com"
                  style={{
                    width: '100%',
                    padding: '0.625rem',
                    border: '1px solid var(--border-color)',
                    borderRadius: '6px',
                    fontSize: '0.875rem',
                    background: 'var(--background-primary)',
                    color: 'var(--text-primary)'
                  }}
                />
              </div>
            )}

            {type === 'javascript' && (
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-primary)' }}>
                  JavaScript Code *
                </label>
                <textarea
                  value={script}
                  onChange={(e) => setScript(e.target.value)}
                  placeholder="// Your JavaScript code here"
                  rows={4}
                  style={{
                    width: '100%',
                    padding: '0.625rem',
                    border: '1px solid var(--border-color)',
                    borderRadius: '6px',
                    fontSize: '0.875rem',
                    background: 'var(--background-primary)',
                    color: 'var(--text-primary)',
                    fontFamily: 'monospace',
                    resize: 'vertical'
                  }}
                />
              </div>
            )}

            {/* Messages */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-primary)' }}>
                  Confirmation Message
                </label>
                <input
                  type="text"
                  value={confirmationMessage}
                  onChange={(e) => setConfirmationMessage(e.target.value)}
                  placeholder="Are you sure?"
                  style={{
                    width: '100%',
                    padding: '0.625rem',
                    border: '1px solid var(--border-color)',
                    borderRadius: '6px',
                    fontSize: '0.875rem',
                    background: 'var(--background-primary)',
                    color: 'var(--text-primary)'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-primary)' }}>
                  Success Message
                </label>
                <input
                  type="text"
                  value={successMessage}
                  onChange={(e) => setSuccessMessage(e.target.value)}
                  placeholder="Action completed!"
                  style={{
                    width: '100%',
                    padding: '0.625rem',
                    border: '1px solid var(--border-color)',
                    borderRadius: '6px',
                    fontSize: '0.875rem',
                    background: 'var(--background-primary)',
                    color: 'var(--text-primary)'
                  }}
                />
              </div>
            </div>

            {/* Display Order and Enabled */}
            <div style={{ display: 'grid', gridTemplateColumns: '150px 1fr', gap: '1rem', alignItems: 'center' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-primary)' }}>
                  Display Order
                </label>
                <input
                  type="number"
                  value={order}
                  onChange={(e) => setOrder(Number(e.target.value))}
                  min={1}
                  style={{
                    width: '100%',
                    padding: '0.625rem',
                    border: '1px solid var(--border-color)',
                    borderRadius: '6px',
                    fontSize: '0.875rem',
                    background: 'var(--background-primary)',
                    color: 'var(--text-primary)'
                  }}
                />
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', paddingTop: '1.75rem' }}>
                <input
                  type="checkbox"
                  id="action-enabled"
                  checked={enabled}
                  onChange={(e) => setEnabled(e.target.checked)}
                  style={{ cursor: 'pointer' }}
                />
                <label htmlFor="action-enabled" style={{ fontSize: '0.875rem', color: 'var(--text-primary)', cursor: 'pointer' }}>
                  Enabled
                </label>
              </div>
            </div>

            {/* Role Visibility */}
            <div>
              <label style={{ display: 'block', marginBottom: '0.75rem', fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-primary)' }}>
                Role Visibility
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.75rem' }}>
                {roles.map(role => (
                  <div key={role.id} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <input
                      type="checkbox"
                      id={`role-${role.id}`}
                      checked={visibleForRoles.includes(role.id)}
                      onChange={() => handleRoleToggle(role.id)}
                      style={{ cursor: 'pointer' }}
                    />
                    <label htmlFor={`role-${role.id}`} style={{ fontSize: '0.875rem', color: 'var(--text-primary)', cursor: 'pointer' }}>
                      {role.label}
                    </label>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div style={{
          padding: '1rem 1.5rem',
          borderTop: '1px solid var(--border-color)',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '0.75rem'
        }}>
          <button
            onClick={onClose}
            disabled={isCreating || isUpdating}
            style={{
              padding: '0.625rem 1.25rem',
              background: 'transparent',
              border: '1px solid var(--border-color)',
              borderRadius: '6px',
              fontSize: '0.875rem',
              fontWeight: 500,
              color: 'var(--text-primary)',
              cursor: 'pointer',
              opacity: isCreating || isUpdating ? 0.5 : 1
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!isValid || isCreating || isUpdating}
            style={{
              padding: '0.625rem 1.25rem',
              background: isValid && !isCreating && !isUpdating ? 'var(--primary-color)' : 'var(--border-color)',
              border: 'none',
              borderRadius: '6px',
              fontSize: '0.875rem',
              fontWeight: 500,
              color: 'white',
              cursor: isValid && !isCreating && !isUpdating ? 'pointer' : 'not-allowed',
              opacity: isCreating || isUpdating ? 0.7 : 1
            }}
          >
            {isCreating || isUpdating ? 'Saving...' : isEditMode ? 'Update Action' : 'Create Action'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Related List Modal Component
const RelatedListModal: React.FC<{
  list: RelatedList | null;
  layoutId: string;
  onClose: () => void;
  onCreate: (listData: any) => void;
  onUpdate: (updates: any) => void;
  isCreating: boolean;
  isUpdating: boolean;
}> = ({ list, layoutId, onClose, onCreate, onUpdate, isCreating, isUpdating }) => {
  const isEditMode = list !== null;

  const [label, setLabel] = useState(list?.label || '');
  const [relatedObject, setRelatedObject] = useState(list?.relatedObject || '');
  const [relationshipField, setRelationshipField] = useState(list?.relationshipField || '');
  const [columns, setColumns] = useState<string[]>(list?.columns || []);
  const [newColumn, setNewColumn] = useState('');
  const [sortField, setSortField] = useState(list?.sortField || '');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>(list?.sortDirection || 'asc');
  const [rowLimit, setRowLimit] = useState(list?.rowLimit || 10);
  const [showNewButton, setShowNewButton] = useState(list?.showNewButton !== false);
  const [showViewAllLink, setShowViewAllLink] = useState(list?.showViewAllLink !== false);
  const [order, setOrder] = useState(list?.order || 1);
  const [enabled, setEnabled] = useState(list?.enabled !== false);

  const handleAddColumn = () => {
    if (newColumn.trim() && !columns.includes(newColumn.trim())) {
      setColumns([...columns, newColumn.trim()]);
      setNewColumn('');
    }
  };

  const handleRemoveColumn = (columnToRemove: string) => {
    setColumns(columns.filter(col => col !== columnToRemove));
  };

  const handleMoveColumnUp = (index: number) => {
    if (index > 0) {
      const newColumns = [...columns];
      [newColumns[index - 1], newColumns[index]] = [newColumns[index], newColumns[index - 1]];
      setColumns(newColumns);
    }
  };

  const handleMoveColumnDown = (index: number) => {
    if (index < columns.length - 1) {
      const newColumns = [...columns];
      [newColumns[index], newColumns[index + 1]] = [newColumns[index + 1], newColumns[index]];
      setColumns(newColumns);
    }
  };

  const handleSave = () => {
    const listData = {
      label,
      relatedObject,
      relationshipField,
      columns,
      sortField,
      sortDirection,
      rowLimit,
      showNewButton,
      showViewAllLink,
      order,
      enabled
    };

    if (isEditMode) {
      onUpdate(listData);
    } else {
      onCreate(listData);
    }
  };

  const isValid = label.trim() !== '' &&
    relatedObject.trim() !== '' &&
    relationshipField.trim() !== '' &&
    columns.length > 0;

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      background: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999,
      padding: '1rem'
    }}>
      <div style={{
        background: 'var(--background-primary)',
        borderRadius: '8px',
        maxWidth: '700px',
        width: '100%',
        maxHeight: '90vh',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
      }}>
        {/* Header */}
        <div style={{
          padding: '1.5rem',
          borderBottom: '1px solid var(--border-color)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <h2 style={{ fontSize: '1.25rem', fontWeight: 600, color: 'var(--text-primary)' }}>
            {isEditMode ? 'Edit Related List' : 'Create Related List'}
          </h2>
          <button
            onClick={onClose}
            style={{
              padding: '0.5rem',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              color: 'var(--text-secondary)',
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '1.5rem', overflowY: 'auto', flex: 1 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
            {/* List Label */}
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-primary)' }}>
                List Label *
              </label>
              <input
                type="text"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="e.g., Cases, Contacts, Opportunities"
                style={{
                  width: '100%',
                  padding: '0.625rem',
                  border: '1px solid var(--border-color)',
                  borderRadius: '6px',
                  fontSize: '0.875rem',
                  background: 'var(--background-primary)',
                  color: 'var(--text-primary)'
                }}
              />
            </div>

            {/* Related Object and Relationship Field */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-primary)' }}>
                  Related Object *
                </label>
                <input
                  type="text"
                  value={relatedObject}
                  onChange={(e) => setRelatedObject(e.target.value)}
                  placeholder="e.g., Case, Contact"
                  style={{
                    width: '100%',
                    padding: '0.625rem',
                    border: '1px solid var(--border-color)',
                    borderRadius: '6px',
                    fontSize: '0.875rem',
                    background: 'var(--background-primary)',
                    color: 'var(--text-primary)'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-primary)' }}>
                  Relationship Field *
                </label>
                <input
                  type="text"
                  value={relationshipField}
                  onChange={(e) => setRelationshipField(e.target.value)}
                  placeholder="e.g., clientId__c"
                  style={{
                    width: '100%',
                    padding: '0.625rem',
                    border: '1px solid var(--border-color)',
                    borderRadius: '6px',
                    fontSize: '0.875rem',
                    background: 'var(--background-primary)',
                    color: 'var(--text-primary)'
                  }}
                />
              </div>
            </div>

            {/* Columns Configuration */}
            <div>
              <label style={{ display: 'block', marginBottom: '0.75rem', fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-primary)' }}>
                Columns * ({columns.length})
              </label>

              {/* Add Column Input */}
              <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem' }}>
                <input
                  type="text"
                  value={newColumn}
                  onChange={(e) => setNewColumn(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddColumn()}
                  placeholder="Enter column name (e.g., caseNumber__c)"
                  style={{
                    flex: 1,
                    padding: '0.625rem',
                    border: '1px solid var(--border-color)',
                    borderRadius: '6px',
                    fontSize: '0.875rem',
                    background: 'var(--background-primary)',
                    color: 'var(--text-primary)'
                  }}
                />
                <button
                  onClick={handleAddColumn}
                  style={{
                    padding: '0.625rem 1rem',
                    background: 'var(--primary-color)',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    color: 'white',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.25rem'
                  }}
                >
                  <Plus size={16} />
                  Add
                </button>
              </div>

              {/* Column List */}
              {columns.length > 0 ? (
                <div style={{
                  border: '1px solid var(--border-color)',
                  borderRadius: '6px',
                  maxHeight: '200px',
                  overflowY: 'auto'
                }}>
                  {columns.map((column, index) => (
                    <div
                      key={index}
                      style={{
                        padding: '0.75rem',
                        borderBottom: index < columns.length - 1 ? '1px solid var(--border-color)' : 'none',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: '0.5rem'
                      }}
                    >
                      <span style={{ fontSize: '0.875rem', color: 'var(--text-primary)', flex: 1 }}>
                        {column}
                      </span>
                      <div style={{ display: 'flex', gap: '0.25rem' }}>
                        <button
                          onClick={() => handleMoveColumnUp(index)}
                          disabled={index === 0}
                          style={{
                            padding: '0.25rem',
                            background: 'transparent',
                            border: '1px solid var(--border-color)',
                            borderRadius: '4px',
                            cursor: index === 0 ? 'not-allowed' : 'pointer',
                            opacity: index === 0 ? 0.5 : 1,
                            display: 'flex',
                            alignItems: 'center'
                          }}
                          title="Move up"
                        >
                          <ChevronUp size={16} />
                        </button>
                        <button
                          onClick={() => handleMoveColumnDown(index)}
                          disabled={index === columns.length - 1}
                          style={{
                            padding: '0.25rem',
                            background: 'transparent',
                            border: '1px solid var(--border-color)',
                            borderRadius: '4px',
                            cursor: index === columns.length - 1 ? 'not-allowed' : 'pointer',
                            opacity: index === columns.length - 1 ? 0.5 : 1,
                            display: 'flex',
                            alignItems: 'center'
                          }}
                          title="Move down"
                        >
                          <ChevronDown size={16} />
                        </button>
                        <button
                          onClick={() => handleRemoveColumn(column)}
                          style={{
                            padding: '0.25rem',
                            background: 'transparent',
                            border: '1px solid rgba(244, 67, 54, 0.5)',
                            borderRadius: '4px',
                            color: '#F44336',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center'
                          }}
                          title="Remove column"
                        >
                          <X size={16} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{
                  padding: '1rem',
                  border: '1px dashed var(--border-color)',
                  borderRadius: '6px',
                  textAlign: 'center',
                  color: 'var(--text-secondary)',
                  fontSize: '0.875rem'
                }}>
                  No columns added yet. Add at least one column.
                </div>
              )}
            </div>

            {/* Sort Configuration */}
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '1rem' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-primary)' }}>
                  Sort Field
                </label>
                <input
                  type="text"
                  value={sortField}
                  onChange={(e) => setSortField(e.target.value)}
                  placeholder="e.g., createdDate__c"
                  style={{
                    width: '100%',
                    padding: '0.625rem',
                    border: '1px solid var(--border-color)',
                    borderRadius: '6px',
                    fontSize: '0.875rem',
                    background: 'var(--background-primary)',
                    color: 'var(--text-primary)'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-primary)' }}>
                  Direction
                </label>
                <select
                  value={sortDirection}
                  onChange={(e) => setSortDirection(e.target.value as 'asc' | 'desc')}
                  style={{
                    width: '100%',
                    padding: '0.625rem',
                    border: '1px solid var(--border-color)',
                    borderRadius: '6px',
                    fontSize: '0.875rem',
                    background: 'var(--background-primary)',
                    color: 'var(--text-primary)'
                  }}
                >
                  <option value="asc">Ascending</option>
                  <option value="desc">Descending</option>
                </select>
              </div>
            </div>

            {/* Display Settings */}
            <div>
              <label style={{ display: 'block', marginBottom: '0.75rem', fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-primary)' }}>
                Display Settings
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                    Row Limit
                  </label>
                  <input
                    type="number"
                    value={rowLimit}
                    onChange={(e) => setRowLimit(Number(e.target.value))}
                    min={1}
                    max={100}
                    style={{
                      width: '100%',
                      padding: '0.625rem',
                      border: '1px solid var(--border-color)',
                      borderRadius: '6px',
                      fontSize: '0.875rem',
                      background: 'var(--background-primary)',
                      color: 'var(--text-primary)'
                    }}
                  />
                </div>

                <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', paddingBottom: '0.625rem' }}>
                    <input
                      type="checkbox"
                      id="show-new-button"
                      checked={showNewButton}
                      onChange={(e) => setShowNewButton(e.target.checked)}
                      style={{ cursor: 'pointer' }}
                    />
                    <label htmlFor="show-new-button" style={{ fontSize: '0.875rem', color: 'var(--text-primary)', cursor: 'pointer' }}>
                      Show New Button
                    </label>
                  </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', paddingBottom: '0.625rem' }}>
                    <input
                      type="checkbox"
                      id="show-view-all"
                      checked={showViewAllLink}
                      onChange={(e) => setShowViewAllLink(e.target.checked)}
                      style={{ cursor: 'pointer' }}
                    />
                    <label htmlFor="show-view-all" style={{ fontSize: '0.875rem', color: 'var(--text-primary)', cursor: 'pointer' }}>
                      Show View All
                    </label>
                  </div>
                </div>
              </div>
            </div>

            {/* Display Order and Enabled */}
            <div style={{ display: 'grid', gridTemplateColumns: '150px 1fr', gap: '1rem', alignItems: 'center' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-primary)' }}>
                  Display Order
                </label>
                <input
                  type="number"
                  value={order}
                  onChange={(e) => setOrder(Number(e.target.value))}
                  min={1}
                  style={{
                    width: '100%',
                    padding: '0.625rem',
                    border: '1px solid var(--border-color)',
                    borderRadius: '6px',
                    fontSize: '0.875rem',
                    background: 'var(--background-primary)',
                    color: 'var(--text-primary)'
                  }}
                />
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', paddingTop: '1.75rem' }}>
                <input
                  type="checkbox"
                  id="list-enabled"
                  checked={enabled}
                  onChange={(e) => setEnabled(e.target.checked)}
                  style={{ cursor: 'pointer' }}
                />
                <label htmlFor="list-enabled" style={{ fontSize: '0.875rem', color: 'var(--text-primary)', cursor: 'pointer' }}>
                  Enabled
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div style={{
          padding: '1rem 1.5rem',
          borderTop: '1px solid var(--border-color)',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '0.75rem'
        }}>
          <button
            onClick={onClose}
            disabled={isCreating || isUpdating}
            style={{
              padding: '0.625rem 1.25rem',
              background: 'transparent',
              border: '1px solid var(--border-color)',
              borderRadius: '6px',
              fontSize: '0.875rem',
              fontWeight: 500,
              color: 'var(--text-primary)',
              cursor: 'pointer',
              opacity: isCreating || isUpdating ? 0.5 : 1
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!isValid || isCreating || isUpdating}
            style={{
              padding: '0.625rem 1.25rem',
              background: isValid && !isCreating && !isUpdating ? 'var(--primary-color)' : 'var(--border-color)',
              border: 'none',
              borderRadius: '6px',
              fontSize: '0.875rem',
              fontWeight: 500,
              color: 'white',
              cursor: isValid && !isCreating && !isUpdating ? 'pointer' : 'not-allowed',
              opacity: isCreating || isUpdating ? 0.7 : 1
            }}
          >
            {isCreating || isUpdating ? 'Saving...' : isEditMode ? 'Update List' : 'Create List'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Phase 8: Assignment Modal Component
const AssignmentModal: React.FC<{
  layoutId: string;
  onClose: () => void;
  onCreate: (assignmentData: any) => void;
  isCreating: boolean;
}> = ({ layoutId, onClose, onCreate, isCreating }) => {
  const { warning } = useToast();
  const [assignmentType, setAssignmentType] = useState<'profile' | 'recordType'>('profile');
  const [formData, setFormData] = useState({
    profileId: '',
    profileName: '',
    recordType: '',
    isDefault: false,
    priority: 10
  });

  const availableProfiles = [
    { id: 'profile_admin', name: 'System Administrator' },
    { id: 'profile_cs_supervisor', name: 'CyberSweep Supervisor' },
    { id: 'profile_cs_analyst', name: 'CyberSweep Analyst' },
    { id: 'profile_cm_supervisor', name: 'Credit Management Supervisor' },
    { id: 'profile_cm_analyst', name: 'Credit Management Analyst' },
    { id: 'profile_auditor', name: 'Auditor' },
    { id: 'profile_viewer', name: 'Viewer' }
  ];

  const availableRecordTypes = [
    'standard_client',
    'enterprise_client',
    'government_client',
    'individual_client'
  ];

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (assignmentType === 'profile' && !formData.profileId) {
      warning('Profile required', 'Please select a profile');
      return;
    }

    if (assignmentType === 'recordType' && !formData.recordType) {
      warning('Record type required', 'Please select a record type');
      return;
    }

    const assignmentData = assignmentType === 'profile'
      ? {
          profileId: formData.profileId,
          profileName: formData.profileName,
          isDefault: formData.isDefault,
          priority: formData.priority
        }
      : {
          recordType: formData.recordType,
          isDefault: formData.isDefault,
          priority: formData.priority
        };

    onCreate(assignmentData);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-content"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: '500px' }}
      >
        <div className="modal-header">
          <h2>Create Layout Assignment</h2>
          <button className="modal-close-button" onClick={onClose}>✕</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-group">
              <label className="form-label">Assignment Type</label>
              <div style={{ display: 'flex', gap: '1rem' }}>
                <label className="form-checkbox">
                  <input
                    type="radio"
                    name="assignmentType"
                    checked={assignmentType === 'profile'}
                    onChange={() => setAssignmentType('profile')}
                  />
                  <span>Profile</span>
                </label>
                <label className="form-checkbox">
                  <input
                    type="radio"
                    name="assignmentType"
                    checked={assignmentType === 'recordType'}
                    onChange={() => setAssignmentType('recordType')}
                  />
                  <span>Record Type</span>
                </label>
              </div>
            </div>

            {assignmentType === 'profile' ? (
              <div className="form-group">
                <label className="form-label">Profile *</label>
                <select
                  className="form-select"
                  value={formData.profileId}
                  onChange={(e) => {
                    const profile = availableProfiles.find(p => p.id === e.target.value);
                    setFormData({
                      ...formData,
                      profileId: e.target.value,
                      profileName: profile?.name || ''
                    });
                  }}
                  required
                >
                  <option value="">Select a profile...</option>
                  {availableProfiles.map(profile => (
                    <option key={profile.id} value={profile.id}>
                      {profile.name}
                    </option>
                  ))}
                </select>
              </div>
            ) : (
              <div className="form-group">
                <label className="form-label">Record Type *</label>
                <select
                  className="form-select"
                  value={formData.recordType}
                  onChange={(e) => setFormData({ ...formData, recordType: e.target.value })}
                  required
                >
                  <option value="">Select a record type...</option>
                  {availableRecordTypes.map(rt => (
                    <option key={rt} value={rt}>
                      {rt.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="form-group">
              <label className="form-label">Priority</label>
              <input
                type="number"
                className="form-input"
                value={formData.priority}
                onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) })}
                min="1"
              />
              <p className="form-help-text">Lower number = higher priority</p>
            </div>

            <div className="form-group">
              <label className="form-checkbox">
                <input
                  type="checkbox"
                  checked={formData.isDefault}
                  onChange={(e) => setFormData({ ...formData, isDefault: e.target.checked })}
                />
                <span>Set as default layout</span>
              </label>
            </div>
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="button button-secondary"
              onClick={onClose}
              disabled={isCreating}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="button button-primary"
              disabled={isCreating}
            >
              {isCreating ? 'Creating...' : 'Create Assignment'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Phase 8: Layout Preview Modal Component
const LayoutPreviewModal: React.FC<{
  previewData: LayoutPreviewData;
  onClose: () => void;
}> = ({ previewData, onClose }) => {
  // Device preview state
  type DeviceType = 'desktop' | 'tablet' | 'mobile';
  const [deviceType, setDeviceType] = React.useState<DeviceType>('desktop');

  // Tab state
  const [activeTabId, setActiveTabId] = React.useState<string>('');

  // Fetch actual tabs for CyberSweep Client Detail page
  const { data: tabsResponse } = useQuery({
    queryKey: ['dynamic-tabs', 'cybersweep_client_detail', 'cybersweep'],
    queryFn: () => fetchDynamicTabs({ page: 'cybersweep_client_detail', application: 'cybersweep' }),
    staleTime: 60 * 1000
  });

  const configuredTabs = tabsResponse?.data || [];
  const visibleSections = previewData.sections.filter(s => previewData.visibleSections.includes(s.id));

  // Create tabs with sections distributed across them
  // If we have configured tabs, use those; otherwise create default tabs
  const previewTabs = configuredTabs.length > 0
    ? configuredTabs.map((tab, index) => ({
        id: tab.id,
        label: tab.name,
        icon: tab.icon || 'layout',
        // Distribute sections across tabs
        sections: visibleSections.filter((_, sIdx) => sIdx % configuredTabs.length === index)
      }))
    : [
        {
          id: 'tab-all',
          label: 'All Sections',
          icon: 'layout',
          sections: visibleSections
        }
      ];

  // Set initial active tab
  React.useEffect(() => {
    if (previewTabs.length > 0 && !activeTabId) {
      setActiveTabId(previewTabs[0].id);
    }
  }, [previewTabs, activeTabId]);

  const activeTab = previewTabs.find(t => t.id === activeTabId) || previewTabs[0];

  // Device-specific container widths
  const getDeviceWidth = () => {
    switch (deviceType) {
      case 'mobile':
        return '375px';
      case 'tablet':
        return '768px';
      case 'desktop':
        return '100%';
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-content"
        onClick={(e) => e.stopPropagation()}
        style={{
          maxWidth: deviceType === 'desktop' ? '1200px' : '95%',
          maxHeight: '95vh',
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {/* Modal Header with Device Toggle */}
        <div className="modal-header" style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '2px solid var(--border-color)',
          paddingBottom: '1rem'
        }}>
          <div>
            <h2 style={{ marginBottom: '0.25rem' }}>Layout Preview</h2>
            <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', margin: 0 }}>
              Preview how your layout appears across different devices
            </p>
          </div>

          {/* Device Toggle */}
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <div style={{
              display: 'inline-flex',
              background: 'var(--background-secondary)',
              borderRadius: '8px',
              padding: '4px',
              marginRight: '1rem'
            }}>
              <button
                onClick={() => setDeviceType('desktop')}
                className={deviceType === 'desktop' ? 'button-sm button-primary' : 'button-sm'}
                style={{
                  padding: '0.5rem 1rem',
                  fontSize: '0.875rem',
                  background: deviceType === 'desktop' ? 'var(--primary-color)' : 'transparent',
                  color: deviceType === 'desktop' ? 'white' : 'var(--text-secondary)',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer'
                }}
              >
                🖥️ Desktop
              </button>
              <button
                onClick={() => setDeviceType('tablet')}
                className={deviceType === 'tablet' ? 'button-sm button-primary' : 'button-sm'}
                style={{
                  padding: '0.5rem 1rem',
                  fontSize: '0.875rem',
                  background: deviceType === 'tablet' ? 'var(--primary-color)' : 'transparent',
                  color: deviceType === 'tablet' ? 'white' : 'var(--text-secondary)',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer'
                }}
              >
                📱 Tablet
              </button>
              <button
                onClick={() => setDeviceType('mobile')}
                className={deviceType === 'mobile' ? 'button-sm button-primary' : 'button-sm'}
                style={{
                  padding: '0.5rem 1rem',
                  fontSize: '0.875rem',
                  background: deviceType === 'mobile' ? 'var(--primary-color)' : 'transparent',
                  color: deviceType === 'mobile' ? 'white' : 'var(--text-secondary)',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer'
                }}
              >
                📱 Mobile
              </button>
            </div>
            <button className="modal-close-button" onClick={onClose}>✕</button>
          </div>
        </div>

        {/* Device Preview Container */}
        <div style={{
          flex: 1,
          display: 'flex',
          justifyContent: 'center',
          padding: '2rem 1rem',
          background: 'var(--background-tertiary)',
          overflow: 'auto'
        }}>
          <div style={{
            width: getDeviceWidth(),
            maxWidth: '100%',
            background: 'var(--background-primary)',
            border: '1px solid var(--border-color)',
            borderRadius: '12px',
            boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
            overflow: 'hidden',
            minHeight: deviceType === 'mobile' ? '667px' : 'auto'
          }}>
            {/* Quick Actions Preview */}
            {previewData.quickActions.length > 0 && (
              <div style={{
                padding: '1rem',
                borderBottom: '1px solid var(--border-color)',
                background: 'var(--background-secondary)'
              }}>
                <h4 style={{
                  marginBottom: '0.75rem',
                  color: 'var(--text-primary)',
                  fontSize: deviceType === 'mobile' ? '0.875rem' : '1rem',
                  fontWeight: 600
                }}>
                  Quick Actions
                </h4>
                <div style={{
                  display: 'flex',
                  gap: '0.5rem',
                  flexWrap: 'wrap'
                }}>
                  {previewData.quickActions
                    .filter(a => a.enabled)
                    .sort((a, b) => a.order - b.order)
                    .map(action => (
                      <button
                        key={action.id}
                        className="button button-sm button-primary"
                        style={{
                          pointerEvents: 'none',
                          fontSize: deviceType === 'mobile' ? '0.75rem' : '0.875rem',
                          padding: deviceType === 'mobile' ? '0.375rem 0.75rem' : '0.5rem 1rem'
                        }}
                      >
                        {action.icon} {action.label}
                      </button>
                    ))}
                </div>
              </div>
            )}

            {/* Tab Navigation */}
            {previewTabs.length > 0 && (
              <div style={{
                borderBottom: '2px solid var(--border-color)',
                background: 'var(--background-primary)'
              }}>
                <nav style={{
                  display: 'flex',
                  padding: '0 1rem',
                  overflowX: 'auto',
                  gap: deviceType === 'mobile' ? '0' : '0.25rem'
                }}>
                  {previewTabs.map(tab => {
                    const isActive = tab.id === activeTabId;
                    // Simple icon mapping
                    const getIconEmoji = (iconName: string) => {
                      const iconMap: Record<string, string> = {
                        'info': 'ℹ️',
                        'clipboard': '📋',
                        'shield': '🛡️',
                        'clock': '🕐',
                        'layout': '📄',
                        'file-text': '📄',
                        'list': '📋'
                      };
                      return iconMap[iconName] || '📄';
                    };

                    return (
                      <button
                        key={tab.id}
                        onClick={() => setActiveTabId(tab.id)}
                        style={{
                          flex: deviceType === 'mobile' ? '1' : 'none',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          padding: deviceType === 'mobile' ? '0.75rem 0.5rem' : '0.75rem 1rem',
                          fontSize: deviceType === 'mobile' ? '0.75rem' : '0.875rem',
                          fontWeight: 500,
                          color: isActive ? 'var(--primary-color)' : 'var(--text-secondary)',
                          background: 'transparent',
                          border: 'none',
                          borderBottom: `2px solid ${isActive ? 'var(--primary-color)' : 'transparent'}`,
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          whiteSpace: 'nowrap'
                        }}
                        onMouseEnter={(e) => {
                          if (!isActive) {
                            e.currentTarget.style.color = 'var(--text-primary)';
                            e.currentTarget.style.borderBottomColor = 'var(--border-color-hover)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!isActive) {
                            e.currentTarget.style.color = 'var(--text-secondary)';
                            e.currentTarget.style.borderBottomColor = 'transparent';
                          }
                        }}
                      >
                        <span style={{ marginRight: deviceType === 'mobile' ? '0.25rem' : '0.5rem' }}>
                          {getIconEmoji(tab.icon)}
                        </span>
                        {tab.label}
                      </button>
                    );
                  })}
                </nav>
              </div>
            )}

            {/* Tab Content - Sections */}
            <div style={{
              padding: deviceType === 'mobile' ? '1rem' : '1.5rem'
            }}>
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: deviceType === 'mobile' ? '1rem' : '1.5rem'
              }}>
                {activeTab?.sections
                  .sort((a, b) => a.order - b.order)
                  .map(section => (
                    <div
                      key={section.id}
                      style={{
                        border: '1px solid var(--border-color)',
                        borderRadius: '8px',
                        overflow: 'hidden',
                        background: 'var(--background-secondary)'
                      }}
                    >
                      <div style={{
                        padding: deviceType === 'mobile' ? '0.5rem 0.75rem' : '0.75rem 1rem',
                        background: 'var(--background-primary)',
                        borderBottom: '1px solid var(--border-color)',
                        fontWeight: 600,
                        fontSize: deviceType === 'mobile' ? '0.875rem' : '1rem',
                        color: 'var(--text-primary)'
                      }}>
                        {section.label}
                      </div>
                      <div style={{
                        padding: deviceType === 'mobile' ? '0.75rem' : '1rem',
                        display: 'grid',
                        gridTemplateColumns:
                          deviceType === 'mobile' ? '1fr' :
                          section.columns === 2 ? '1fr 1fr' : '1fr',
                        gap: deviceType === 'mobile' ? '0.75rem' : '1rem'
                      }}>
                        {section.fields
                          .sort((a, b) => a.order - b.order)
                          .map(field => (
                            <div key={field.id} style={{ fontSize: deviceType === 'mobile' ? '0.8125rem' : '0.875rem' }}>
                              <div style={{
                                color: 'var(--text-secondary)',
                                marginBottom: '0.25rem',
                                fontSize: deviceType === 'mobile' ? '0.6875rem' : '0.75rem',
                                fontWeight: 500
                              }}>
                                {field.fieldLabel}
                                {(field.required || previewData.requiredFields.includes(field.id)) && (
                                  <span style={{ color: 'var(--danger-color)', marginLeft: '0.25rem' }}>*</span>
                                )}
                                {(field.readonly || previewData.readonlyFields.includes(field.id)) && (
                                  <span style={{ marginLeft: '0.5rem', opacity: 0.6 }}>🔒</span>
                                )}
                              </div>
                              <div style={{
                                padding: deviceType === 'mobile' ? '0.375rem 0.5rem' : '0.5rem',
                                background: 'var(--background-primary)',
                                border: '1px solid var(--border-color)',
                                borderRadius: '4px',
                                color: 'var(--text-secondary)',
                                fontSize: deviceType === 'mobile' ? '0.75rem' : '0.8125rem'
                              }}>
                                {field.fieldType} {field.fieldType === 'text' && '(Sample Value)'}
                              </div>
                            </div>
                          ))}
                      </div>
                    </div>
                  ))}
              </div>

              {/* Related Lists in Last Tab */}
              {activeTabId === 'tab-related' && previewData.relatedLists.length > 0 && (
                <div style={{ marginTop: deviceType === 'mobile' ? '1rem' : '1.5rem' }}>
                  <h4 style={{
                    marginBottom: '0.75rem',
                    color: 'var(--text-primary)',
                    fontSize: deviceType === 'mobile' ? '0.875rem' : '1rem',
                    fontWeight: 600
                  }}>
                    Related Lists
                  </h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    {previewData.relatedLists
                      .filter(l => l.enabled)
                      .sort((a, b) => a.order - b.order)
                      .map(list => (
                        <div
                          key={list.id}
                          style={{
                            padding: deviceType === 'mobile' ? '0.5rem 0.75rem' : '0.75rem 1rem',
                            background: 'var(--background-secondary)',
                            border: '1px solid var(--border-color)',
                            borderRadius: '6px',
                            fontSize: deviceType === 'mobile' ? '0.8125rem' : '0.875rem',
                            color: 'var(--text-primary)'
                          }}
                        >
                          📋 {list.label} ({list.columns.length} columns)
                        </div>
                      ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Modal Footer */}
        <div className="modal-footer" style={{
          borderTop: '2px solid var(--border-color)',
          padding: '1rem',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
            👁️ Viewing as: <strong>{deviceType.charAt(0).toUpperCase() + deviceType.slice(1)}</strong>
            {previewTabs.length > 1 && <span style={{ marginLeft: '1rem' }}>
              📑 Tab: <strong>{activeTab?.label}</strong>
            </span>}
          </div>
          <button
            type="button"
            className="button button-primary"
            onClick={onClose}
          >
            Close Preview
          </button>
        </div>
      </div>
    </div>
  );
};

// Phase 8: Validation Modal Component
const ValidationModal: React.FC<{
  result: LayoutValidationResult;
  onClose: () => void;
}> = ({ result, onClose }) => {
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-content"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: '700px' }}
      >
        <div className="modal-header">
          <h2>Layout Validation</h2>
          <button className="modal-close-button" onClick={onClose}>✕</button>
        </div>

        <div className="modal-body">
          {/* Validation Status */}
          <div style={{
            padding: '1rem',
            background: result.isValid
              ? 'rgba(76, 175, 80, 0.15)'
              : 'rgba(244, 67, 54, 0.15)',
            border: `1px solid ${result.isValid ? '#4CAF50' : '#F44336'}`,
            borderRadius: '6px',
            marginBottom: '1.5rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem'
          }}>
            <span style={{ fontSize: '2rem' }}>
              {result.isValid ? '✅' : '❌'}
            </span>
            <div>
              <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
                {result.isValid ? 'Layout is Valid' : 'Validation Failed'}
              </div>
              <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                {result.errors.length} error(s), {result.warnings.length} warning(s)
              </div>
            </div>
          </div>

          {/* Errors */}
          {result.errors.length > 0 && (
            <div style={{ marginBottom: '1.5rem' }}>
              <h4 style={{ marginBottom: '0.75rem', color: 'var(--text-primary)' }}>
                Errors ({result.errors.length})
              </h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {result.errors.map((error, idx) => (
                  <div
                    key={idx}
                    style={{
                      padding: '0.75rem',
                      background: 'rgba(244, 67, 54, 0.1)',
                      border: '1px solid rgba(244, 67, 54, 0.3)',
                      borderRadius: '6px'
                    }}
                  >
                    <div style={{ fontWeight: 600, color: '#F44336', marginBottom: '0.25rem' }}>
                      {error.section}{error.field ? ` > ${error.field}` : ''}
                    </div>
                    <div style={{ fontSize: '0.875rem', color: 'var(--text-primary)' }}>
                      {error.message}
                    </div>
                    {error.fix && (
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '0.25rem' }}>
                        💡 {error.fix}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Warnings */}
          {result.warnings.length > 0 && (
            <div style={{ marginBottom: '1.5rem' }}>
              <h4 style={{ marginBottom: '0.75rem', color: 'var(--text-primary)' }}>
                Warnings ({result.warnings.length})
              </h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {result.warnings.map((warning, idx) => (
                  <div
                    key={idx}
                    style={{
                      padding: '0.75rem',
                      background: 'rgba(255, 152, 0, 0.1)',
                      border: '1px solid rgba(255, 152, 0, 0.3)',
                      borderRadius: '6px'
                    }}
                  >
                    <div style={{ fontWeight: 600, color: '#FF9800', marginBottom: '0.25rem' }}>
                      {warning.section}{warning.field ? ` > ${warning.field}` : ''}
                    </div>
                    <div style={{ fontSize: '0.875rem', color: 'var(--text-primary)' }}>
                      {warning.message}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Suggestions */}
          {result.suggestions.length > 0 && (
            <div>
              <h4 style={{ marginBottom: '0.75rem', color: 'var(--text-primary)' }}>
                Suggestions
              </h4>
              <ul style={{
                margin: 0,
                paddingLeft: '1.5rem',
                color: 'var(--text-secondary)',
                fontSize: '0.875rem'
              }}>
                {result.suggestions.map((suggestion, idx) => (
                  <li key={idx} style={{ marginBottom: '0.5rem' }}>
                    {suggestion}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button
            type="button"
            className="button button-primary"
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// Phase 8: Health Check Modal Component
const HealthCheckModal: React.FC<{
  healthCheck: LayoutHealthCheck;
  onClose: () => void;
}> = ({ healthCheck, onClose }) => {
  const getHealthColor = (health: string) => {
    switch (health) {
      case 'excellent': return { bg: 'rgba(76, 175, 80, 0.15)', color: '#4CAF50', emoji: '💚' };
      case 'good': return { bg: 'rgba(139, 195, 74, 0.15)', color: '#8BC34A', emoji: '💛' };
      case 'fair': return { bg: 'rgba(255, 152, 0, 0.15)', color: '#FF9800', emoji: '🧡' };
      case 'poor': return { bg: 'rgba(244, 67, 54, 0.15)', color: '#F44336', emoji: '❤️' };
      default: return { bg: 'rgba(158, 158, 158, 0.15)', color: '#9E9E9E', emoji: '🩶' };
    }
  };

  const colors = getHealthColor(healthCheck.health);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-content"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: '600px' }}
      >
        <div className="modal-header">
          <h2>Layout Health Check</h2>
          <button className="modal-close-button" onClick={onClose}>✕</button>
        </div>

        <div className="modal-body">
          {/* Health Score */}
          <div style={{
            padding: '1.5rem',
            background: colors.bg,
            border: `2px solid ${colors.color}`,
            borderRadius: '8px',
            marginBottom: '1.5rem',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '3rem', marginBottom: '0.5rem' }}>
              {colors.emoji}
            </div>
            <div style={{ fontSize: '2rem', fontWeight: 700, color: colors.color, marginBottom: '0.25rem' }}>
              {healthCheck.score}/100
            </div>
            <div style={{ fontSize: '1.125rem', fontWeight: 600, color: colors.color, textTransform: 'uppercase' }}>
              {healthCheck.health}
            </div>
          </div>

          {/* Metrics */}
          <div style={{ marginBottom: '1.5rem' }}>
            <h4 style={{ marginBottom: '0.75rem', color: 'var(--text-primary)' }}>Layout Metrics</h4>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: '0.75rem'
            }}>
              <div style={{
                padding: '0.75rem',
                background: 'var(--background-secondary)',
                borderRadius: '6px',
                border: '1px solid var(--border-color)'
              }}>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>
                  Sections
                </div>
                <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--text-primary)' }}>
                  {healthCheck.metrics.sectionsCount}
                </div>
              </div>

              <div style={{
                padding: '0.75rem',
                background: 'var(--background-secondary)',
                borderRadius: '6px',
                border: '1px solid var(--border-color)'
              }}>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>
                  Fields
                </div>
                <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--text-primary)' }}>
                  {healthCheck.metrics.fieldsCount}
                </div>
              </div>

              <div style={{
                padding: '0.75rem',
                background: 'var(--background-secondary)',
                borderRadius: '6px',
                border: '1px solid var(--border-color)'
              }}>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>
                  Visibility Rules
                </div>
                <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--text-primary)' }}>
                  {healthCheck.metrics.rulesCount}
                </div>
              </div>

              <div style={{
                padding: '0.75rem',
                background: 'var(--background-secondary)',
                borderRadius: '6px',
                border: '1px solid var(--border-color)'
              }}>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>
                  Related Lists
                </div>
                <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--text-primary)' }}>
                  {healthCheck.metrics.relatedListsCount}
                </div>
              </div>

              <div style={{
                padding: '0.75rem',
                background: 'var(--background-secondary)',
                borderRadius: '6px',
                border: '1px solid var(--border-color)'
              }}>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>
                  Quick Actions
                </div>
                <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--text-primary)' }}>
                  {healthCheck.metrics.quickActionsCount}
                </div>
              </div>

              <div style={{
                padding: '0.75rem',
                background: 'var(--background-secondary)',
                borderRadius: '6px',
                border: '1px solid var(--border-color)'
              }}>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>
                  Unused Fields
                </div>
                <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--text-primary)' }}>
                  {healthCheck.metrics.unusedFields}
                </div>
              </div>
            </div>
          </div>

          {/* Recommendations */}
          {healthCheck.recommendations.length > 0 && (
            <div>
              <h4 style={{ marginBottom: '0.75rem', color: 'var(--text-primary)' }}>
                Recommendations
              </h4>
              <ul style={{
                margin: 0,
                paddingLeft: '1.5rem',
                color: 'var(--text-secondary)',
                fontSize: '0.875rem'
              }}>
                {healthCheck.recommendations.map((rec, idx) => (
                  <li key={idx} style={{ marginBottom: '0.5rem' }}>
                    {rec}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button
            type="button"
            className="button button-primary"
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// Phase 8: Clone Modal Component
const CloneModal: React.FC<{
  sourceLayoutId: string;
  onClose: () => void;
  onClone: (cloneRequest: LayoutCloneRequest) => void;
  isCloning: boolean;
}> = ({ sourceLayoutId, onClose, onClone, isCloning }) => {
  const { warning } = useToast();
  const [formData, setFormData] = useState({
    newName: '',
    newLabel: '',
    includeVisibilityRules: true,
    includeRelatedLists: true,
    includeQuickActions: true
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.newLabel.trim()) {
      warning('Layout label required', 'Please enter a layout label');
      return;
    }

    onClone({
      sourceLayoutId,
      newName: formData.newName || formData.newLabel.toLowerCase().replace(/\s+/g, '_'),
      newLabel: formData.newLabel,
      includeVisibilityRules: formData.includeVisibilityRules,
      includeRelatedLists: formData.includeRelatedLists,
      includeQuickActions: formData.includeQuickActions
    });
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-content"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: '500px' }}
      >
        <div className="modal-header">
          <h2>Clone Layout</h2>
          <button className="modal-close-button" onClick={onClose}>✕</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-group">
              <label className="form-label">New Layout Label *</label>
              <input
                type="text"
                className="form-input"
                value={formData.newLabel}
                onChange={(e) => setFormData({ ...formData, newLabel: e.target.value })}
                placeholder="e.g., Client Detail Layout (Copy)"
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label">What to include:</label>
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '0.5rem',
                padding: '0.75rem',
                background: 'var(--background-secondary)',
                borderRadius: '6px'
              }}>
                <label className="form-checkbox">
                  <input
                    type="checkbox"
                    checked={formData.includeVisibilityRules}
                    onChange={(e) => setFormData({ ...formData, includeVisibilityRules: e.target.checked })}
                  />
                  <span>Visibility Rules</span>
                </label>

                <label className="form-checkbox">
                  <input
                    type="checkbox"
                    checked={formData.includeRelatedLists}
                    onChange={(e) => setFormData({ ...formData, includeRelatedLists: e.target.checked })}
                  />
                  <span>Related Lists</span>
                </label>

                <label className="form-checkbox">
                  <input
                    type="checkbox"
                    checked={formData.includeQuickActions}
                    onChange={(e) => setFormData({ ...formData, includeQuickActions: e.target.checked })}
                  />
                  <span>Quick Actions</span>
                </label>
              </div>
              <p className="form-help-text">
                Sections and fields are always included
              </p>
            </div>
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="button button-secondary"
              onClick={onClose}
              disabled={isCloning}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="button button-primary"
              disabled={isCloning}
            >
              {isCloning ? 'Cloning...' : 'Clone Layout'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PageLayoutManager;
